import './polyfills.js';
import * as THREE from 'three';
import { coastlineRadius, coastlineBandRadius, coastlineShaderRadius, islandCoastlineShaderRadius } from './coastline.js';
import { createBeachMaterial } from './beachMaterial.js';
import { COAST_SWASH_GLSL } from './coastSwash.js';
import { groundPlacements } from './terrain.js';
import { createRoamer, offshorePlacements, shoreRadiusAt, stepRoamer, upperBeachPoint } from './seaEcology.js';
import { createFishSource, fishPickRadius } from './fishVisual.js';
import { wildlifeActivityAt, seagullPose } from './wildlifeActivity.js';
import { sparseBushPlacements } from './bushLayout.js';
import { createFoliageMaterial, applyFoliageAtlas } from './foliageMaterial.js';
import { createPointerGesture, movePointerGesture, isPointerTap, TAP_MOVE_TOLERANCE } from './pointerGesture.js';
import { planWalkRoute } from './navigation.js';
import { CHARACTER_RADIUS, CHARACTER_HEIGHT, collisionObstacles, resolveCharacterStep, createVault, stepVault } from './characterCollision.js';
import { collectPhysicalColliders } from './physicalBounds.js';
import { isWorldVisible, pickWorldAction } from './worldPicking.js';
import { FARM_ORIGIN, FARM_BED_OFFSETS as bedPoints, FARM_BED_CENTRES } from './farmLayout.js';
import { DOCK_APPROACH, DOCK_DECK_OBSTACLE, TRADE_SIGN_POSITION } from './dockNavigation.js';
import { bedRestPlan, restProgress } from './rest.js';
import { createStaminaRuntime, stepStamina, SWIM_STAMINA_DRAIN } from './stamina.js';
import { createStaminaVisual } from './staminaVisual.js';
import { compassTarget, explorationLayout } from './exploration.js';
import { createExplorationHud } from './explorationVisual.js';
import { createExplorationCamera } from './explorationCamera.js';
import { createExplorationCameraUi } from './explorationCameraUi.js';
import { createCameraOcclusion } from './cameraOcclusion.js';
import { questSummary, questSiteHint } from './beaconQuest.js';
import { HOME_BOARD, HOME_BOARD_APPROACH, projectStatus, islandLifeSummary } from './islandLife.js';
import { createIslandLifeVisual } from './islandLifeVisual.js';
import { drawIslandLifePanel, islandLifePanelHeight } from './islandLifeUi.js';
import { submitHomeProject } from './economy.js';
import { describePlayerGoal, selectPlayerGoal, clearFinishedPlayerGoal } from './playerGoals.js';
import { dailyOrderStatus } from './economy.js';
import { createIslandAtlasMaterial, createMeadowMaterial, createShallowFloorMaterial, addMeadowAccents, addHutDetails, createFarmEnclosure } from './sceneArt.js';
import { drawIslandStatus, drawIslandNavigation, drawNavIcon, loadUiArt, pill, UI_COLORS } from './uiArt.js';
import { gradeSurface, gradeWorld } from './scenePalette.js';
import { createTouchControls, touchPadLayout } from './touchControls.js';
import { layoutCharacterTip, drawCharacterTip } from './characterTip.js';
import { mobileLayout, insideRect, panelScrollGeometry, panelPoint, wrapCanvasText } from './mobileLayout.js';
import { resizeTextureCanvas } from './canvasTexture.js';
import { nextPlayerAction } from './playerGuidance.js';
import { advanceFishingSession, createFishingSession, fishingCue, fishingForceGuide, FISHING_FORCE_LABELS, hookFish, setFishingForce, setFishingAssistance } from './fishing.js';
import { BOAT_MESH_ORIGIN, BOAT_MOOR, boatFloatOffset, isBoatPassenger, seatAvatarInBoat } from './boat.js';
import { ISLANDS, voyageGatherLabel } from './voyage.js';
import { createVoyageScene } from './voyageScene.js';
import { plotGuidance, selectFarmPlot } from './playerGuidance.js';
import { attachAvatarTools } from './avatarTools.js';
import { animateAvatarFace, AVATAR_WORLD_SCALE, prepareAvatarSeatedRig, resetAvatarSeatedJoints, poseAvatarWorkElbows } from './avatarMotion.js';
import { createSandCrab, stepSandCrab } from './sandCrab.js';
import { createSandCrabGeometry, createSandCrabMaterial } from './sandCrabVisual.js';
import { createSandTracks } from './sandTracks.js';
import { createSandTrackVisual } from './sandTrackVisual.js';
import { createEnvironment } from './environmentVisual.js';
import { WAVE_BARRIER_APPROACH, WAVE_BARRIER_POSTS, WAVE_BARRIER_SITE } from './waveBarrier.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { BUILDINGS, CROPS, SEAFOOD, SHOP, TREE_MATURE_MINUTES, build, buyItem, captureAvailability, captureFailureMessage, catchSeafood, chopTree, eatMeal, ensureDailyOrders, gather, harvestCrop, installDefense, inventoryUsed, moveAllStock, moveStock, normalizeState, patchAssetWithWood, plantCrop, plantTree, refillOxygen, repairAsset, repairQuote, restRecoveryCap, sellItem, submitOrder, tendTree, advanceGameTime, spendActionTime, tideAt, usableCropSeed, waterCrop, woodPatchQuote } from './economy.js';
import { stormStatus, summarizeStormReport, updateStorm } from './storm.js';
import { INSTALLABLE_STOCK, drawIcon, drawInventoryPanel, getInventoryRows } from './inventoryUi.js';
import { FRESHWATER_SITE, dailyWaterYield, drinkWater, rainForDay, refillCanteen } from './freshwater.js';
import { initGoogleAuth } from './googleAuth.js';
import { DISHES, RECIPES, edibleDishCount, learnRecipe, recipeQuote, upgradeRecipe } from './cooking.js';
import { cookMeal } from './economy.js';
import { COOKING_MOBILE_HEIGHT, drawCookingPanel } from './cookingUi.js';
import { HUT_ORIGIN, HUT_APPROACH, HUT_SPAWN, KITCHEN_SITE, KITCHEN_APPROACH, HUT_DOOR_INSIDE, HUT_DOOR_OUTSIDE, createHutCrossing, hutControls, hutDoorIntent, isOutsideHut, keepHutControlsOpen, stepHutCrossing } from './hut.js';

import { canvas, getBrowserSaveStorage, loadImage, loadState, nextFrame, offscreen, onForegroundChange, onResize, onTouches, saveState, suspendLocalSaves, viewport } from './platform.js';
import islandGlb from '../assets/generated/tropical-island.glb';
import vegetationGlb from '../assets/generated/tropical-vegetation.glb';
import buildingsGlb from '../assets/generated/tropical-buildings.glb';
import avatarGlb from '../assets/generated/tropical-avatar.glb';

const C = {
  lagoon: 0x16baac, shallow: 0x00859f, deep: 0x07507f, foam: 0xf3fbef,
  sand: 0xefdbab, wood: 0x9a6a36,
};

const initialViewport = viewport();
const renderer = new THREE.WebGLRenderer({ canvas, antialias: !initialViewport.isWeChat && !initialViewport.isTouch, powerPreference: 'high-performance' });
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.26;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.autoClear = false;

const scene = new THREE.Scene();
scene.background = new THREE.Color(C.deep);
scene.fog = new THREE.FogExp2(0x1c8fa6, 0.005);
const camera = new THREE.PerspectiveCamera(52, initialViewport.width / initialViewport.height, .12, 260);
const explorationCamera = createExplorationCamera();
const cameraOcclusion = createCameraOcclusion();
const hudScene = new THREE.Scene();
const hudCamera = new THREE.OrthographicCamera();
const raycaster = new THREE.Raycaster();
const Y_AXIS = new THREE.Vector3(0, 1, 0);
const X_AXIS = new THREE.Vector3(1, 0, 0);
const interactive = [];
const animated = [];
const animatedShaderUniforms = [];
const birdFlightUniform = { value: 1 }, birdTimeUniform = { value: 0 };
let fishActivityTime = 0, birdFlightTime = 0, birdRestCount = 0;
const fishRoamers = [];
const windShaderUniforms = [];
const hutRoofs = [];
let heroBoat = null;
const forceCutaway = typeof location !== 'undefined' && /cutaway/.test(location.search);
const state = normalizeState(loadState());
let accountDialogOpen = false;
const webAccountRoot = !initialViewport.isWeChat && typeof globalThis.document?.getElementById === 'function'
  ? document.getElementById('google-auth-root') : null;
initGoogleAuth({
  readState: () => state, normalize: normalizeState,
  saveStorage: (() => { try { return getBrowserSaveStorage(); } catch { return null; } })(),
  onPause: (paused) => { accountDialogOpen = paused; if (paused) saveState(state); },
  reload: () => { suspendLocalSaves(); location.reload(); },
});
let screen = viewport();
let azimuth = -0.72;
let cameraPose = null;
let messageUntil = 0;
let feedbackText = '', characterTipRect = null, characterTipAction = null, characterTipPaintKey = '';
let feedbackPending = false, feedbackDuration = 2800;
let hudPage = 'activity';
let islandLifeMode = 'wishes';
let selectedHomeProject = 'windchime';
let islandLifeVisual = null;
let playerGoalHint = null;
const playerGoalAction = new THREE.Object3D();
playerGoalAction.userData.action = { type: 'player-goal' };
let inventoryMode = 'bag';
let inventoryPage = 0;
let selectedInventoryId = null;
let selectedRecipeId = 'bakedPotato';
let kitchenPage = 0;
let cookingJob = null;
let shopPage = 0;
let sellPage = 0;
let tradeMode = 'buy';
let stormReportOpen = false;
let selectedSeed = 'sweetPotato';
let selectedPlotId = null;
let selectedSeafood = 'crab';
let selectedTreeId = state.trees[0]?.id;
const treeVisuals = new Map();
const marineTargets = [];
let activeMarineTarget = null;
let marineTargetMarker = null;
let fishingSession = null;
let fishingResult = null;
let fishingVisual = null;
let fishingLine = null;
let lastFishingUiMs = 0;
let avatar = null;
let avatarTemplate = null;
let avatarTarget = null;
let avatarRoute = [];
let avatarVault = null;
let arrivalAction = null;
let busyAction = false;
let restTransition = null;
let bedApproach = null;
let hutBed = null;
let hutDoorPivot = null;
let hutDoorLabel = null;
let walkDestinationMarker = null;
let walkDestinationUntil = 0;
let hutCrossing = null;
let sceneElapsed = 0;
let boatTrip = null;
let voyage = null;
let voyagePalmSource = null;
let voyageArtAssets = {};
let marineMode = 'land';
let diveBlend = 0;
let diveSecondsLeft = 0;
let diveSecondsMax = 0;
let diveMarker = null;
let swimWake = null;
const staminaRuntime = createStaminaRuntime(state);
const staminaVisual = createStaminaVisual();
scene.add(staminaVisual.sprite);
const explorationHud = createExplorationHud(hudScene);
const catchEffects = [];
let walkGroundMeshes = [];
let sandTracks = null;
let sandTrackVisual = null;
const groundRaycaster = new THREE.Raycaster();
const groundSample = { x: NaN, z: NaN, y: 2.03 };
const MAX_SURFACE_DISTANCE = 6.5;
const MAX_ROUTE_RADIUS = 31;
let foreground = true;
let sceneReady = false;
let previousFrameMs = 0;
let lastHudDrawMs = 0;
const PASSIVE_TIME_SCALE = .125; // advanceGameTime converts each input second into two game minutes.
let lastSaveMs = 0;
const cropObjects = [];
const worldLabels = [];
let nearbyAction = null, padWalking = false, padRefresh = 0;
const movementKeys = new Set();
const normalizeMovementKey = key => key.length === 1 ? key.toLowerCase() : key;
const movementKey = key => ({ w: 'up', ArrowUp: 'up', s: 'down', ArrowDown: 'down',
  a: 'left', ArrowLeft: 'left', d: 'right', ArrowRight: 'right' })[normalizeMovementKey(key)];
function stopManualMovement() {
  movementKeys.clear(); voyage?.stopManualMove();
  if (padWalking) { avatarTarget = null; avatarRoute = []; arrivalAction = null; walkDestinationUntil = 0; padWalking = false; }
}
const cameraUi = createExplorationCameraUi(hudScene, {
  onToggle: () => { explorationCamera.toggleMode(); },
  onRecenter: () => { explorationCamera.recenter(voyage?.sailing ? voyage.heading : avatar?.rotation.y || 0); },
});
const touchControls = createTouchControls(hudScene, {
  onInteract: () => {
    if (nearbyAction) activateWorldAction(nearbyAction);
    else { hudPage = 'activity'; hudOpen = true; drawHud(); }
  },
  onStop: () => { voyage?.stopManualMove(); if (padWalking) { avatarTarget = null; avatarRoute = []; arrivalAction = null; walkDestinationUntil = 0; padWalking = false; } },
});
const homeCollisionSources = [];
let homeColliderCache = [], lastHomeColliderRefresh = -Infinity;
function registerHomeCollision(root, id, options = {}) {
  homeCollisionSources.push({ root, id, options, signature: null, colliders: [] });
  lastHomeColliderRefresh = -Infinity;
}
function getHomeColliders(force = false) {
  const now = Date.now();
  if (!force && now - lastHomeColliderRefresh < 220) return homeColliderCache;
  lastHomeColliderRefresh = now;
  const result = [];
  for (const source of homeCollisionSources) {
    source.root.updateWorldMatrix(true, true);
    const parts = [];
    source.root.traverse(object => {
      parts.push(object.visible);
      if (object.isMesh) parts.push(...object.matrixWorld.elements.map(v => Math.round(v * 10000)),
        object.isInstancedMesh ? object.instanceMatrix.version : 0);
    });
    const signature = `${source.root.visible}|${parts.join(',')}`;
    if (signature !== source.signature) {
      source.signature = signature;
      source.colliders = collectPhysicalColliders(source.root, { id: source.id, groundHeightAt,
        bodyHeight: CHARACTER_HEIGHT, ...source.options });
    }
    result.push(...source.colliders);
  }
  if (state.built.hut) {
    result.push({ id: 'home-hut-walls', type: 'box', x0: HUT_ORIGIN.x - 1.94, x1: HUT_ORIGIN.x + 1.94,
      z0: HUT_ORIGIN.z - 1.54, z1: HUT_ORIGIN.z + 1.54, bottom: HUT_ORIGIN.y, top: HUT_ORIGIN.y + 2.43, low: false });
    result.push({ id: 'home-porch-bench', type: 'box', x0: HUT_ORIGIN.x - 1.62, x1: HUT_ORIGIN.x - .33,
      z0: HUT_ORIGIN.z - 3.27, z1: HUT_ORIGIN.z - 2.65, bottom: HUT_ORIGIN.y, top: HUT_ORIGIN.y + .82, low: false });
    result.push({ id: 'home-porch-barrel', x: HUT_ORIGIN.x + .95, z: HUT_ORIGIN.z - 2.86,
      r: .34, bottom: HUT_ORIGIN.y, top: HUT_ORIGIN.y + 1, low: false });
  }
  // Raw Dock_Source bounds; navigation and sweep add the body allowance once.
  if (state.built.dock) result.push({ id: 'home-dock', type: 'oriented-box',
    x: DOCK_DECK_OBSTACLE.x, z: DOCK_DECK_OBSTACLE.z, angle: DOCK_DECK_OBSTACLE.angle,
    halfWidth: 1.971, halfDepth: .92, bottom: -1.05, top: 1.935, low: false });
  homeColliderCache = result;
  return result;
}
function homeWalkFeetAt(x, z) {
  const ground = groundHeightAt(x, z);
  return waterDepthMetres(x, z) < .25 ? ground + .02
    : Math.max(water.position.y - (marineMode === 'dive' ? 1 : .55), ground + .03);
}
function homeWalkObstacles(colliders) {
  return collisionObstacles(colliders.filter(c => {
    const x = c.x ?? (c.x0 + c.x1) / 2, z = c.z ?? (c.z0 + c.z1) / 2;
    const feet = homeWalkFeetAt(x, z);
    return c.top > feet && c.bottom < feet + CHARACTER_HEIGHT;
  }));
}
function beginHomeVault(dx, dz) {
  if (!avatar || avatarVault || busyAction || boatTrip || fishingSession || hutCrossing || bedApproach || state.shelter.inside || marineMode !== 'land') return false;
  const plan = createVault(avatar.position, { dx, dz }, getHomeColliders(), { feetY: avatar.position.y, groundHeightAt,
    canStandAt: (x,z) => waterDepthMetres(x,z) < .25 && Math.hypot(x,z) < MAX_ROUTE_RADIUS });
  if (!plan) return false;
  avatarVault = plan; avatarTarget = null; avatarRoute = []; arrivalAction = null;
  avatar.rotation.y = Math.atan2(dx,dz);
  flash('跨过低矮障碍');
  return true;
}
const visualProps = {};
const storm = { nextDay: 7, strength: 1, phase: 'calm', settled: 0, lastShown: 0 };

const artDaylight = {value: 0};
const mat = (color, roughness = 0.85) => gradeSurface(new THREE.MeshStandardMaterial({ color, roughness, metalness: 0 }), artDaylight);
const materials = {
  wood: mat(C.wood), pink: mat(0xff8fbd), white: mat(0xfff4df), gold: mat(0xf8c857),
  shell: new THREE.MeshStandardMaterial({ color: 0xfff1dd, roughness: .38, metalness: .03, vertexColors: true }),
  pinkShell: new THREE.MeshStandardMaterial({ color: 0xffb1c2, roughness: .4, metalness: .02, vertexColors: true }),
  driftwood: new THREE.MeshStandardMaterial({ roughness: .94, vertexColors: true }),
};

const leafShape = new THREE.Shape();
leafShape.moveTo(0, 0);
leafShape.quadraticCurveTo(-.14, .28, 0, .62);
leafShape.quadraticCurveTo(.14, .28, 0, 0);
const leafGeometry = new THREE.ShapeGeometry(leafShape);

function curvedRod(length, radius, material) {
  return mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3([
    new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, length * .48, -.025), new THREE.Vector3(.045, length, -.12),
  ]), 8, radius, 7, false), material, 0, 0, 0, false);
}

function driftwoodGeometry(length, radius) {
  const geometry = new THREE.CylinderGeometry(radius * .58, radius, length, 12, 4);
  const pos = geometry.attributes.position;
  const colors = [];
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), z = pos.getZ(i), y = pos.getY(i);
    const angle = Math.atan2(z, x);
    const grain = Math.sin(y * 28 + Math.sin(angle * 3) * 1.4) * .08;
    const knot = Math.max(0, Math.cos(angle * 5 + y * 2)) * .1;
    const shade = .78 + grain + knot;
    colors.push(.50 * shade, .32 * shade, .17 * shade);
  }
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  for (let i = 0; i < pos.count; i++) {
    const angle = Math.atan2(pos.getZ(i), pos.getX(i));
    const y = pos.getY(i) / length + .5;
    const wobble = 1 + .035 * Math.sin(angle * 3 + y * 7) + .018 * Math.sin(angle * 7 - y * 11);
    pos.setX(i, pos.getX(i) * wobble);
    pos.setZ(i, pos.getZ(i) * wobble);
  }
  geometry.computeVertexNormals();
  return geometry;
}

function shellGeometry(radius, ribs) {
  const geometry = new THREE.SphereGeometry(radius, 24, 14);
  const pos = geometry.attributes.position;
  const colors = [];
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const angle = Math.atan2(z, x);
    const radial = Math.hypot(x, z) / radius;
    const ridge = Math.cos(angle * ribs);
    const pleat = 1 + ridge * .075 * radial;
    pos.setX(i, x * pleat);
    pos.setZ(i, z * pleat);
    pos.setY(i, y * .48);
    const shade = .82 + .11 * (ridge * .5 + .5) + .07 * (y / radius + 1) * .5;
    colors.push(shade, shade * .93, shade * .82);
  }
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.computeVertexNormals();
  return geometry;
}

const driftwoodLong = driftwoodGeometry(1.45, .19);
const driftwoodBranch = driftwoodGeometry(.82, .115);
const shellGeometries = [shellGeometry(.22, 8), shellGeometry(.27, 11)];

function mesh(geometry, material, x = 0, y = 0, z = 0, shadow = true) {
  const object = new THREE.Mesh(geometry, material);
  object.position.set(x, y, z);
  object.castShadow = shadow;
  object.receiveShadow = shadow;
  return object;
}

function createAvatar() {
  const actor = new THREE.Group();
  const skin = mat(0xc9825f, .92);
  const shirt = mat(0x2f7467, .88);
  const shorts = mat(0x314b54, .9);
  const fabric = mat(0xdec89a, .95);
  const torso = mesh(new THREE.SphereGeometry(.28, 16, 10), shirt, 0, .815, 0);
  torso.scale.z = .73;
  const head = mesh(new THREE.SphereGeometry(.35, 20, 12), skin, 0, 1.44, .025);
  const hair = mesh(new THREE.SphereGeometry(.36, 16, 10, 0, Math.PI * 2, 0, Math.PI * .40), mat(0x503a2c), 0, 1.44, -.02);
  const hatBrim = mesh(new THREE.CylinderGeometry(.443, .455, .043, 24), fabric, 0, 1.768, .02);
  const hatCrown = mesh(new THREE.CylinderGeometry(.259, .277, .146, 20), fabric, 0, 1.849, .02);
  const pack = mesh(new THREE.CapsuleGeometry(.14, .16, 3, 7), fabric, 0, .815, -.25);
  const leftLeg = new THREE.Group(); leftLeg.position.set(-.145, .53, 0);
  const rightLeg = new THREE.Group(); rightLeg.position.set(.145, .53, 0);
  const leftArm = new THREE.Group(); leftArm.position.set(-.305, 1.018, .015);
  const rightArm = new THREE.Group(); rightArm.position.set(.305, 1.018, .015);
  for (const leg of [leftLeg, rightLeg]) {
    leg.add(mesh(new THREE.CapsuleGeometry(.12, .10, 3, 8), shorts, 0, -.11, 0));
    leg.add(mesh(new THREE.CylinderGeometry(.085, .085, .2, 10), skin, 0, -.31, 0));
  }
  for (const arm of [leftArm, rightArm]) {
    arm.add(mesh(new THREE.CapsuleGeometry(.10, .11, 3, 8), shirt, 0, -.105, 0));
    arm.add(mesh(new THREE.CapsuleGeometry(.075, .08, 3, 8), skin, 0, -.29, 0));
    arm.add(mesh(new THREE.SphereGeometry(.088, 12, 8), skin, 0, -.427, .02, false));
  }
  const boots = mat(0x463b31, .92);
  for (const leg of [leftLeg, rightLeg]) {
    const boot = mesh(new THREE.SphereGeometry(.13, 12, 8), boots, 0, -.448, .07, false);
    boot.scale.set(1, .63, 1.42);
    leg.add(boot);
  }
  leftArm.rotation.z = -.22;
  rightArm.rotation.z = .22;
  const toolRoot = new THREE.Group();
  toolRoot.position.set(.39, .72, .04);
  const rod = new THREE.Group();
  const rodShaft = curvedRod(.94, .018, mat(0x6e4529));
  const rodGrip = mesh(new THREE.CylinderGeometry(.03, .038, .22, 8), mat(0x493329), 0, .1, 0, false);
  const rodLine = mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3([
    new THREE.Vector3(.04, .84, -.1), new THREE.Vector3(.08, .56, -.2), new THREE.Vector3(.16, .22, -.32),
  ]), 8, .005, 5, false), mat(0xd6e8e2, .75), 0, 0, 0, false);
  const float = mesh(new THREE.SphereGeometry(.034, 8, 6), mat(0xf2a642, .62), .16, .18, -.32, false);
  rod.add(rodShaft, rodGrip, rodLine, float);
  const rodGuides = [];
  for (const [i, y] of [.34, .58, .8].entries()) {
    const guide = mesh(new THREE.TorusGeometry(.028 - i * .004, .006, 5, 8), mat(0x708986, .42), .01, y, -.035, false);
    guide.rotation.x = Math.PI / 2;
    rod.add(guide); rodGuides.push(guide);
  }
  const rodWrap = mesh(new THREE.TorusGeometry(.04, .012, 5, 8), mat(0xcf9b48, .66), 0, .29, 0, false);
  rodWrap.rotation.x = Math.PI / 2; rod.add(rodWrap);
  const rodReel = new THREE.Group();
  rodReel.add(mesh(new THREE.CylinderGeometry(.052, .052, .09, 10), mat(0x73898a, .4), 0, 0, 0, false));
  rodReel.add(mesh(new THREE.TorusGeometry(.047, .009, 5, 10), mat(0xc7a35d, .48), 0, .048, 0, false));
  rodReel.position.set(-.055, .28, .025); rod.add(rodReel); rodReel.visible = false;
  rod.rotation.z = -.72;
  const shovel = new THREE.Group();
  const handle = curvedRod(.62, .027, mat(0x85512d));
  handle.rotation.z = -.48;
  const spade = mesh(new THREE.SphereGeometry(.11, 10, 7), mat(0x6f7d79, .46), -.27, -.37, 0, false);
  spade.scale.set(.72, 1.22, .22);
  const shovelCollar = mesh(new THREE.CylinderGeometry(.046, .052, .09, 8), mat(0x64757a, .4), -.17, -.27, 0, false);
  const shovelGrip = mesh(new THREE.TorusGeometry(.065, .015, 5, 9), mat(0x3f3429), .12, .47, 0, false);
  shovelGrip.rotation.x = Math.PI / 2;
  shovel.add(handle, spade, shovelCollar, shovelGrip);
  const axe = new THREE.Group();
  const axeHandle = curvedRod(.62, .026, mat(0x865a38));
  axeHandle.rotation.z = -.45;
  const axeHead = mesh(new THREE.SphereGeometry(.14, 12, 8), mat(0x86999b, .42), -.24, .37, 0, false);
  axeHead.scale.set(1.5, .64, .28);
  axe.add(axeHandle, axeHead);
  const net = new THREE.Group();
  const netHandle = curvedRod(.54, .018, mat(0x79482a));
  netHandle.rotation.z = -.44;
  const hoop = mesh(new THREE.TorusGeometry(.16, .014, 5, 12), mat(0xb8dfd3, .65), -.22, .39, -.02, false);
  hoop.rotation.x = Math.PI / 2;
  const netting = mesh(new THREE.CircleGeometry(.145, 12), new THREE.MeshStandardMaterial({ color: 0x91c9be, transparent: true, opacity: .38, side: THREE.DoubleSide, roughness: .78 }), -.22, .39, -.018, false);
  netting.rotation.x = -Math.PI / 2;
  const netGrid = new THREE.LineSegments(new THREE.WireframeGeometry(new THREE.CircleGeometry(.145, 12)), new THREE.LineBasicMaterial({ color: 0xd1eee1, transparent: true, opacity: .72 }));
  netGrid.position.set(-.22, .39, -.016); netGrid.rotation.x = -Math.PI / 2;
  net.add(netHandle, hoop, netting, netGrid);
  for (const angle of [0, Math.PI * .67, Math.PI * 1.33]) net.add(mesh(new THREE.SphereGeometry(.022, 7, 5), mat(0xe7bd58, .62), -.22 + Math.cos(angle) * .145, .39, -.02 + Math.sin(angle) * .145, false));
  const wateringCan = new THREE.Group();
  const canBody = mesh(new THREE.SphereGeometry(.13, 12, 8), mat(0x6f9c8b, .6), 0, .08, 0, false);
  canBody.scale.set(1.2, .86, .9);
  const canHandle = mesh(new THREE.TorusGeometry(.095, .014, 5, 10, Math.PI), mat(0x536f64, .62), -.02, .17, 0, false);
  const canSpout = curvedRod(.28, .016, mat(0x6f9c8b, .6));
  canSpout.position.set(.12, .12, .01); canSpout.rotation.z = -.95;
  const canRose = mesh(new THREE.CylinderGeometry(.04, .026, .035, 10), mat(0x536f64, .62), .29, .25, .01, false);
  canRose.rotation.z = -.55;
  const waterDrops = new THREE.Group();
  const dropMaterial = new THREE.MeshBasicMaterial({ color: 0x9be9ed, transparent: true, opacity: .76, depthWrite: false });
  for (let index = 0; index < 6; index++) {
    const drop = mesh(new THREE.SphereGeometry(.018 + index % 2 * .006, 7, 5), dropMaterial, .27, -.03 - index * .085, .02 + (index % 3 - 1) * .024, false);
    waterDrops.add(drop);
  }
  waterDrops.visible = false;
  wateringCan.add(canBody, canHandle, canSpout, canRose, waterDrops);
  const dive = new THREE.Group();
  const suit = mesh(new THREE.SphereGeometry(.30, 16, 10), mat(0x285d67, .62), 0, .815, 0, false);
  suit.scale.set(1, .92, .72);
  const maskRim = mesh(new THREE.TorusGeometry(.16, .018, 6, 16), mat(0x506e73, .42), 0, 1.47, .337, false);
  maskRim.scale.set(1.55, .77, 1);
  const maskGlass = mesh(new THREE.CircleGeometry(.148, 16), new THREE.MeshStandardMaterial({ color: 0xa8e1e4, transparent: true, opacity: .5, roughness: .22, side: THREE.DoubleSide }), 0, 1.47, .339, false);
  maskGlass.scale.set(1.55, .77, 1);
  const snorkel = curvedRod(.28, .012, mat(0x4b7d7c));
  snorkel.position.set(.30, 1.49, .19); snorkel.rotation.z = -.25;
  dive.add(suit, maskRim, maskGlass, snorkel);
  const fins = [];
  for (const [index, x] of [-.13, .13].entries()) {
    const tank = mesh(new THREE.CylinderGeometry(.065, .075, .35, 8), mat(0x79969b, .46), x, .82, -.32, false);
    tank.rotation.z = x * .08;
    const fin = mesh(new THREE.ConeGeometry(.12, .42, 5), mat(0x315f68, .72), 0, -.48, .20, false);
    fin.rotation.x = Math.PI / 2;
    fin.visible = false;
    [leftLeg, rightLeg][index].add(fin);
    fins.push(fin);
    dive.add(tank);
  }
  rod.visible = false;
  shovel.visible = false;
  axe.visible = false;
  net.visible = false;
  wateringCan.visible = false;
  dive.visible = false;
  attachAvatarTools(toolRoot, { rod, shovel, axe, net, wateringCan });
  // The older capsules remain as a no-asset fallback, but the shipped hero is
  // a Blender-authored, named-pivot model.  Its body silhouette and the
  // animation pivots stay separate, so a chop or casting pose bends limbs
  // instead of swinging a single block character.
  const visual = avatarTemplate?.clone(true);
  const importedLimbs = visual && {
    leftLeg: visual.getObjectByName('Avatar_Leg_L'), rightLeg: visual.getObjectByName('Avatar_Leg_R'),
    leftArm: visual.getObjectByName('Avatar_Arm_L'), rightArm: visual.getObjectByName('Avatar_Arm_R'),
  };
  const importedGrip = visual?.getObjectByName('Avatar_ToolGrip');
  if (importedLimbs && Object.values(importedLimbs).every(Boolean)) {
    actor.add(visual);
    actor.userData.limbs = importedLimbs;
    const faceHead = visual.getObjectByName('Avatar_HeadPivot');
    const eyes = ['Avatar_Eye_L', 'Avatar_Eye_R'].map((name) => visual.getObjectByName(name));
    if (faceHead && eyes.every(Boolean)) actor.userData.face = { head: faceHead, eyes };
    // Fit equipment to the longer legs and smaller face of the current hero.
    suit.position.y = 1.08; suit.scale.set(.86, 1.1, .65);
    maskRim.position.set(0, 1.62, .207); maskRim.scale.set(1, .65, 1);
    maskGlass.position.set(0, 1.62, .209); maskGlass.scale.set(1, .65, 1);
    snorkel.position.set(.21, 1.65, .11);
    dive.children.filter(child => child.geometry?.type === 'CylinderGeometry').forEach(tank => {
      tank.position.y = 1.08; tank.position.z = -.25;
    });
    fins.forEach((fin, index) => { fin.position.y = -.73; (index ? importedLimbs.rightLeg : importedLimbs.leftLeg).add(fin); });
    if (importedGrip) {
      importedGrip.add(toolRoot);
      toolRoot.position.set(0, 0, 0);
      toolRoot.rotation.set(0, 0, 0);
    } else actor.add(toolRoot);
  } else {
    actor.add(torso, head, hair, hatBrim, hatCrown, pack, leftLeg, rightLeg, leftArm, rightArm, toolRoot);
    actor.userData.limbs = { leftLeg, rightLeg, leftArm, rightArm };
    for (const x of [-.126, .126]) {
      const white = mesh(new THREE.SphereGeometry(1, 12, 8), mat(0xf8f3de), x, 1.472, .314, false);
      white.scale.set(.073, .095, .024);
      const pupil = mesh(new THREE.SphereGeometry(1, 10, 6), mat(0x32241b), x, 1.468, .338, false);
      pupil.scale.set(.039, .062, .012);
      actor.add(white, pupil);
    }
    rightArm.add(toolRoot);
    toolRoot.position.set(0, -.425, .06);
  }
  actor.add(dive);
  actor.scale.setScalar(AVATAR_WORLD_SCALE);
  actor.position.set(state.shelter.inside ? HUT_DOOR_INSIDE.x : HUT_SPAWN.x, 2.03, state.shelter.inside ? HUT_DOOR_INSIDE.z : HUT_SPAWN.z);
  actor.userData.fins = fins;
  actor.userData.tools = { rod, shovel, axe, net, wateringCan, dive };
  actor.userData.effects = { waterDrops, rodLine, float };
  actor.userData.upgrades = { rod, rodGuides, rodWrap, rodReel, spade, shovelCollar, shovelGrip };
  actor.userData.toolRoot = toolRoot;
  actor.userData.actionUntil = 0;
  actor.userData.startY = actor.position.y;
  prepareAvatarSeatedRig(actor);
  scene.add(actor);
  avatar = actor;
  diveMarker = new THREE.Group();
  const bubbleMaterial = new THREE.MeshStandardMaterial({ color: 0xc6f6e9, emissive: 0x459b9e, emissiveIntensity: .28, roughness: .2, transparent: true, opacity: .82 });
  for (const [x, y, z, radius] of [[0, .02, 0, .075], [.19, .1, -.11, .045], [-.16, .16, .07, .036]]) {
    diveMarker.add(mesh(new THREE.SphereGeometry(radius, 8, 6), bubbleMaterial, x, y, z, false));
  }
  diveMarker.visible = false;
  scene.add(diveMarker);
  swimWake = new THREE.Mesh(new THREE.RingGeometry(.36, .45, 32),
    new THREE.MeshBasicMaterial({ color: 0xdafbf2, transparent: true, opacity: .38, depthWrite: false, side: THREE.DoubleSide }));
  swimWake.rotation.x = -Math.PI / 2;
  swimWake.renderOrder = 4;
  swimWake.visible = false;
  scene.add(swimWake);
}

function playAvatarAction(kind) {
  if (!avatar) return;
  resetAvatarSeatedJoints(avatar);
  avatar.userData.toolRoot.rotation.set(0, 0, 0);
  const tools = avatar.userData.tools;
  for (const [name, object] of Object.entries(tools)) object.visible = name === kind;
  avatar.userData.actionKind = kind;
  avatar.userData.actionStarted = sceneElapsed;
  avatar.userData.actionDuration = ({ rod: 1.45, net: 1.22, hand: .74, diveCatch: 1.05, axe: 1.06, shovel: .88, wateringCan: 1.12 })[kind] || .7;
  avatar.userData.actionUntil = sceneElapsed + avatar.userData.actionDuration;
}

function spawnCatchEffects(target, count) {
  if (!target?.source || !avatar) return;
  for (let i = 0; i < Math.min(count, 2); i++) {
    const visual = target.source.clone(true);
    visual.traverse((part) => {
      if (!part.isMesh) return;
      part.castShadow = false;
      part.receiveShadow = false;
      const sourceMaterial = part.material;
      part.material = sourceMaterial.clone();
      // Keep this species' authored anatomy and tail shader during the catch
      // arc; only the fading opacity belongs to the short-lived clone.
      part.material.onBeforeCompile = sourceMaterial.onBeforeCompile;
      part.material.customProgramCacheKey = sourceMaterial.customProgramCacheKey;
      part.material.transparent = true;
    });
    const start = new THREE.Vector3(target.x + i * .16, Math.max(water.position.y + .18, target.y + .25), target.z);
    const end = new THREE.Vector3(avatar.position.x, avatar.position.y + (marineMode === 'dive' ? .7 : 1.0), avatar.position.z + .12);
    const scale = Math.max(.52, target.member.size * .72);
    visual.scale.setScalar(scale);
    visual.position.copy(start);
    visual.renderOrder = 5;
    scene.add(visual);
    catchEffects.push({ visual, start, end, age: -i * .12, duration: 1.15, scale });
  }
}

function updateCatchEffects(deltaSeconds) {
  for (let i = catchEffects.length - 1; i >= 0; i--) {
    const effect = catchEffects[i];
    effect.age += deltaSeconds;
    const t = THREE.MathUtils.clamp(effect.age / effect.duration, 0, 1);
    effect.visual.position.lerpVectors(effect.start, effect.end, t);
    effect.visual.position.y += Math.sin(t * Math.PI) * .75;
    effect.visual.rotation.y += deltaSeconds * 7;
    effect.visual.rotation.z = Math.sin(t * Math.PI) * .25;
    effect.visual.scale.setScalar(effect.scale * (1 - t * .45));
    effect.visual.traverse((part) => { if (part.isMesh) part.material.opacity = Math.min(1, (1 - t) * 3); });
    if (t < 1) continue;
    scene.remove(effect.visual);
    effect.visual.traverse((part) => { if (part.isMesh) part.material.dispose(); });
    catchEffects.splice(i, 1);
  }
}

function refreshEquipmentVisuals() {
  if (!avatar) return;
  const { rod, rodGuides, rodWrap, rodReel, spade, shovelCollar, shovelGrip } = avatar.userData.upgrades;
  rod.scale.y = 1 + (state.rodLevel - 1) * .08;
  for (const guide of rodGuides) guide.visible = state.rodLevel >= 2;
  rodWrap.visible = state.rodLevel >= 3;
  rodReel.visible = state.rodLevel >= 2;
  spade.scale.set(.72 + (state.shovelLevel - 1) * .11, 1.22 + (state.shovelLevel - 1) * .12, .22 + (state.shovelLevel - 1) * .035);
  spade.material.color.setHex(state.shovelLevel >= 3 ? 0xc1ccca : 0x6f7d79);
  shovelCollar.visible = state.shovelLevel >= 2;
  shovelGrip.visible = state.shovelLevel >= 3;
}

function waterDepthMetres(x, z) {
  return Math.max(0, water.position.y - groundHeightAt(x, z));
}

function diveDepthLimit() { return state.diveLevel === 2 ? 30 : state.diveLevel === 1 ? 25 : 3; }

function coastTarget(x, z) {
  const distance = Math.hypot(x, z);
  if (!distance) return { x, z };
  const shore = shoreRadiusAt(x, z);
  const maxDistance = shore + (marineMode === 'dive' && !state.diveLevel ? 4.5 : MAX_SURFACE_DISTANCE);
  let targetDistance = Math.min(distance, maxDistance);
  if (marineMode === 'dive' && waterDepthMetres(x * targetDistance / distance, z * targetDistance / distance) > diveDepthLimit()) {
    let safe = shore, unsafe = targetDistance;
    for (let i = 0; i < 12; i++) {
      const middle = (safe + unsafe) / 2;
      if (waterDepthMetres(x * middle / distance, z * middle / distance) <= diveDepthLimit()) safe = middle;
      else unsafe = middle;
    }
    targetDistance = safe;
  }
  return { x: x * targetDistance / distance, z: z * targetDistance / distance };
}

function groundHeightAt(x, z) {
  if (Math.hypot(x - groundSample.x, z - groundSample.z) < .015) return groundSample.y;
  groundRaycaster.set(new THREE.Vector3(x, 30, z), new THREE.Vector3(0, -1, 0));
  groundSample.x = x;
  groundSample.z = z;
  groundSample.y = groundRaycaster.intersectObjects(walkGroundMeshes, false)[0]?.point.y ?? 2.03;
  return groundSample.y;
}

const trackRaycaster = new THREE.Raycaster();
const trackOrigin = new THREE.Vector3(), trackDown = new THREE.Vector3(0, -1, 0);
function sampleTrackSand(x, z) {
  trackRaycaster.set(trackOrigin.set(x, 30, z), trackDown);
  const hit = trackRaycaster.intersectObjects(walkGroundMeshes, false)[0];
  if (!hit) return null;
  let inland, runupLimit = 3.2;
  if (hit.object.name === 'Island_Sand') {
    const angle = Math.atan2(-z, x), radius = Math.hypot(x, z);
    inland = coastlineRadius(angle) - radius;
    if (inland < .10 || radius < coastlineBandRadius(angle, 10.5) + .16) return null;
    const deck = DOCK_DECK_OBSTACLE, dx = x - deck.x, dz = z - deck.z;
    const localX = dx * Math.cos(deck.angle) + dz * Math.sin(deck.angle);
    const localZ = -dx * Math.sin(deck.angle) + dz * Math.cos(deck.angle);
    if (Math.abs(localX) < deck.halfWidth && Math.abs(localZ) < deck.halfDepth) return null;
  } else if (hit.object.name.startsWith('VoyageTerrain_')) {
    const island = ISLANDS.find(i => hit.object.name === `VoyageTerrain_${i.id}`);
    if (!island) return null;
    const dx = x - island.x, dz = z - island.z, angle = Math.atan2(dx, dz);
    const shore = island.radius * (.955 + .025 * Math.sin(angle * 3 + island.x) + .015 * Math.sin(angle * 5 + island.z));
    const radius = Math.hypot(dx, dz);
    if (radius < shore * .80 || radius > shore - .10) return null;
    inland = shore - radius; runupLimit = Math.min(3.2, island.radius * .22);
  } else return null;
  const normal = hit.face.normal.clone().transformDirection(hit.object.matrixWorld);
  return { y: hit.point.y, normal: { x: normal.x, y: normal.y, z: normal.z }, inland, runupLimit };
}

function surfaceDive(text) {
  marineMode = 'swim';
  diveSecondsLeft = 0;
  avatarRoute = [];
  avatarTarget = null;
  arrivalAction = null;
  if (text) flash(text);
}

function updateEndurance(deltaSeconds) {
  if (!avatar) return;
  const inWater = !isBoatPassenger(boatTrip, fishingSession) && !voyage?.engaged && marineMode !== 'land';
  if (!inWater) staminaRuntime.returning = false;
  const working = !restTransition && ((busyAction && !cookingJob) || Boolean(fishingSession)
    || avatar.userData.actionUntil > sceneElapsed);
  stepStamina(state, staminaRuntime, deltaSeconds, {
    effort: inWater && !staminaRuntime.returning ? SWIM_STAMINA_DRAIN : 0,
    busy: working || inWater,
  });
  if (inWater && state.stamina <= 0 && !staminaRuntime.returning) {
    releaseMarineTarget();
    surfaceDive();
    staminaRuntime.returning = true;
    const angle = Math.atan2(avatar.position.z, avatar.position.x);
    const radius = shoreRadiusAt(avatar.position.x, avatar.position.z) - 1.25;
    avatarTarget = new THREE.Vector3(Math.cos(angle) * radius, avatar.position.y, Math.sin(angle) * radius);
    flash('体力耗尽，正在游回岸边 · 上岸后自然恢复');
  }
}

function toggleDive() {
  if (staminaRuntime.returning) { flash('体力耗尽，正在游回岸边'); return; }
  if (marineMode === 'dive') { surfaceDive('已经浮出水面'); return; }
  if (marineMode !== 'swim') { flash('先走到海边，再点击海面进入水中'); return; }
  if (waterDepthMetres(avatar.position.x, avatar.position.z) < 1) {
    flash('这里水深不足 1 米，请先游到稍深的浅海'); return;
  }
  if (waterDepthMetres(avatar.position.x, avatar.position.z) > diveDepthLimit()) {
    flash(`当前水深超出 ${diveDepthLimit()} 米下潜上限，请游回近岸`); return;
  }
  if (state.diveLevel) {
    if (state.oxygen < 12 && (state.inventory.diveSupply || 0) > 0) {
      state.inventory.diveSupply--;
      if (!state.inventory.diveSupply) delete state.inventory.diveSupply;
      refillOxygen(state);
    }
    if (state.oxygen < 12) { flash('氧气瓶不足，请购买潜水耗材包'); return; }
    diveSecondsMax = Math.min(state.diveLevel === 2 ? 75 : 50, state.oxygen / 2);
  } else diveSecondsMax = 12;
  diveSecondsLeft = diveSecondsMax;
  marineMode = 'dive';
  flash(state.diveLevel ? `开始潜水 · ${Math.ceil(diveSecondsMax)} 秒氧气 · 限深 ${diveDepthLimit()} 米`
    : '徒手闭气潜水 · 最多 12 秒、3 米；氧气耗尽自动上浮');
}

function updateCoastalAvatar(deltaSeconds) {
  if (!avatar) return;
  if (restTransition || bedApproach || avatarVault) return;
  if (isBoatPassenger(boatTrip, fishingSession) || voyage?.engaged) {
    if (swimWake) swimWake.visible = false;
    if (diveMarker) diveMarker.visible = false;
    return;
  }
  const depth = waterDepthMetres(avatar.position.x, avatar.position.z);
  if (depth < .25) {
    marineMode = 'land';
    diveBlend = 0;
    diveSecondsLeft = 0;
    avatar.position.y = groundHeightAt(avatar.position.x, avatar.position.z) + .02;
    avatar.rotation.x = THREE.MathUtils.damp(avatar.rotation.x, 0, 8, deltaSeconds);
    avatar.userData.tools.dive.visible = false;
    avatar.userData.fins.forEach((fin) => { fin.visible = false; });
    if (avatar.userData.actionUntil <= sceneElapsed) {
      avatar.userData.limbs.leftArm.rotation.z = THREE.MathUtils.damp(avatar.userData.limbs.leftArm.rotation.z, -.22, 7, deltaSeconds);
      avatar.userData.limbs.rightArm.rotation.z = THREE.MathUtils.damp(avatar.userData.limbs.rightArm.rotation.z, .22, 7, deltaSeconds);
    }
    if (diveMarker) diveMarker.visible = false;
    if (swimWake) swimWake.visible = false;
    return;
  }
  if (marineMode === 'land') { marineMode = 'swim'; flash('已进入海水 · 活动页可下潜，点击沙滩返回'); }
  if (marineMode === 'dive') {
    diveSecondsLeft -= deltaSeconds;
    if (state.diveLevel) state.oxygen = Math.max(0, state.oxygen - deltaSeconds * 2);
    if (diveSecondsLeft <= 0 || (state.diveLevel && state.oxygen <= 0)) {
      surfaceDive('氧气耗尽，已自动上浮');
    } else if (depth > diveDepthLimit() + .1) {
      surfaceDive(`超过 ${diveDepthLimit()} 米安全深度，已自动上浮`);
    }
  }
  diveBlend = THREE.MathUtils.damp(diveBlend, marineMode === 'dive' ? 1 : 0, 5, deltaSeconds);
  const groundY = groundHeightAt(avatar.position.x, avatar.position.z);
  const surfaceY = Math.max(water.position.y - .55, groundY + .03);
  const diveY = Math.max(water.position.y - 1.0, groundY + .12);
  avatar.position.y = THREE.MathUtils.lerp(surfaceY, diveY, diveBlend)
    + Math.sin(sceneElapsed * (avatarTarget ? 7 : 3)) * (marineMode === 'dive' ? .025 : .04);
  avatar.rotation.x = THREE.MathUtils.damp(avatar.rotation.x, .12 + diveBlend * 1.03, 5, deltaSeconds);
  const limbs = avatar.userData.limbs;
  const moving = Boolean(avatarTarget || avatarRoute.length);
  const catching = avatar.userData.actionUntil > sceneElapsed
    && ['rod', 'net', 'hand', 'diveCatch'].includes(avatar.userData.actionKind);
  const phase = sceneElapsed * (moving ? 8 : 3.5);
  limbs.leftLeg.rotation.x = Math.sin(phase) * (marineMode === 'dive' ? .52 : moving ? .35 : .12);
  limbs.rightLeg.rotation.x = -limbs.leftLeg.rotation.x;
  if (!catching) {
    if (marineMode === 'dive') {
      limbs.leftArm.rotation.x = -1.05 + Math.sin(phase) * .24;
      limbs.rightArm.rotation.x = -1.05 - Math.sin(phase) * .24;
      limbs.leftArm.rotation.z = -.08;
      limbs.rightArm.rotation.z = .08;
    } else {
      limbs.leftArm.rotation.x = Math.sin(phase) * (moving ? .62 : .28);
      limbs.rightArm.rotation.x = -limbs.leftArm.rotation.x;
      limbs.leftArm.rotation.z = -.45 - Math.cos(phase) * .18;
      limbs.rightArm.rotation.z = .45 + Math.cos(phase) * .18;
    }
  }
  avatar.userData.tools.dive.visible = marineMode === 'dive' && state.diveLevel > 0;
  avatar.userData.fins.forEach((fin) => { fin.visible = marineMode === 'dive' && state.diveLevel > 0; });
  if (diveMarker) {
    diveMarker.visible = marineMode === 'dive' || diveBlend > .15;
    diveMarker.position.set(avatar.position.x, water.position.y + .09, avatar.position.z);
    diveMarker.children.forEach((bubble, index) => {
      bubble.position.y = .03 + ((sceneElapsed * .45 + index * .33) % .8);
      bubble.position.x = Math.sin(sceneElapsed * 2 + index * 2.1) * (.07 + index * .04);
    });
  }
  if (swimWake) {
    swimWake.visible = marineMode === 'swim' && moving;
    swimWake.position.set(avatar.position.x, water.position.y + .035, avatar.position.z);
    const ripple = 1 + .18 * Math.sin(sceneElapsed * 8);
    swimWake.scale.setScalar(ripple);
    swimWake.material.opacity = moving ? .28 + .1 * Math.sin(sceneElapsed * 8) : 0;
  }
}

function walkTo(x, z, action = null, stopDistance = .72, quiet = false) {
  // A deliberate click owns its new route even if manual input was released
  // in this same frame. Releasing the old stick must not erase that action.
  if (!quiet) { padWalking = false; movementKeys.clear(); }
  if (avatarVault) { if (!quiet) flash('等落地后再操作'); return false; }
  if (staminaRuntime.returning) { flash('体力耗尽，正在游回岸边'); return false; }
  if (voyage?.engaged) { flash('正在远航，请先返航靠岸后操作家园设施'); return false; }
  const shelterAction = ['hut-door-open', 'hut-door-close'].includes(action?.type);
  if (storm.phase === 'impact' && !shelterAction) { if (!quiet) flash('台风正在过境，请留在小屋避险'); return false; }
  if (busyAction) { if (!quiet) flash('请先完成当前动作'); return false; }
  if (state.shelter.inside) { if (!quiet) flash('先开门离开小屋，再操作屋外设施'); return false; }
  if (activeMarineTarget && !['capture', 'sea-capture', 'trip-start'].includes(action?.type)) releaseMarineTarget();
  ({ x, z } = coastTarget(x, z));
  const colliders = getHomeColliders();
  const start = resolveCharacterStep(avatar.position, avatar.position, colliders, { feetY: avatar.position.y });
  avatar.position.x = start.x; avatar.position.z = start.z;
  const obstacles = homeWalkObstacles(colliders);
  const route = planWalkRoute(avatar.position, { x, z }, obstacles, MAX_ROUTE_RADIUS);
  if (!route.length) { if (!quiet) flash('这里被建筑或植被挡住，暂时无法到达'); return false; }
  avatarRoute = route.slice(1).map((point) => new THREE.Vector3(point.x, avatar.position.y, point.z));
  avatarTarget = new THREE.Vector3(route[0].x, avatar.position.y, route[0].z);
  // A blocked destination is projected to the nearest reachable edge.
  const projected = Math.hypot(route.at(-1).x - x, route.at(-1).z - z) > .3;
  arrivalAction = action ? { ...action, stopDistance: projected ? .12 : stopDistance } : null;
  if (!quiet) showWalkDestination(route.at(-1));
  if (Math.hypot(x - avatar.position.x, z - avatar.position.z) > stopDistance) {
    const worldAction = action?.root?.userData.action;
    const notice = action?.type === 'hut-door-open' ? '已选小屋门 · 正前往开门'
      : action?.type === 'hut-door-close' ? '已选小屋门 · 正前往关门'
        : action?.type === 'water-station' ? '已选淡水收集场 · 到达后查看储水'
          : action?.type === 'voyage-board' ? '已选小船 · 正前往码头登船'
            : worldAction?.type === 'gather' ? `已选${worldAction.kind === 'wood' ? '木材' : '贝壳'} · 到达后采集` : null;
    if (notice) flash(notice);
  }
  if (!avatarRoute.length && Math.hypot(avatarTarget.x - avatar.position.x, avatarTarget.z - avatar.position.z) <= (arrivalAction?.stopDistance ?? .12)) {
    avatarTarget = null;
    const next = arrivalAction;
    arrivalAction = null;
    if (next) completeArrival(next);
  }
  return true;
}

function showWalkDestination(point) {
  if (!walkDestinationMarker) {
    walkDestinationMarker = new THREE.Mesh(new THREE.RingGeometry(.34, .48, 32),
      new THREE.MeshBasicMaterial({ color: 0xffe4a0, transparent: true, opacity: .9,
        depthWrite: false, side: THREE.DoubleSide }));
    walkDestinationMarker.rotation.x = -Math.PI / 2;
    scene.add(walkDestinationMarker);
  }
  walkDestinationMarker.position.set(point.x, Math.max(groundHeightAt(point.x, point.z), water.position.y) + .08, point.z);
  walkDestinationMarker.visible = true;
  walkDestinationUntil = Date.now() + 2400;
}

function updateWalkDestination() {
  if (!walkDestinationMarker) return;
  const remaining = walkDestinationUntil - Date.now();
  walkDestinationMarker.visible = remaining > 0 && !state.shelter.inside && !voyage?.engaged;
  if (!walkDestinationMarker.visible) return;
  walkDestinationMarker.scale.setScalar(1 + Math.sin(remaining / 150) * .12);
  walkDestinationMarker.material.opacity = Math.min(.9, remaining / 600);
}

function updateAvatar(deltaSeconds) {
  if (restTransition || bedApproach) return;
  if (voyage?.engaged) return;
  if (!avatar || hutCrossing || (storm.phase === 'impact' && !avatarVault && !['hut-door-open', 'hut-door-close'].includes(arrivalAction?.type))) return;
  sceneElapsed += deltaSeconds;
  const wasSeated = avatar.userData.seatedRig?.seated;
  resetAvatarSeatedJoints(avatar);
  if (wasSeated) avatar.rotation.set(0, avatar.rotation.y, 0);
  if (boatTrip) return;
  const limbs = avatar.userData.limbs;
  if (avatarVault) {
    const step = stepVault(avatarVault, deltaSeconds);
    avatar.position.set(step.x, step.y, step.z);
    limbs.leftLeg.rotation.x = limbs.rightLeg.rotation.x = -.24;
    limbs.leftArm.rotation.x = limbs.rightArm.rotation.x = .24;
    for (const joint of avatar.userData.seatedRig?.knees || []) joint.rotation.x = .55;
    if (step.done) { avatarVault = null; resetAvatarSeatedJoints(avatar); }
    return;
  }
  if (marineMode !== 'land' && !fishingSession && avatar.userData.actionKind !== 'diveCatch') avatar.userData.actionUntil = 0;
  const acting = avatar.userData.actionUntil > sceneElapsed;
  if (!acting && !fishingSession) {
    for (const [name, tool] of Object.entries(avatar.userData.tools)) if (name !== 'dive') tool.visible = false;
  }
  const effects = avatar.userData.effects;
  if (effects?.waterDrops) effects.waterDrops.visible = false;
  if (effects?.rodLine) effects.rodLine.visible = false;
  if (cookingJob) {
    limbs.leftArm.rotation.x = -.85;
    limbs.rightArm.rotation.x = -.9 + Math.sin(sceneElapsed * 7) * .12;
    limbs.leftArm.rotation.z = -.16;
    limbs.rightArm.rotation.z = .25 + Math.cos(sceneElapsed * 7) * .12;
    avatar.userData.toolRoot.rotation.set(0, 0, 0);
  } else if (acting) {
    const progress = THREE.MathUtils.clamp((sceneElapsed - avatar.userData.actionStarted) / avatar.userData.actionDuration, 0, 1);
    const beat = Math.sin(sceneElapsed * 15);
    const cast = Math.sin(progress * Math.PI);
    const action = avatar.userData.actionKind;
    poseAvatarWorkElbows(avatar, action, progress);
    if (action === 'rod') {
      // Step back, cast, then lean into a short reel: visibly distinct from a net sweep.
      limbs.leftArm.rotation.x = -.32 - cast * .66;
      limbs.rightArm.rotation.x = -.58 - cast * .92;
      limbs.leftArm.rotation.z = -.34 - cast * .18;
      limbs.rightArm.rotation.z = .24 + cast * .42;
      avatar.userData.toolRoot.rotation.x = -.16 - cast * .78;
      avatar.userData.toolRoot.rotation.z = -.48 + cast * .76;
      if (effects?.rodLine) {
        effects.rodLine.visible = true;
        effects.rodLine.scale.y = 1 + cast * .68;
        effects.float.position.y = .18 - cast * .16;
      }
    } else if (action === 'net') {
      limbs.leftArm.rotation.x = -.5 - cast * .9;
      limbs.rightArm.rotation.x = -.36 - cast * 1.02;
      limbs.leftArm.rotation.z = -.4 + cast * .2;
      limbs.rightArm.rotation.z = .43 + cast * .22;
      avatar.userData.toolRoot.rotation.x = .3 + cast * .3;
      avatar.userData.toolRoot.rotation.z = -.88 + cast * 1.58;
    } else if (action === 'hand' || action === 'diveCatch') {
      const reach = action === 'diveCatch' ? 1.22 : .9;
      limbs.leftArm.rotation.x = -reach - cast * .35;
      limbs.rightArm.rotation.x = -reach - cast * .45;
      limbs.leftArm.rotation.z = -.12 - cast * .28;
      limbs.rightArm.rotation.z = .12 + cast * .28;
      avatar.userData.toolRoot.rotation.set(0, 0, 0);
    } else if (action === 'axe') {
      const windup = progress < .42 ? progress / .42 : 1 - (progress - .42) / .58;
      const impact = Math.exp(-Math.pow((progress - .64) / .10, 2));
      limbs.leftArm.rotation.x = -.28 - windup * .85 + impact * .34;
      limbs.rightArm.rotation.x = -.4 - windup * 1.12 + impact * .54;
      limbs.leftArm.rotation.z = -.32 - windup * .52;
      limbs.rightArm.rotation.z = .46 + windup * .66;
      limbs.leftLeg.rotation.x = .18; limbs.rightLeg.rotation.x = -.14;
      avatar.userData.toolRoot.rotation.x = .75 - windup * .6 + impact * .2;
      avatar.userData.toolRoot.rotation.z = -.12 + windup * .18 - impact * .25;
    } else if (action === 'shovel') {
      const scoop = Math.sin(progress * Math.PI);
      limbs.leftArm.rotation.x = -.38 - scoop * .68;
      limbs.rightArm.rotation.x = -.22 - scoop * .84;
      limbs.leftArm.rotation.z = -.42 + scoop * .12;
      limbs.rightArm.rotation.z = .34 + scoop * .2;
      limbs.leftLeg.rotation.x = .12; limbs.rightLeg.rotation.x = -.1;
      avatar.userData.toolRoot.rotation.x = .25 - scoop * .88;
      avatar.userData.toolRoot.rotation.z = -.28 + scoop * .38;
    } else if (action === 'wateringCan') {
      const pour = Math.sin(progress * Math.PI);
      limbs.leftArm.rotation.x = -.35 - pour * .58;
      limbs.rightArm.rotation.x = -.2 - pour * .74;
      limbs.leftArm.rotation.z = -.42 - pour * .16;
      limbs.rightArm.rotation.z = .33 + pour * .31;
      limbs.leftLeg.rotation.x = .07; limbs.rightLeg.rotation.x = -.07;
      avatar.userData.toolRoot.rotation.x = -.12 - pour * .34;
      avatar.userData.toolRoot.rotation.z = -.1 - pour * .72;
      if (effects?.waterDrops) {
        effects.waterDrops.visible = progress > .18 && progress < .84;
        effects.waterDrops.children.forEach((drop, index) => {
          const phase = (sceneElapsed * 4.5 + index * .21) % 1;
          drop.position.set(.27 + phase * .14, -.04 - phase * .54, (index % 3 - 1) * .026);
          drop.scale.setScalar(.72 + phase * .35);
        });
      }
    }
  } else if (fishingSession) {
    poseAvatarWorkElbows(avatar, 'rod', .5);
    const reeling = fishingSession.phase === 'reeling';
    const pull = reeling ? Math.sin(sceneElapsed * 9) * .15 : Math.sin(sceneElapsed * 2) * .035;
    limbs.leftArm.rotation.x = -.42 - pull;
    limbs.rightArm.rotation.x = -.62 + pull;
    limbs.leftArm.rotation.z = -.28;
    limbs.rightArm.rotation.z = .38;
    avatar.userData.toolRoot.rotation.x = -.2 + pull;
    avatar.userData.toolRoot.rotation.z = -.2;
  } else {
    avatar.userData.toolRoot.rotation.x = 0;
    avatar.userData.toolRoot.rotation.z = 0;
  }
  if (!avatarTarget) {
    if (!acting && !fishingSession && !cookingJob) {
      limbs.leftLeg.rotation.x = 0;
      limbs.rightLeg.rotation.x = 0;
      limbs.leftArm.rotation.x = 0;
      limbs.rightArm.rotation.x = 0;
    }
    return;
  }
  const dx = avatarTarget.x - avatar.position.x;
  const dz = avatarTarget.z - avatar.position.z;
  const distance = Math.hypot(dx, dz);
  // Intermediate corners are physical waypoints. Skipping a nearby corner
  // can cut inside the body padding even though both planned segments clear.
  const stop = avatarRoute.length ? .0001 : (arrivalAction?.stopDistance ?? .12) + .025;
  if (distance <= stop) {
    if (avatarRoute.length) {
      avatarTarget = avatarRoute.shift();
      return;
    }
    avatarTarget = null;
    const next = arrivalAction;
    arrivalAction = null;
    if (!acting && !fishingSession) {
      limbs.leftLeg.rotation.x = 0;
      limbs.rightLeg.rotation.x = 0;
      limbs.leftArm.rotation.x = 0;
      limbs.rightArm.rotation.x = 0;
    }
    if (next) completeArrival(next);
    return;
  }
  // Move through the threshold instead of asymptotically parking exactly on
  // it (float rounding could otherwise stall a waypoint forever).
  const stride = Math.min(distance, deltaSeconds * (marineMode === 'dive' ? 1.2 : marineMode === 'swim' ? 1.65 : 3.3));
  const desired = { x: avatar.position.x + dx / distance * stride, z: avatar.position.z + dz / distance * stride };
  const destinationFeet = homeWalkFeetAt(desired.x, desired.z);
  const motion = resolveCharacterStep(avatar.position, desired, getHomeColliders(), {
    feetY: Math.min(avatar.position.y, destinationFeet), height: CHARACTER_HEIGHT + Math.abs(avatar.position.y-destinationFeet) });
  if (motion.blocked && padWalking && beginHomeVault(dx, dz)) return;
  const displacement = Math.hypot(motion.x-avatar.position.x,motion.z-avatar.position.z);
  avatar.position.x = motion.x; avatar.position.z = motion.z;
  if (motion.blocked && displacement < .001 && !padWalking) {
    const destination = avatarRoute.at(-1) || avatarTarget, action = arrivalAction;
    avatarTarget = null; avatarRoute = []; arrivalAction = null;
    walkTo(destination.x, destination.z, action, action?.stopDistance || .12);
    return;
  }
  avatar.rotation.y = Math.atan2(dx, dz);
  const stridePhase = sceneElapsed * 9;
  limbs.leftLeg.rotation.x = Math.sin(stridePhase) * .32;
  limbs.rightLeg.rotation.x = -limbs.leftLeg.rotation.x;
  limbs.leftArm.rotation.x = -limbs.leftLeg.rotation.x * .65;
  limbs.rightArm.rotation.x = -limbs.rightLeg.rotation.x * .65;
}

const rainGroundRay = new THREE.Raycaster(), rainGroundOrigin = new THREE.Vector3();
const rainGroundDown = new THREE.Vector3(0, -1, 0);
const environment = createEnvironment(scene, { rainCount: screen.width < 600 ? 1200 : 2400,
  heightAt: (x, z) => {
    rainGroundRay.set(rainGroundOrigin.set(x, 30, z), rainGroundDown);
    return rainGroundRay.intersectObjects(walkGroundMeshes, false)[0]?.point.y ?? -1.7;
  } });

const seabedGeometry = new THREE.CircleGeometry(220, 96);
seabedGeometry.rotateX(-Math.PI / 2);
{
  const position = seabedGeometry.attributes.position;
  const near = new THREE.Color(0x188b98);
  const far = new THREE.Color(0x03486e);
  const colors = [];
  for (let i = 0; i < position.count; i++) {
    const radius = Math.hypot(position.getX(i), position.getZ(i));
    const color = near.clone().lerp(far, Math.min(1, Math.max(0, (radius - 17) / 30)));
    colors.push(color.r, color.g, color.b);
  }
  seabedGeometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
}
const seabed = mesh(seabedGeometry, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1, metalness: 0 }), 0, -1.7, 0, false);
scene.add(seabed);

const water = mesh(
  new THREE.PlaneGeometry(440, 440),
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      uTime: { value: 0 },
      uCaustics: { value: null },
      uLagoon: { value: new THREE.Color(C.lagoon) },
      uShallow: { value: new THREE.Color(C.shallow) },
      uDeep: { value: new THREE.Color(C.deep) },
      uFoam: { value: new THREE.Color(C.foam) },
      uBoat: { value: new THREE.Vector2(BOAT_MOOR.x, BOAT_MOOR.z) },
      uBoatDir: { value: new THREE.Vector2(1, 0) },
      uBoatWake: { value: 0 },
      uTide: { value: 0 },
      uStorm: { value: 0 },
      uDaylight: { value: 1 },
      uSunlight: { value: 1 },
      uMoonlight: { value: 0 },
      uMoonDirection: { value: new THREE.Vector3(-.6, .7, .4).normalize() },
      uSunDirection: { value: new THREE.Vector3(-.4, .8, -.3).normalize() },
      uIslands: { value: ISLANDS.map(i => new THREE.Vector3(i.x, i.z, i.radius)) },
      uIslandLandingAngles: { value: ISLANDS.map(i => Math.atan2(-i.x, -i.z)) },
    },
    vertexShader: `varying vec3 vWorld; void main(){ vec4 w=modelMatrix*vec4(position,1.); vWorld=w.xyz; gl_Position=projectionMatrix*viewMatrix*w; }`,
    fragmentShader: `
      uniform sampler2D uCaustics; uniform float uTime, uBoatWake, uTide, uStorm, uDaylight,uSunlight,uMoonlight; uniform vec2 uBoat,uBoatDir; uniform vec3 uLagoon,uShallow,uDeep,uFoam,uSunDirection,uMoonDirection; uniform vec3 uIslands[4]; uniform float uIslandLandingAngles[4]; varying vec3 vWorld;
      ${COAST_SWASH_GLSL}
      float hash(vec2 p){ return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453123); }
      float noise(vec2 p){
        vec2 i=floor(p), f=fract(p); f=f*f*(3.-2.*f);
        float a=hash(i), b=hash(i+vec2(1.,0.)), c=hash(i+vec2(0.,1.)), d=hash(i+vec2(1.,1.));
        return mix(mix(a,b,f.x), mix(c,d,f.x), f.y);
      }
      void main(){
        vec2 p=vWorld.xz;
        float ang=atan(-p.y,p.x);
        float r=length(p);
        float shore=${coastlineShaderRadius()};
        // Higher water moves the water/sand intersection inland; lower water
        // reveals the underwater slope. This same contour drives the foam mask.
        float wetShore=shore-uTide*.48;
        float shoreDepth=max(0.0,r-wetShore);
        float runupLimit=3.2;
        for(int i=0;i<4;i++){
          vec2 delta=p-uIslands[i].xy;
          float islandAngle=atan(delta.x,delta.y);
          float islandShore=${islandCoastlineShaderRadius('uIslands[i].z', 'islandAngle', 'uIslands[i].x', 'uIslands[i].y', 'uIslandLandingAngles[i]')};
          float islandWet=islandShore-uTide*.2;
          float islandDepth=max(0.0,length(delta)-islandWet);
          if(islandDepth<shoreDepth){shoreDepth=islandDepth;shore=islandShore;wetShore=islandWet;r=length(delta);ang=islandAngle;runupLimit=min(3.2,uIslands[i].z*.22);}
        }
        float lagoon=smoothstep(.45,5.5,shoreDepth);
        float deep=smoothstep(2.0,8.0,shoreDepth);
        float c1=noise(vec2(p.x*2.6+uTime*.35,p.y*2.6-uTime*.30));
        float c2=noise(vec2(p.x*3.5-uTime*.42,p.y*3.5+uTime*.30));
        float caustic=texture2D(uCaustics,p*.10+vec2(uTime*.012,-uTime*.008)).r;
        caustic=max(caustic,texture2D(uCaustics,p.yx*.087+vec2(-uTime*.007,uTime*.011)).r*.8);
        float lagoonMask=1.0-smoothstep(.0,9.0,shoreDepth);
        float w1=sin(p.x*.5-uTime*(1.1+uStorm*.55))*.5+sin(p.y*.4+uTime*(.9+uStorm*.5))*.5;
        float rip=noise(vec2(p.x*2.8+uTime*(.85+uStorm*.7),p.y*2.8-uTime*(.65+uStorm*.55)))-.5;
        // Water is transparent over dry land. This guard also means surf can
        // never draw a white ring through the beach when shore geometry changes.
        float waterMask=smoothstep(wetShore-.20,wetShore+.95,r);
        // Break the surf into short tangential runs. Its distance comes from the
        // same harmonic shoreline used by the Blender mesh, rather than a circle.
        float along=ang*shore;
        float edgeNoise=(noise(vec2(along*.24+uTime*.12,uTime*.055))-.5)*.24
          +(noise(p*.38+uTime*.045)-.5)*.15;
        vec4 swash=coastSwash(p,shore-r,uTime,uTide,uStorm,runupLimit);
        float shoreFoam=swash.y;
        float surfDistance=shoreDepth+rip*(.13+uStorm*.08)-edgeNoise;
        float breakerOffset=.82+1.1*noise(vec2(along*.13,uTime*.035));
        float outerFoam=smoothstep(breakerOffset,breakerOffset+.13,surfDistance)
          *(1.0-smoothstep(breakerOffset+.20,breakerOffset+.55,surfDistance));
        outerFoam*=smoothstep(.63,.84,noise(vec2(along*.22,uTime*.05)))*.28;
        float foam=max(shoreFoam,outerFoam);
        vec2 boatDelta=p-uBoat;
        vec2 boatSide=vec2(-uBoatDir.y,uBoatDir.x);
        vec2 boatSpace=vec2(dot(boatDelta,uBoatDir),dot(boatDelta,boatSide));
        float trail=clamp((-boatSpace.x-.25)/3.8,0.0,1.0);
        float wakeWidth=.08+trail*.62;
        float wakeWaver=sin(trail*19.0+uTime*1.7)*.055*trail;
        float wakeLines=1.0-smoothstep(.035,.12,abs(abs(boatSpace.y-wakeWaver)-wakeWidth));
        float wakeFade=smoothstep(.25,.6,-boatSpace.x)*(1.0-smoothstep(3.6,4.2,-boatSpace.x));
        foam=max(foam,wakeLines*wakeFade*uBoatWake*(.7+.3*noise(boatSpace*2.0+uTime*.4)));
        foam*=.72+.28*w1;
        vec3 color=mix(uLagoon,uShallow,lagoon);
        color=mix(color,uDeep,deep);
        color+=uFoam*smoothstep(.40,.90,caustic)*(.23+rip*.02)*lagoonMask;
        color=mix(color,uFoam,clamp(foam*(.70+uStorm*.12),0.,.82));
        color+=uFoam*rip*.028;
        // Sky reflection: ripple normals catch a pale sky, so the surface reads
        // as lit and reflective instead of a flat sheet of colour.
        vec2 slope=vec2(cos(p.x*1.3+p.y*.8-uTime*.8)*.18+cos(p.x*3.1-uTime*1.2)*.06,
          sin(p.y*1.7-p.x*.5+uTime*.7)*.16+sin(p.y*2.9+uTime*1.1)*.06);
        vec3 rippleN=normalize(vec3(slope.x,1.0,slope.y));
        float fres=pow(1.0-max(dot(rippleN,vec3(0.,1.,0.)),0.),3.0);
        color=mix(color,vec3(.72,.90,1.0),fres*.30*(1.0-deep*.6));
        vec3 halfLight=normalize(uSunDirection+vec3(.40,.82,.40));
        float sparkle=pow(max(0.,dot(rippleN,halfLight)),220.)*smoothstep(.35,.82,c1);
        color*=mix(.012,1.0,mix(uDaylight,sqrt(max(0.,uDaylight)),smoothstep(.05,.3,uSunlight)));
        color+=vec3(1.,.90,.71)*sparkle*.065*uSunlight*(1.0-uStorm*.65);
        vec3 moonHalfLight=normalize(uMoonDirection+vec3(.40,.82,.40));
        float moonSparkle=pow(max(0.,dot(rippleN,moonHalfLight)),90.);
        color+=vec3(.56,.69,1.)*moonSparkle*.11*uMoonlight;
        float alpha=(mix(.88,.97,deep)+clamp(foam,0.,1.)*.12)*waterMask;
        gl_FragColor=vec4(color,clamp(alpha,0.,1.));
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
  }), 0, -0.02, 0, false,
);
water.rotation.x = -Math.PI / 2;
water.renderOrder = 2;
scene.add(water);

const gltfLoader = new GLTFLoader();
const buildingSources = {};

function parseGlb(bytes) {
  const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  return new Promise((resolve, reject) => gltfLoader.parse(buffer, '', resolve, reject));
}

async function loadAtlas() {
  let image;
  try { image = await loadImage('assets/generated/tropical-atlas.webp'); }
  catch { image = await loadImage('assets/generated/tropical-atlas.png'); }
  const texture = new THREE.Texture(image);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.flipY = false;
  texture.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
  texture.needsUpdate = true;
  return texture;
}

async function loadSurfaceTexture(name, color = true) {
  const texture = new THREE.Texture(await loadImage(`assets/generated/${name}.png`));
  texture.colorSpace = color ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
  texture.needsUpdate = true; return texture;
}

const atlasDoubleSidedNames = new Set(['Palm', 'Bush', 'Sea_Grass', 'Kelp']);

function applyAtlas(root, solidMaterial, leafMaterial = solidMaterial) {
  root.traverse((object) => {
    if (!object.isMesh) return;
    object.material = atlasDoubleSidedNames.has(object.name) ? leafMaterial : solidMaterial;
    object.castShadow = true;
    object.receiveShadow = true;
  });
}

function addInstances(source, placements, material, tint) {
  source.updateMatrixWorld(true);
  const fromRoot = new THREE.Matrix4().copy(source.matrixWorld).invert();
  source.traverse((part) => {
    if (!part.isMesh) return;
    const local = new THREE.Matrix4().multiplyMatrices(fromRoot, part.matrixWorld);
    const instances = new THREE.InstancedMesh(part.geometry, material || part.material, placements.length);
    instances.userData.cameraOccluder = Boolean(source.userData.authoredPlant || part.userData.authoredPlant || /Palm|Bush|Rock/.test(source.name));
    if (/Rock|Bush/.test(source.name)) registerHomeCollision(instances, `${source.name}-${homeCollisionSources.length}`);
    const placement = new THREE.Matrix4();
    const rotation = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    const final = new THREE.Matrix4();
    placements.forEach(([x, y, z, size = 1, angle = 0], index) => {
      rotation.setFromAxisAngle(THREE.Object3D.DEFAULT_UP, angle);
      scale.setScalar(size);
      placement.compose(new THREE.Vector3(x, y, z), rotation, scale);
      instances.setMatrixAt(index, final.multiplyMatrices(placement, local));
      if (tint) instances.setColorAt(index, tint[index % tint.length]);
    });
    if (tint) instances.instanceColor.needsUpdate = true;
    instances.castShadow = true;
    instances.receiveShadow = true;
    scene.add(instances);
  });
}

function createTreeVisuals(source, terrainMeshes) {
  if (!source) return;
  const placements = groundPlacements(source, state.trees.map((tree) => [tree.x, 2, tree.z, tree.scale, tree.angle]), terrainMeshes);
  const sourceBottom = new THREE.Box3().setFromObject(source).min.y;
  const stumpMaterial = mat(0x8f633c);
  const soilMaterial = mat(0x866d4d);
  for (let index = 0; index < state.trees.length; index++) {
    const tree = state.trees[index];
    if (!clearOfFarm(tree.x, tree.z, 1.15 + tree.scale * .35)) continue;
    const placement = placements[index];
    const groundY = placement[1] + sourceBottom * tree.scale;
    const root = actionRoot(new THREE.Group(), { type: 'tree', treeId: tree.id });
    root.position.set(tree.x, groundY, tree.z);
    const crown = source.clone(true);
    crown.traverse(object => { if (object.isMesh) object.userData.cameraOccluder = true; });
    crown.rotation.y = tree.angle;
    const stump = new THREE.Mesh(new THREE.CylinderGeometry(.23, .35, .3, 10), stumpMaterial);
    stump.position.y = .15;
    stump.castShadow = true;
    const soil = new THREE.Mesh(new THREE.CylinderGeometry(.6, .65, .06, 16), soilMaterial);
    soil.position.y = .035;
    soil.receiveShadow = true;
    root.add(crown, stump, soil);
    scene.add(root);
    treeVisuals.set(tree.id, { root, crown, stump, soil, sourceBottom });
    registerHomeCollision(root, `tree-${tree.id}`, { filter: object => object !== soil });
  }
  refreshTreeVisuals();
}

function refreshTreeVisuals() {
  for (const tree of state.trees || []) {
    const visual = treeVisuals.get(tree.id);
    if (!visual) continue;
    const hasTree = tree.stage === 'mature' || tree.stage === 'growing';
    const growth = tree.stage === 'mature' ? 1 : .22 + .78 * Math.min(1, tree.ageMinutes / TREE_MATURE_MINUTES);
    const silhouette = [1.12,1.15,1.05,.66,.62,.61,.59,1.4][Number(tree.id.replace('palm',''))-1] || 1;
    const size = tree.scale * growth * silhouette;
    visual.crown.visible = hasTree;
    visual.crown.scale.setScalar(size);
    visual.crown.position.y = -visual.sourceBottom * size;
    visual.stump.visible = tree.stage === 'stump';
    visual.soil.visible = tree.stage === 'empty' || tree.stage === 'growing';
  }
}

// Reserve every current and future bed, including its label and interaction
// footprint. Clearing only the garden centre left a palm trunk inside a row.
const PLOT_POINTS = [[5.5, 2.2], [10.2, 7.0], [FRESHWATER_SITE.x, FRESHWATER_SITE.z]];
const clearOfFarm = (x, z, radius = 1.4) =>
  FARM_BED_CENTRES.every(([px, pz]) => Math.hypot(x - px, z - pz) >= radius);

// Deterministic scatter for reef/foliage dressing. Build plots are kept clear.
function scatter(count, minR, maxR, seed, y, clearance = 0, minScale = .55, maxScale = 1.25) {
  const out = [];
  let state = seed >>> 0;
  const rand = () => { state = (state * 1664525 + 1013904223) >>> 0; return state / 4294967296; };
  for (let i = 0; i < count; i++) {
    const angle = rand() * Math.PI * 2;
    const radius = minR + rand() * (maxR - minR);
    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;
    if (clearance && (PLOT_POINTS.some(([px, pz]) => Math.hypot(x - px, z - pz) < clearance)
      || !clearOfFarm(x, z, Math.max(1.45, clearance * .75)))) continue;
    out.push([x, y, z, minScale + rand() * (maxScale - minScale), rand() * Math.PI * 2]);
  }
  return out;
}

function addHibiscusBlossoms(placements) {
  const flowers = [[-.36, .68, -.08], [.34, .65, .13], [.04, .73, .35]];
  const petals = new THREE.InstancedMesh(new THREE.SphereGeometry(.1, 8, 6), mat(0xfff1ed), placements.length * flowers.length * 5);
  const centres = new THREE.InstancedMesh(new THREE.SphereGeometry(.045, 7, 5), mat(0xffd565), placements.length * flowers.length);
  const flowerColors = [0xe96a8e, 0xf394a4, 0xf3a473].map((color) => new THREE.Color(color));
  const rotation = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  const matrix = new THREE.Matrix4();
  let petalIndex = 0, centreIndex = 0;
  for (const [x, y, z, size = 1, orientation = 0] of placements) {
    for (let flowerIndex = 0; flowerIndex < flowers.length; flowerIndex++) {
      const [fx, fy, fz] = flowers[flowerIndex];
      const cx = x + (fx * Math.cos(orientation) - fz * Math.sin(orientation)) * size;
      const cz = z + (fx * Math.sin(orientation) + fz * Math.cos(orientation)) * size;
      const cy = y + fy * size;
      centres.setMatrixAt(centreIndex++, matrix.compose(new THREE.Vector3(cx, cy + .035 * size, cz),
        new THREE.Quaternion(), new THREE.Vector3(size, size, size)));
      for (let petal = 0; petal < 5; petal++) {
        const angle = orientation + petal * Math.PI * 2 / 5;
        rotation.setFromAxisAngle(Y_AXIS, -angle);
        scale.set(.96 * size, .28 * size, 1.2 * size);
        petals.setMatrixAt(petalIndex, matrix.compose(new THREE.Vector3(
          cx + Math.cos(angle) * .095 * size, cy, cz + Math.sin(angle) * .095 * size), rotation, scale));
        petals.setColorAt(petalIndex++, flowerColors[flowerIndex]);
      }
    }
  }
  petals.instanceMatrix.needsUpdate = true;
  petals.instanceColor.needsUpdate = true;
  centres.instanceMatrix.needsUpdate = true;
  petals.castShadow = centres.castShadow = false;
  scene.add(petals, centres);
}

const marineHitGeometry = new THREE.SphereGeometry(.78, 8, 6);
const marineHitMaterial = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false });

function targetAvailable(target) {
  return target && (state.gatherCooldowns[target.id] || 0) <= state.gameMinutes;
}

function releaseMarineTarget() {
  if (activeMarineTarget) activeMarineTarget.reserved = false;
  activeMarineTarget = null;
  if (marineTargetMarker) marineTargetMarker.visible = false;
}

function reserveMarineTarget(target) {
  releaseMarineTarget();
  target.reserved = true;
  activeMarineTarget = target;
  if (!marineTargetMarker) {
    marineTargetMarker = new THREE.Mesh(new THREE.RingGeometry(.72, .88, 32),
      new THREE.MeshBasicMaterial({ color: 0xffd773, side: THREE.DoubleSide, depthTest: false, transparent: true, opacity: .88 }));
    marineTargetMarker.rotation.x = -Math.PI / 2;
    marineTargetMarker.renderOrder = 9;
    scene.add(marineTargetMarker);
  }
  marineTargetMarker.visible = true;
  marineTargetMarker.position.set(target.x, water.position.y + .12, target.z);
}

function ensureFishingVisual() {
  if (fishingVisual) return;
  const root = new THREE.Group();
  const body = mesh(new THREE.SphereGeometry(.16, 12, 9), mat(0xf5ede0, .36), 0, .13, 0, false);
  body.scale.set(.8, 1.45, .8);
  const cap = mesh(new THREE.SphereGeometry(.105, 11, 7), mat(0xef713e, .38), 0, .31, 0, false);
  cap.scale.set(.85, .75, .85);
  const stem = mesh(new THREE.CylinderGeometry(.013, .017, .22, 8), mat(0x3b4844), 0, .43, 0, false);
  const ripple = new THREE.Mesh(new THREE.RingGeometry(.29, .39, 30),
    new THREE.MeshBasicMaterial({ color: 0xe9faf0, transparent: true, opacity: .66,
      depthWrite: false, depthTest: false, side: THREE.DoubleSide }));
  ripple.rotation.x = -Math.PI / 2;
  ripple.position.y = -.03;
  root.add(body, cap, stem, ripple);
  root.renderOrder = 5;
  scene.add(root);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(6), 3));
  fishingLine = new THREE.Line(geometry,
    new THREE.LineBasicMaterial({ color: 0xe2e5d3, transparent: true, opacity: .82, depthTest: false }));
  fishingLine.renderOrder = 5;
  scene.add(fishingLine);
  fishingVisual = { root, ripple };
  root.visible = false;
  fishingLine.visible = false;
}

function updateFishingVisual(time) {
  if (!fishingSession || !avatar) return;
  ensureFishingVisual();
  const session = fishingSession;
  const target = marineTargets.find((entry) => entry.id === session.targetId);
  if (!target) return;
  const cast = session.phase === 'casting' ? Math.min(1, session.elapsed / session.castSeconds) : 1;
  const retrieval = session.phase === 'reeling' ? session.progress * .78 : 0;
  const distance = (1 - retrieval) * cast;
  const x = avatar.position.x + (target.x - avatar.position.x) * distance;
  const z = avatar.position.z + (target.z - avatar.position.z) * distance;
  const bite = session.phase === 'bite';
  const wobble = session.phase === 'waiting' ? Math.sin(time * 4.2) * .035
    : bite ? -.14 + Math.sin(time * 19) * .045
      : session.phase === 'reeling' ? Math.sin(time * 11) * .06 : 0;
  fishingVisual.root.position.set(x, water.position.y + .06 + wobble, z);
  fishingVisual.root.rotation.z = bite ? Math.sin(time * 18) * .18 : 0;
  fishingVisual.ripple.material.color.setHex(bite ? 0xffd068 : 0xe9faf0);
  fishingVisual.ripple.material.opacity = bite ? .83 : .4 + Math.sin(time * 5) * .15;
  const pulse = bite ? 1.25 + Math.sin(time * 15) * .16 : 1 + Math.sin(time * 4) * .08;
  fishingVisual.ripple.scale.setScalar(pulse);
  const lineStart = avatar.localToWorld(new THREE.Vector3(.12, 1.42, .14));
  const positions = fishingLine.geometry.attributes.position;
  positions.setXYZ(0, lineStart.x, lineStart.y, lineStart.z);
  positions.setXYZ(1, x, fishingVisual.root.position.y + .42, z);
  positions.needsUpdate = true;
}

function startFishingSession(target, fromBoat) {
  fishingResult = null;
  if (!targetAvailable(target) || activeMarineTarget?.id !== target.id || !target.proxy.visible) {
    releaseMarineTarget(); flash('鱼已游开，请重新选择可见鱼群'); return false;
  }
  const availability = captureAvailability(state, target.speciesId, 'rod');
  if (!availability.ok) { releaseMarineTarget(); flash(captureFailureMessage(availability)); return false; }
  fishingSession = Object.assign(createFishingSession(target.speciesId, state.rodLevel),
    { targetId: target.id, fromBoat });
  hudOpen = false;
  if (marineTargetMarker) marineTargetMarker.visible = false;
  ensureFishingVisual();
  fishingVisual.root.visible = true;
  fishingLine.visible = true;
  avatar.rotation.y = Math.atan2(target.x - avatar.position.x, target.z - avatar.position.z);
  playAvatarAction('rod');
  drawHud();
  drawFishingPanel();
  return true;
}

function finishFishingSession(reason = null) {
  const session = fishingSession;
  if (!session) return;
  const target = marineTargets.find((entry) => entry.id === session.targetId);
  fishingSession = null;
  // Boat return can skip updateAvatar until disembarkation, so clear the
  // casting/reeling pose at the actual completion/cancellation boundary.
  if (avatar) {
    avatar.userData.actionUntil = 0;
    avatar.userData.actionKind = null;
    resetAvatarSeatedJoints(avatar);
    for (const [limb, rotation] of avatar.userData.seatedRig?.neutral || []) {
      if (limb === avatar.userData.limbs.leftArm || limb === avatar.userData.limbs.rightArm) limb.rotation.copy(rotation);
    }
    avatar.userData.toolRoot?.rotation.set(0, 0, 0);
    for (const [name, tool] of Object.entries(avatar.userData.tools || {})) if (name !== 'dive') tool.visible = false;
    if (avatar.userData.effects?.waterDrops) avatar.userData.effects.waterDrops.visible = false;
    if (avatar.userData.effects?.rodLine) avatar.userData.effects.rodLine.visible = false;
  }
  if (fishingVisual) fishingVisual.root.visible = false;
  if (fishingLine) fishingLine.visible = false;
  fishingSprite.visible = false;
  if (!reason && session.phase === 'landed') {
    const result = catchSeafood(state, session.speciesId, 'rod', Math.random, { landed: true });
    if (result.ok && result.caught > 0) {
      if (target) {
        state.gatherCooldowns[target.id] = state.gameMinutes + 360;
        spawnCatchEffects(target, result.caught);
      }
      fishingResult = { ...session, phase: 'result', success: true, caught: result.caught, value: result.value,
        explanation: `收获 ${SEAFOOD[session.speciesId].label} ×${result.caught} · 已装入背包`,
        penalty: { stamina: result.stamina, minutes: result.minutes }, journalNew: result.journalNew,
        tip: result.journalNew ? '首次鱼获已写入海岛手记，出售后也会保留'
          : '鱼获可用于烹饪、海风来信，或在码头出售' };
      flash(`收竿成功！${SEAFOOD[session.speciesId].label} ×${result.caught} · 估值 ${result.value} 金${result.journalNew ? ' · 海岛手记已记录' : ''}`);
    } else flash(captureFailureMessage(result));
  } else {
    const failure = reason || session.reason || 'cancelled';
    const penalty = failure === 'storm' ? { stamina: 0, minutes: 0, cooldown: 20 }
      : failure === 'cancelled' ? { stamina: 2, minutes: 8, cooldown: 30 }
        : { stamina: failure === 'hook-slip' ? 5 : 3, minutes: failure === 'hook-slip' ? 20 : 15, cooldown: 90 };
    state.stamina = Math.max(0, state.stamina - penalty.stamina);
    if (penalty.minutes) spendActionTime(state, penalty.minutes);
    if (target) state.gatherCooldowns[target.id] = state.gameMinutes + penalty.cooldown;
    const explanation = ({ 'too-early': '浮漂还没下沉，提竿过早惊走了鱼', 'missed-bite': '咬钩窗口已过，鱼脱钩游走',
      'hook-slip': '收线力度与鱼的挣扎不匹配，鱼挣脱了', 'line-slack': '收线太慢，鱼松脱了',
      storm: '风浪变大，已收竿结束垂钓', cancelled: '已收起鱼竿，稍后再试' })[failure] || '鱼儿脱钩了';
    fishingResult = { ...session, phase: 'result', explanation, penalty,
      tip: failure === 'too-early' || failure === 'missed-bite'
        ? '下次等浮漂突然下沉，再点「提竿」'
        : failure === 'hook-slip' || failure === 'line-slack'
          ? '下次跟随鱼的动作调力度，也可开启辅助收线'
          : '稍后选择另一处可见鱼群再试' };
    flash(explanation);
  }
  releaseMarineTarget();
  if (session.fromBoat) startBoatReturnTrip();
  saveState(state);
  drawHud();
  if (fishingResult) drawFishingPanel();
}

function handleFishingAction(action) {
  if (!fishingSession) return;
  if (action === 'cancel') { finishFishingSession('cancelled'); return; }
  if (action === 'hook') {
    const hooked = hookFish(fishingSession);
    if (hooked) { playAvatarAction('rod'); drawFishingPanel(); }
    else if (fishingSession.phase === 'failed') finishFishingSession();
    return;
  }
  if (action === 'assist') { setFishingAssistance(fishingSession, !fishingSession.assisted); drawFishingPanel(); return; }
  if (action === 'force-down' || action === 'force-up') {
    setFishingForce(fishingSession, fishingSession.forceValue + (action === 'force-up' ? .05 : -.05)); drawFishingPanel(); return;
  }
  if (setFishingForce(fishingSession, action)) drawFishingPanel();
}

function updateFishingSession(seconds, timeMs) {
  if (!fishingSession) return;
  if (storm.phase === 'preparing' || storm.phase === 'impact') { finishFishingSession('storm'); return; }
  const previous = fishingSession.phase;
  advanceFishingSession(fishingSession, seconds);
  if (fishingSession.phase === 'failed') { finishFishingSession(); return; }
  if (fishingSession.phase === 'landed') { finishFishingSession(); return; }
  if (previous !== fishingSession.phase || timeMs - lastFishingUiMs > 80) {
    drawFishingPanel(); lastFishingUiMs = timeMs;
  }
  updateFishingVisual(timeMs / 1000);
}

function findMarineTarget(speciesId, preferred = null) {
  const origin = preferred || avatar?.position || { x: 0, z: 0 };
  return marineTargets.filter((target) => target.speciesId === speciesId && targetAvailable(target)
    && (SEAFOOD[speciesId]?.boatTier || Math.hypot(target.x, target.z) <= shoreRadiusAt(target.x, target.z) + MAX_SURFACE_DISTANCE - .4))
    .sort((a, b) => Math.hypot(a.x - origin.x, a.z - origin.z) - Math.hypot(b.x - origin.x, b.z - origin.z))[0] || null;
}

// Marine animals share instanced draw calls. Each catchable
// instance also has a pick proxy, so inventory only changes for a visible target.
function addSchool(source, count, seed, tint, baseY, material, options = {}) {
  if (!source) return;
  const { radiusMin = 17, radiusMax = 32, spread = .8, sizeMin = 1.4, sizeMax = 2.5,
          bank = 0, bobble = .14, speedMin = .12, speedMax = .3, wander = false, speciesId = null, bird = false } = options;
  let state = seed >>> 0;
  const rand = () => { state = (state * 1664525 + 1013904223) >>> 0; return state / 4294967296; };
  const data = [];
  const fishBounds = wander ? new THREE.Box3().setFromObject(source).getSize(new THREE.Vector3()) : null;
  let groundMinY = 0;
  if (speciesId === 'crab' || bird) {
    groundMinY = Infinity;
    source.traverse((part) => {
      if (!part.isMesh) return;
      part.geometry.computeBoundingBox();
      groundMinY = Math.min(groundMinY, part.geometry.boundingBox.min.y);
    });
    if (!Number.isFinite(groundMinY)) groundMinY = 0;
  }
  for (let i = 0; i < count; i++) {
    const radius = radiusMin + rand() * (radiusMax - radiusMin);
    const speed = speedMin + rand() * (speedMax - speedMin);
    const phase = rand() * Math.PI * 2;
    data.push({
      radius,
      speed: wander ? speed : speed * (rand() < .5 ? -1 : 1),
      phase,
      roamer: null,
      crab: speciesId === 'crab' ? createSandCrab(radius, phase, seed + i * 73) : null,
      bob: rand() * Math.PI * 2,
      y: baseY + (rand() - .5) * spread,
      size: sizeMin + rand() * (sizeMax - sizeMin),
      bank,
      bobble,
      groundMinY,
    });
    if (bird) {
      const angle = .28 + birdRestCount++ * Math.PI * 2 / 18;
      const site = upperBeachPoint(angle, 1.8), member = data[i];
      member.rest = { ...site, y: groundHeightAt(site.x, site.z) - groundMinY * member.size + .025, heading: angle + Math.PI / 2 };
    }
    if (wander) {
      const member = data[i], footprint = Math.hypot(fishBounds.x, fishBounds.z) * member.size * .5 + .1;
      member.roamer = createRoamer(radius, phase, speed, seed + i * 73, fishRoamers, footprint
        + (source.geometry?.userData?.tailAmplitude || 0) * member.size);
      fishRoamers.push(member.roamer);
    }
  }
  if (speciesId) data.forEach((member, index) => {
    const target = { id: `marine-${speciesId}-${seed}-${index}`, speciesId, member, source,
      x: member.crab?.x ?? member.roamer?.x ?? Math.cos(member.phase) * member.radius,
      z: member.crab?.z ?? member.roamer?.z ?? Math.sin(member.phase) * member.radius,
      y: member.y, reserved: false };
    const proxy = actionRoot(new THREE.Mesh(marineHitGeometry, marineHitMaterial), { type: 'marine', targetId: target.id });
    proxy.position.set(target.x, target.y, target.z);
    // The longer authored tail and head remain selectable at the actual size.
    // Other marine targets keep their existing touch-friendly minimum.
    if (source.userData.authoredFish) proxy.scale.setScalar(fishPickRadius(source.geometry, member.size) / .78);
    scene.add(proxy);
    target.proxy = proxy;
    member.target = target;
    marineTargets.push(target);
  });
  source.traverse((part) => {
    if (!part.isMesh) return;
    const geometry = speciesId === 'crab' ? part.geometry.clone() : part.geometry;
    const crabGait = speciesId === 'crab' ? new THREE.InstancedBufferAttribute(new Float32Array(count * 2), 2) : null;
    if (crabGait) {
      crabGait.setUsage(THREE.DynamicDrawUsage);
      geometry.setAttribute('crabGait', crabGait);
      data.forEach((member, i) => crabGait.setXY(i, member.crab.gait, 0));
    }
    const inst = new THREE.InstancedMesh(geometry, material || part.material, count);
    if (source.userData.fishModelId) {
      inst.name = `MarineFish_${source.userData.fishModelId}_${seed}`;
      inst.userData.fishModelId = source.userData.fishModelId;
    }
    inst.castShadow = false;
    inst.receiveShadow = false;
    if (bird) inst.frustumCulled = false;
    if (material?.userData?.surfaceSchool) inst.renderOrder = 3;
    if (tint) {
      for (let i = 0; i < count; i++) inst.setColorAt(i, tint[i % tint.length]);
      inst.instanceColor.needsUpdate = true;
    }
    scene.add(inst);
    animated.push({
      school: { inst, data, bird, crabGait, normal: new THREE.Vector3(), slopeQuat: new THREE.Quaternion(),
                matrix: new THREE.Matrix4(), pos: new THREE.Vector3(),
                quat: new THREE.Quaternion(), bankQuat: new THREE.Quaternion(),
                scale: new THREE.Vector3() },
    });
  });
}

async function loadSceneAssets() {
  const [islandAsset, vegetationAsset, buildingAsset, avatarAsset, atlas, meadowTexture, causticsTexture] = await Promise.all([
    parseGlb(islandGlb), parseGlb(vegetationGlb), parseGlb(buildingsGlb), parseGlb(avatarGlb), loadAtlas(), loadSurfaceTexture('meadow-albedo'), loadSurfaceTexture('water-caustics', false), loadUiArt(),
  ]);
  water.material.uniforms.uCaustics.value = causticsTexture;
  const atlasMaterial = createIslandAtlasMaterial(atlas);
  const atlasLeafMaterial = createFoliageMaterial(windShaderUniforms, atlas);
  const authoredPlantMaterial = createFoliageMaterial(windShaderUniforms);
  applyAtlas(islandAsset.scene, atlasMaterial);
  const beach = islandAsset.scene.getObjectByName('Island_Sand');
  if (beach) beach.material = createBeachMaterial(water.material.uniforms);
  const meadow = islandAsset.scene.getObjectByName('Island_Grass');
  if (meadow) meadow.material = createMeadowMaterial(meadowTexture);
  const shelf = islandAsset.scene.getObjectByName('Island_Underwater');
  if (shelf) shelf.material = createShallowFloorMaterial(water.material.uniforms);
  applyFoliageAtlas(vegetationAsset.scene, atlasMaterial, atlasLeafMaterial, authoredPlantMaterial);
  applyAtlas(buildingAsset.scene, atlasMaterial);
  avatarTemplate = avatarAsset.scene;
  avatarTemplate.traverse((object) => {
    if (!object.isMesh) return;
    object.castShadow = true;
    object.receiveShadow = true;
    if (object.name === 'Avatar_ContactMarker') object.visible = false;
  });
  const boatMesh = islandAsset.scene.getObjectByName('Boat');
  if (boatMesh) {
    // The source GLB bakes its island-space location into vertex positions.
    // Centre it once so rocking and upgrades share a true hull pivot.
    boatMesh.geometry.translate(-BOAT_MESH_ORIGIN.x, -BOAT_MESH_ORIGIN.y, -BOAT_MESH_ORIGIN.z);
    heroBoat = new THREE.Group();
    heroBoat.name = 'BoatPivot';
    heroBoat.position.set(BOAT_MESH_ORIGIN.x, BOAT_MESH_ORIGIN.y, BOAT_MESH_ORIGIN.z);
    heroBoat.add(boatMesh);
    heroBoat.userData.action = { type: 'voyage-board' };
    interactive.push(heroBoat);
    islandAsset.scene.add(heroBoat);
    registerHomeCollision(heroBoat, 'home-boat', { groundHeightAt: (x,z) => Math.max(water.position.y-.55,groundHeightAt(x,z)) });
  }
  // The playable hut is built from Hut_Source; the baked showcase copy must not overlap it.
  const showcaseHut = islandAsset.scene.getObjectByName('HeroHut');
  if (showcaseHut) showcaseHut.visible = false;
  islandAsset.scene.updateMatrixWorld(true);
  const terrainMeshes = ['Island_Grass', 'Island_Sand', 'Island_Underwater']
    .map((name) => islandAsset.scene.getObjectByName(name)).filter(Boolean).concat(seabed);
  walkGroundMeshes = terrainMeshes;
  scene.add(islandAsset.scene);
  islandAsset.scene.traverse((object) => {
    if (object.name.startsWith('HutRoof')) hutRoofs.push(object);
  });
  createBoatUpgradeVisuals();

  for (const kind of ['garden', 'hut', 'dock']) {
    buildingSources[kind] = buildingAsset.scene.getObjectByName(`${kind[0].toUpperCase()}${kind.slice(1)}_Source`);
  }

  const palmSource = vegetationAsset.scene.getObjectByName('Palm_Source');
  voyagePalmSource = palmSource;
  createTreeVisuals(palmSource, terrainMeshes);
  addMeadowAccents(scene, { heightAt: groundHeightAt, trees: state.trees, windUniforms: windShaderUniforms, clearOfFarm });
  const bushSource = vegetationAsset.scene.getObjectByName('Bush_Source');
  const clearBushes = sparseBushPlacements(
    scatter(64, 6.4, 11.2, 7, 2, 1.8, .55, .95),
    { trees: state.trees, farmBeds: FARM_BED_CENTRES,
      hut: HUT_ORIGIN, dock: DOCK_APPROACH, trade: TRADE_SIGN_POSITION,
      maxCount: 14 },
  );
  const understory = state.trees.filter(t => t.stage === 'mature' || t.stage === 'growing').flatMap((t,i) =>
    [0,1,2].map(j => {const angle=i*1.7+j*2.1;return [t.x+Math.cos(angle)*.7,0,t.z+Math.sin(angle)*.7,.52+j*.06];}))
    .filter(([x,,z])=>clearOfFarm(x,z,.6) && !(Math.abs(x-HUT_ORIGIN.x)<2.9 && Math.abs(z-HUT_ORIGIN.z)<3.7));
  const cottageBushes=[[HUT_ORIGIN.x+2.9,0,HUT_ORIGIN.z+2.4,.85],[HUT_ORIGIN.x-2.5,0,HUT_ORIGIN.z+2.4,.85],[HUT_ORIGIN.x+3.0,0,HUT_ORIGIN.z-1.1,.8]];
  const bushPlacements = groundPlacements(bushSource, clearBushes.concat(understory,cottageBushes), terrainMeshes);
  addInstances(bushSource, bushPlacements, undefined, bushSource.userData.authoredPlant
    ? undefined : [0xc9eab1, 0xe3f3c2, 0xa9d397].map((color) => new THREE.Color(color)));
  // The approved mesh already includes its attached petals and stamens.
  if (!bushSource.userData.authoredPlant) addHibiscusBlossoms(bushPlacements);
  const rockSource = vegetationAsset.scene.getObjectByName('Rock_Source');
  const fishSources = Object.fromEntries(['clownfish', 'butterflyfish', 'tang', 'parrotfish', 'silverJack']
    .map(id => [id, createFishSource(id, { animatedUniforms: animatedShaderUniforms })]));
  voyageArtAssets = {
    rock: rockSource, bush: bushSource,
    coral: vegetationAsset.scene.getObjectByName('Reef_Staghorn_Source'),
    seaGrass: vegetationAsset.scene.getObjectByName('Sea_Grass_Source'),
    fishSources,
  };
  const rocks = [
    [-7.4, 1.3, 5.2, 1.8], [7.8, 1.1, 5.1, 1.7], [-8.5, 1, -4.2, 1.7], [8.7, 1, -3.5, 1.45],
  ];
  const stoneMaterial = new THREE.MeshStandardMaterial({color:0x9b9c86,roughness:1});
  const shoreStones = offshorePlacements(scatter(26, 15.0, 20.5, 213, -1.7, 0, 1.2, 2.5), .3);
  addInstances(rockSource, groundPlacements(rockSource, rocks.concat(shoreStones), terrainMeshes),stoneMaterial);
  addInstances(rockSource, scatter(24, 20.5, 34, 11, -1.45, 0, .7, 1.3),stoneMaterial);
  const reefMaterial = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .85, metalness: 0 });
  const coralTint = [0x86e0ff, 0xb79cff, 0xffc46e, 0xff8fb4, 0x84ecc8, 0xff9a86].map((c) => new THREE.Color(c));
  const plantTint = [0x6fd48f, 0x93dd6c, 0x46c295, 0x7fca7c].map((c) => new THREE.Color(c));
  const bloomTint = [0xff9fc0, 0xffd27f, 0xff8f8f, 0xc9a6ff].map((c) => new THREE.Color(c));
  const reefSource = (name) => vegetationAsset.scene.getObjectByName(name + '_Source');
  const reefKinds = [
    ['Reef_Staghorn', 30, 18, 34, 31, .8, 1.5, coralTint],
    ['Reef_Fan', 20, 19, 34, 37, .8, 1.4, coralTint],
    ['Reef_Brain', 26, 18, 33, 41, .7, 1.2, coralTint],
    ['Reef_Table', 18, 19, 35, 43, .8, 1.3, coralTint],
    ['Reef_Tube', 30, 18, 34, 47, .8, 1.4, coralTint],
    ['Reef_Mushroom', 18, 18, 32, 53, .7, 1.2, coralTint],
    ['Reef_Anemone', 20, 18, 33, 59, .7, 1.1, coralTint],
    ['Sea_Grass', 40, 17, 34, 61, .8, 1.6, plantTint],
    ['Kelp', 22, 18, 33, 67, .8, 1.3, plantTint],
  ];
  const reefFloor = [islandAsset.scene.getObjectByName('Island_Underwater'), seabed].filter(Boolean);
  for (const [name, count, r0, r1, seed, s0, s1, tint] of reefKinds) {
    addInstances(reefSource(name), offshorePlacements(scatter(Math.ceil(count * .38), r0, r1, seed, -1.7, 0, s0, s1), .5), reefMaterial, tint);
  }
  const coralSource = reefSource('Coral');
  const coralSites = offshorePlacements(scatter(24, 16.5, 23.5, 19, -1.7, 0, .36, .68), 2.1);
  addInstances(coralSource, groundPlacements(coralSource, coralSites, reefFloor), reefMaterial, [...coralTint, ...bloomTint]);

  const animalMaterial = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .5, metalness: 0 });
  addSchool(fishSources.clownfish, 14, 71, null, -.75, fishSources.clownfish.material,
            { radiusMin: 16.5, radiusMax: 18.5, spread: .9, sizeMin: .76, sizeMax: 1.22, speedMin: .24, speedMax: .45, wander: true, speciesId: 'reefFish' });
  addSchool(fishSources.butterflyfish, 10, 83, null, -1.1, fishSources.butterflyfish.material,
            { radiusMin: 16.5, radiusMax: 18.5, spread: .8, sizeMin: .76, sizeMax: 1.13, speedMin: .22, speedMax: .41, wander: true, speciesId: 'reefFish' });
  const crabSource = new THREE.Mesh(createSandCrabGeometry(), createSandCrabMaterial());
  addSchool(crabSource, 7, 103, [new THREE.Color(0xffceac), new THREE.Color(0xf8b786)], .75, crabSource.material,
            { radiusMin: 12.6, radiusMax: 14.2, spread: .06, sizeMin: .42, sizeMax: .54, bobble: .012, speedMin: .025, speedMax: .06, speciesId: 'crab' });
  addSchool(fishSources.silverJack, 8, 107, null, -.82, fishSources.silverJack.material,
            { radiusMin: 22, radiusMax: 31, spread: .65, sizeMin: .9, sizeMax: 1.34, bank: .08, speedMin: .3, speedMax: .54, wander: true, speciesId: 'silverJack' });
  addSchool(reefSource('Sea_Lobster'), 5, 109, [new THREE.Color(0xc75b43), new THREE.Color(0xdf7653)], -1.42, animalMaterial,
            { radiusMin: 18, radiusMax: 24, spread: .12, sizeMin: .6, sizeMax: .85, bobble: .02, speedMin: .02, speedMax: .05, speciesId: 'lobster' });
  const oysterSource = reefSource('Sea_PearlOyster');
  addSchool(oysterSource, 4, 113, [new THREE.Color(0xe4d6c4), new THREE.Color(0xc7b4d1)], -1.56, reefMaterial,
    { radiusMin: 25, radiusMax: 29, spread: 0, sizeMin: .55, sizeMax: .78, bobble: 0, speedMin: 0, speedMax: 0, speciesId: 'pearlOyster' });

  const birdTint = [0xf2f6fa, 0xdfe8f0, 0xcdd8e2].map((c) => new THREE.Color(c));
  const birdMaterial = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .7, metalness: 0 });
  birdMaterial.onBeforeCompile = (shader) => {
    shader.uniforms.uCreatureTime = birdTimeUniform;
    shader.uniforms.uBirdFlight = birdFlightUniform;
    shader.vertexShader = shader.vertexShader.replace('#include <common>', '#include <common>\nuniform float uCreatureTime;\nuniform float uBirdFlight;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        float wingWeight=smoothstep(.08,.34,abs(position.z));
        transformed.z*=mix(1.0-.84*wingWeight,1.0,uBirdFlight);
        transformed.x-=(1.0-uBirdFlight)*wingWeight*.10;
        transformed.y+=sin(uCreatureTime*8.0+abs(position.z)*.7)*.28*wingWeight*uBirdFlight;`);
  };
  birdMaterial.customProgramCacheKey = () => 'island-bird-rest-v2';
  addSchool(reefSource('Bird_A'), 10, 91, birdTint, 7.5, birdMaterial,
            { radiusMin: 12, radiusMax: 30, spread: 5, sizeMin: .4, sizeMax: .7, bank: .6, bobble: .3, bird: true });
  addSchool(reefSource('Bird_B'), 8, 97, birdTint, 9.5, birdMaterial,
            { radiusMin: 10, radiusMax: 26, spread: 4, sizeMin: .35, sizeMax: .6, bank: .6, bobble: .3, bird: true });
}

function createBoatUpgradeVisuals() {
  if (!heroBoat || visualProps.boatUpgrades) return;
  const root = new THREE.Group();
  const iron = new THREE.Group();
  const speed = new THREE.Group();
  const metal = mat(0x6a8588, .38);
  const trim = mat(0xc89445, .55);
  for (const side of [-1, 1]) {
    iron.add(mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3([
      new THREE.Vector3(-1.3, .35, side * .28), new THREE.Vector3(0, .52, side * .38), new THREE.Vector3(1.28, .34, side * .28),
    ]), 10, .026, 6, false), metal, 0, 0, 0, false));
  }
  const bowBand = mesh(new THREE.TorusGeometry(.2, .024, 5, 12), trim, 1.3, .33, 0, false);
  bowBand.rotation.y = Math.PI / 2; iron.add(bowBand);
  const motor = mesh(new THREE.CapsuleGeometry(.16, .28, 4, 9), metal, -1.35, .36, 0, false);
  motor.rotation.z = Math.PI / 2;
  const propeller = new THREE.Group();
  propeller.position.set(-1.55, .2, 0);
  for (const angle of [0, Math.PI * .67, Math.PI * 1.33]) {
    const blade = mesh(new THREE.SphereGeometry(.09, 8, 5), trim, Math.cos(angle) * .1, Math.sin(angle) * .1, 0, false);
    blade.scale.set(1.6, .38, .2); propeller.add(blade);
  }
  speed.add(motor, propeller);
  root.add(iron, speed);
  visualProps.boatUpgrades = { root, iron, speed, propeller };
  scene.add(root);
  refreshBoatVisuals();
}

function refreshBoatVisuals() {
  const props = visualProps.boatUpgrades;
  if (!props || !heroBoat) return;
  props.root.position.copy(heroBoat.position);
  props.root.rotation.copy(heroBoat.rotation);
  props.iron.visible = state.boatTier === 'iron' || state.boatTier === 'speed';
  props.speed.visible = state.boatTier === 'speed';
}

function actionRoot(object, data) {
  object.userData.action = data;
  interactive.push(object);
  return object;
}

function addResource(kind, x, z, nodeId) {
  const group = actionRoot(new THREE.Group(), { type: 'gather', kind, nodeId, active: true });
  group.position.set(x, groundHeightAt(x, z) + .025, z);
  if (kind === 'wood') {
    for (const [i, xOffset, angle] of [[0, -.12, -.15], [1, .12, .12], [2, .01, .42]]) {
      const log = mesh(i === 2 ? driftwoodBranch : driftwoodLong,
        materials.driftwood, xOffset, .16 + i * .06, (i - 1) * .12);
      log.rotation.set((i - 1) * .12, angle, Math.PI / 2 + (i - 1) * .08);
      group.add(log);
    }
  } else {
    for (let i = 0; i < 4; i++) {
      const x = (i - 1.5) * .27, z = Math.sin(i) * .18;
      const shell = mesh(shellGeometries[i % 2], i % 2 ? materials.pinkShell : materials.shell, x, .12, z);
      shell.rotation.y = i * .72;
      group.add(shell);
      const lip = mesh(new THREE.TorusGeometry((i % 2 ? .27 : .22) * .82, .012, 5, 14), mat(i % 2 ? 0xc77d93 : 0xd3b892), x, .135, z, false);
      lip.rotation.x = Math.PI / 2;
      lip.rotation.z = i * .72;
      group.add(lip);
    }
  }
  scene.add(group);
  registerHomeCollision(group, `gather-${nodeId}`);
}

function createResources() {
  const westernWrack = upperBeachPoint(2.1);
  const southernWrack = upperBeachPoint(-2.1);
  addResource('wood', westernWrack.x, westernWrack.z, 'wood-wrack-west');
  addResource('wood', southernWrack.x, southernWrack.z, 'wood-wrack-south');
  addResource('shells', -7.6, 5.5, 'shells-76-55');
  addResource('shells', 7.6, -4.8, 'shells-76-48');
}

function createSiteProps() {
  if (visualProps.root) return;
  const root = new THREE.Group();
  const wood = mat(0x765037);
  const rope = mat(0xc9ad78, .96);
  const metal = mat(0x64777a, .42);
  const radio = new THREE.Group();
  radio.position.set(9.15, 1.5, 6.25);
  const radioBody = mesh(new THREE.CylinderGeometry(.24, .29, .18, 10), mat(0x3c6e69, .72), 0, .37, 0, false);
  radioBody.rotation.x = Math.PI / 2;
  const dial = mesh(new THREE.TorusGeometry(.075, .018, 5, 12), rope, 0, .37, .105, false);
  dial.rotation.x = Math.PI / 2;
  const antenna = curvedRod(.52, .012, metal);
  antenna.position.set(.08, .45, 0); antenna.rotation.z = -.15;
  for (const x of [-.17, .17]) radio.add(mesh(new THREE.CylinderGeometry(.025, .04, .52, 7), wood, x, .15, 0, false));
  radio.add(radioBody, dial, antenna);
  const crate = new THREE.Group();
  crate.position.set(8.75, 1.25, 6.45);
  for (const y of [.12, .28, .44]) crate.add(mesh(new RoundedBoxGeometry(.46, .08, .34, 3, .025), wood, 0, y, 0, false));
  for (const x of [-.22, .22]) crate.add(mesh(new THREE.CylinderGeometry(.035, .04, .52, 7), wood, x, .27, -.15, false));
  root.add(radio, crate);

  const anchor = new THREE.Group();
  anchor.position.set(9.4, 2.12, 7.3);
  const shank = mesh(new THREE.CylinderGeometry(.035, .05, .54, 8), metal, 0, .25, 0, false);
  const ring = mesh(new THREE.TorusGeometry(.09, .018, 6, 12), metal, 0, .55, 0, false);
  ring.rotation.x = Math.PI / 2;
  const stock = mesh(new THREE.CylinderGeometry(.025, .025, .42, 7), wood, 0, .4, 0, false);
  stock.rotation.z = Math.PI / 2;
  for (const direction of [-1, 1]) {
    const fluke = mesh(new THREE.ConeGeometry(.1, .22, 4), metal, direction * .1, .04, 0, false);
    fluke.rotation.z = direction * 1.02;
    anchor.add(fluke);
  }
  const coil = mesh(new THREE.TorusGeometry(.19, .018, 6, 18), rope, .26, .09, .02, false);
  coil.rotation.x = Math.PI / 2;
  anchor.add(shank, ring, stock, coil); root.add(anchor);

  const barrierSite = actionRoot(new THREE.Group(), { type: 'wave-barrier-site' });
  const barrier = new THREE.Group();
  const pileWood = mat(0x82613f);
  const railWood = mat(0xac8353);
  const seaStone = mat(0x7d918b);
  const points = WAVE_BARRIER_POSTS.map((point) => new THREE.Vector3(point.x, 0, point.z));
  const radial = new THREE.Vector3(WAVE_BARRIER_SITE.x, 0, WAVE_BARRIER_SITE.z).normalize();
  const beam = (from, to, radius, material) => {
    const direction = new THREE.Vector3().subVectors(to, from);
    const part = mesh(new THREE.CylinderGeometry(radius * .9, radius, direction.length(), 9), material,
      (from.x + to.x) / 2, (from.y + to.y) / 2, (from.z + to.z) / 2, false);
    part.quaternion.setFromUnitVectors(Y_AXIS, direction.normalize());
    barrier.add(part);
  };
  for (let row = 0; row < 2; row++) {
    const offset = row ? .34 : -.34;
    const line = points.map((point) => point.clone().addScaledVector(radial, offset));
    line.forEach((point, index) => {
      const seabed = groundHeightAt(point.x, point.z);
      const top = .64 + (index % 2) * .09;
      beam(new THREE.Vector3(point.x, seabed - .08, point.z),
        new THREE.Vector3(point.x + radial.x * .08, top, point.z + radial.z * .08), .085, pileWood);
      const footing = mesh(new THREE.DodecahedronGeometry(.24, 1), seaStone,
        point.x, seabed + .08, point.z, false);
      footing.scale.set(1.15, .58, .85);
      barrier.add(footing);
      if (index % 2 === 0) {
        const braceFoot = point.clone().addScaledVector(radial, row ? .25 : -.25);
        beam(new THREE.Vector3(braceFoot.x, groundHeightAt(braceFoot.x, braceFoot.z), braceFoot.z),
          new THREE.Vector3(point.x, .38, point.z), .045, pileWood);
      }
    });
    for (let index = 0; index < line.length - 1; index++) {
      for (const height of [.22, .47]) {
        beam(new THREE.Vector3(line[index].x, height, line[index].z),
          new THREE.Vector3(line[index + 1].x, height, line[index + 1].z), .047, railWood);
      }
    }
  }
  barrierSite.add(barrier);

  const marker = new THREE.Group();
  marker.position.set(WAVE_BARRIER_SITE.x, .12, WAVE_BARRIER_SITE.z);
  marker.add(mesh(new THREE.CylinderGeometry(.16, .21, .28, 12), mat(0xe2aa4a, .5), 0, .12, 0, false));
  marker.add(mesh(new THREE.TorusGeometry(.25, .025, 7, 20), mat(0xffdf91, .46), 0, .13, 0, false));
  marker.add(mesh(new THREE.CylinderGeometry(.025, .03, .53, 8), pileWood, 0, .49, 0, false));
  const markerCanvas = offscreen(512, 96);
  const markerContext = markerCanvas.getContext('2d');
  markerContext.fillStyle = 'rgba(5, 48, 53, .94)';
  roundRect(markerContext, 4, 4, 504, 88, 14);
  markerContext.fillStyle = '#ffe5a3';
  markerContext.textAlign = 'center';
  markerContext.font = '700 43px sans-serif';
  markerContext.fillText('护栏安装点', 256, 65);
  const markerTexture = new THREE.CanvasTexture(markerCanvas);
  markerTexture.colorSpace = THREE.SRGBColorSpace;
  const markerLabel = new THREE.Sprite(new THREE.SpriteMaterial({ map: markerTexture, transparent: true, depthTest: true }));
  markerLabel.position.set(0, 1.28, 0);
  markerLabel.scale.set(4.8, 1.02, 1);
  marker.add(markerLabel);
  worldLabels.push(markerLabel);
  marker.add(mesh(new THREE.SphereGeometry(.7, 12, 8),
    new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }), 0, .62, 0, false));
  barrierSite.add(marker);
  root.add(barrierSite);

  const cabinet = new THREE.Group();
  cabinet.position.set(HUT_ORIGIN.x+2.5, 2.2, HUT_ORIGIN.z+2.2);
  for (const x of [-.21, .21]) for (const z of [-.13, .13]) cabinet.add(mesh(new THREE.CylinderGeometry(.026, .034, .42, 7), wood, x, .2, z, false));
  cabinet.add(mesh(new THREE.CylinderGeometry(.28, .3, .48, 8), mat(0x496c65, .78), 0, .48, 0, false));
  const cabinetBand = mesh(new THREE.TorusGeometry(.255, .015, 5, 12), rope, 0, .48, .01, false);
  cabinetBand.rotation.x = Math.PI / 2; cabinet.add(cabinetBand); root.add(cabinet);

  const shutters = new THREE.Group();
  shutters.position.set(HUT_ORIGIN.x+1.2, 3.52, HUT_ORIGIN.z+1.6);
  for (const x of [-.18, .18]) {
    const shutter = mesh(new RoundedBoxGeometry(.14, .36, .028, 3, .012), wood, x, 0, 0, false);
    shutter.rotation.y = x * 1.5; shutters.add(shutter);
  }
  root.add(shutters);
  visualProps.root = root;
  visualProps.anchor = anchor;
  visualProps.barrier = barrier;
  visualProps.barrierMarker = marker;
  visualProps.cabinet = cabinet;
  visualProps.shutters = shutters;
  scene.add(root);
  registerHomeCollision(root, 'home-equipment', { filter: object => {
    for (let parent = object; parent; parent = parent.parent) if (parent === marker) return false;
    return true;
  } });
  refreshSiteProps();
}

function refreshSiteProps() {
  if (!visualProps.root) return;
  const defenses = state.defenses || {};
  visualProps.anchor.visible = defenses.anchor === true || Number(defenses.anchor) > 0;
  visualProps.barrier.visible = defenses.waveBarrier === true || Number(defenses.waveBarrier) > 0;
  visualProps.barrierMarker.visible = !visualProps.barrier.visible;
  visualProps.cabinet.visible = defenses.waterproofCabinet === true || Number(defenses.waterproofCabinet) > 0;
  visualProps.shutters.visible = defenses.shutters === true || Number(defenses.shutters) > 0;
}

const plots = {
  garden: { pos: [FARM_ORIGIN.x, FARM_ORIGIN.y, FARM_ORIGIN.z] },
  hut: { pos: [HUT_ORIGIN.x, HUT_ORIGIN.y, HUT_ORIGIN.z] },
  dock: { pos: [10.2, .95, 7.0] },
};

function activateHutDoor() {
  if (!state.built.hut || hutCrossing || busyAction) return;
  const intent = hutDoorIntent(state.shelter, Math.hypot(avatar.position.x - HUT_DOOR_OUTSIDE.x, avatar.position.z - HUT_DOOR_OUTSIDE.z));
  if (intent === 'open') {
    state.shelter.doorOpen = true;
    hudPage = 'activity'; hudOpen = true;
    flash('小屋门已打开 · 点屋外或“走出小屋”出门；再次点门关闭');
  } else if (intent === 'close') { closeHutDoor(); return; }
  else if (intent === 'approach') {
    walkTo(HUT_DOOR_OUTSIDE.x, HUT_DOOR_OUTSIDE.z, { type: 'hut-door-open' }, .45);
  } else startHutCrossing(true);
  saveState(state);
  drawHud();
}

function closeHutDoor() {
  if (!state.shelter.doorOpen || hutCrossing || busyAction) return;
  if (!state.shelter.inside && Math.hypot(avatar.position.x - HUT_DOOR_OUTSIDE.x, avatar.position.z - HUT_DOOR_OUTSIDE.z) > 1.4) {
    walkTo(HUT_DOOR_OUTSIDE.x, HUT_DOOR_OUTSIDE.z, { type: 'hut-door-close' }, .45);
    return;
  }
  state.shelter.doorOpen = false;
  if (state.shelter.inside) { hudPage = 'activity'; hudOpen = true; }
  flash(state.shelter.inside ? '小屋门已关闭 · 留在屋内安全避险' : '小屋门已关闭');
  saveState(state);
  drawHud();
}

function startHutCrossing(entering, destination = null) {
  if (hutCrossing || busyAction || avatarVault || !avatar) return;
  const crossing = createHutCrossing(state.shelter, avatar.position, entering, destination);
  if (!crossing) return;
  avatarTarget = null;
  avatarRoute = [];
  arrivalAction = null;
  hutCrossing = crossing;
  hudOpen = false;
}

function updateHutDoor(deltaSeconds) {
  if (hutDoorLabel) {
    const text = state.shelter.inside ? (state.shelter.doorOpen ? '小屋 · 关门' : '小屋 · 开门')
      : state.shelter.doorOpen ? '进入小屋' : '打开小屋';
    paintActionLabel(hutDoorLabel, text);
  }
  if (hutDoorPivot) {
    const target = state.shelter.doorOpen ? 1.35 : 0;
    hutDoorPivot.rotation.y += (target - hutDoorPivot.rotation.y) * Math.min(1, deltaSeconds * 8);
  }
  if (!hutCrossing || !avatar) return;
  const step = stepHutCrossing(state.shelter, hutCrossing, deltaSeconds);
  avatar.position.x = step.x;
  avatar.position.z = step.z;
  avatar.rotation.y = Math.atan2(hutCrossing.endX - hutCrossing.startX, hutCrossing.endZ - hutCrossing.startZ);
  if (!step.done) return;
  const destination = hutCrossing.destination;
  hutCrossing = null;
  // Crossing used to hide the only close button. Present indoor controls
  // immediately on arrival, independently of the previously selected tab.
  if (state.shelter.inside) { hudPage = 'activity'; hudOpen = true; }
  else if (hudPage === 'activity') hudOpen = false;
  updateCamera();
  flash(state.shelter.inside ? '已进入小屋 · 请关门避险' : '已走出小屋 · 请关门');
  saveState(state);
  drawHud();
  if (destination && !state.shelter.inside && storm.phase !== 'impact') walkTo(destination.x, destination.z);
}

function createHutDoorSign() {
  const root = actionRoot(new THREE.Group(), { type: 'hut-door' });
  root.position.set(HUT_ORIGIN.x-.1, 2.03, HUT_ORIGIN.z-1.88);
  const sign = mesh(new RoundedBoxGeometry(.9, .33, .07, 2, .04), mat(0x214d4a), 0, 2.05, -.15, false);
  root.add(sign);
  hutDoorLabel = createActionLabel('小屋 · 开门', 2.5);
  hutDoorLabel.position.set(0, 2.48, -.35);
  hutDoorLabel.userData.hutDoorLabel = true;
  root.add(hutDoorLabel);
  root.add(mesh(new THREE.BoxGeometry(1.5, 2.4, .65), new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }), 0, 1.2, -.08, false));
  scene.add(root);
}

function createActionLabel(text, width) {
  const canvas = offscreen(320, 100);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  // Labels are click targets. Keep their text legible above roofs and walls.
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true,
    depthTest: false, depthWrite: false }));
  sprite.renderOrder = 100;
  sprite.scale.set(width, width * canvas.height / canvas.width, 1);
  sprite.userData.actionLabel = { canvas, texture, text: '' };
  paintActionLabel(sprite, text);
  worldLabels.push(sprite);
  return sprite;
}

function paintActionLabel(sprite, text) {
  const label = sprite.userData.actionLabel;
  if (label.text === text) return;
  label.text = text;
  const ctx = label.canvas.getContext('2d');
  ctx.clearRect(0, 0, 320, 100);
  ctx.fillStyle = UI_COLORS.panel;
  pill(ctx, 3, 3, 314, 94, 46);
  ctx.fill();
  ctx.strokeStyle = UI_COLORS.gold; ctx.lineWidth = 2; ctx.stroke();
  ctx.fillStyle = '#fff4d1'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.font = '700 43px sans-serif';
  if (sprite.userData.hutDoorLabel) {drawNavIcon(ctx,'house',48,51,42);ctx.fillText(text,184,52,228);}
  else ctx.fillText(text, 160, 52, 290);
  label.texture.needsUpdate = true;
}

function createCookingStation() {
  const root = actionRoot(new THREE.Group(), { type: 'kitchen' });
  root.position.set(KITCHEN_SITE.x, groundHeightAt(KITCHEN_SITE.x, KITCHEN_SITE.z), KITCHEN_SITE.z);
  root.add(mesh(new RoundedBoxGeometry(1.1, .65, .85, 2, .07), mat(0x9c8870), 0, .32, 0));
  root.add(mesh(new THREE.CylinderGeometry(.34, .29, .26, 16), mat(0x374e4a, .5), 0, .77, 0));
  const broth = mesh(new THREE.CircleGeometry(.30, 20), mat(0xdca951), 0, .91, 0, false);
  broth.rotation.x = -Math.PI / 2; root.add(broth);
  for (const x of [-.4, .4]) root.add(mesh(new THREE.BoxGeometry(.2, .06, .09), materials.wood, x, .83, 0, false));
  const steam = [];
  for (let i = 0; i < 5; i++) {
    const puff = mesh(new THREE.SphereGeometry(.065, 8, 6), new THREE.MeshBasicMaterial({ color: 0xfff3d6, transparent: true, opacity: .45, depthWrite: false }), 0, 1, 0, false);
    root.add(puff); steam.push(puff);
  }
  const image = offscreen(256, 96), ctx = image.getContext('2d');
  ctx.fillStyle = '#204e47'; roundRect(ctx, 4, 4, 248, 88, 12);
  ctx.fillStyle = '#ffe3a6'; ctx.font = '700 44px sans-serif'; ctx.textAlign = 'center'; ctx.fillText('海岛厨房', 128, 64);
  const texture = new THREE.CanvasTexture(image); texture.colorSpace = THREE.SRGBColorSpace;
  const sign = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true }));
  sign.position.set(0, 1.65, 0); sign.scale.set(1.65, .62, 1); root.add(sign); worldLabels.push(sign);
  root.add(mesh(new THREE.BoxGeometry(1.25, 1.2, 1), new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }), 0, .6, 0, false));
  scene.add(root); visualProps.kitchen = { root, steam };
  registerHomeCollision(root, 'home-kitchen', { filter: object => !steam.includes(object) });
}

function cookingFailure(reason) {
  return ({ locked: '先学会这道菜谱', ingredients: '食材不足，请收获或捕获后再来；仓库食材需先取回',
    capacity: '制作后背包仍会超量，请先整理背包', gold: '金币不足', practice: '熟练度不足，先制作更多份料理',
    'max-level': '菜谱已满级', known: '已经学会这道菜谱' })[reason] || '暂时不能制作这道料理';
}

function beginCooking(recipeId) {
  if (busyAction || hutCrossing || fishingSession || boatTrip || voyage?.engaged) { flash('请先完成当前动作并返回家园'); return; }
  if (storm.phase === 'impact' && !state.shelter.inside) { flash('台风登陆中，请先进入小屋避险'); return; }
  if (!state.built.hut || state.hutDurability <= 0) { flash('小屋已损坏，请先维修厨房'); return; }
  if (!state.shelter.inside && (marineMode !== 'land' || Math.hypot(avatar.position.x - KITCHEN_APPROACH.x, avatar.position.z - KITCHEN_APPROACH.z) > 1.5)) {
    flash('先走到小屋旁的海岛厨房'); return;
  }
  const quote = recipeQuote(state, recipeId);
  if (!quote.ok) { flash(cookingFailure(quote.reason)); return; }
  avatarTarget = null; avatarRoute = []; arrivalAction = null;
  selectedRecipeId = recipeId;
  kitchenPage = Math.floor(Object.keys(RECIPES).indexOf(recipeId) / 4);
  cookingJob = { recipeId, elapsed: 0, duration: 5 };
  busyAction = true; hudPage = 'kitchen'; hudOpen = true;
  playAvatarAction('hand');
  if (!state.shelter.inside) avatar.rotation.y = Math.atan2(KITCHEN_SITE.x - avatar.position.x, KITCHEN_SITE.z - avatar.position.z);
  flash(`正在制作${RECIPES[recipeId].name}，完成后装入背包`);
}

function updateCooking(deltaSeconds) {
  if (visualProps.kitchen) visualProps.kitchen.steam.forEach((puff, i) => {
    puff.visible = Boolean(cookingJob) && !state.shelter.inside;
    const phase = ((cookingJob?.elapsed || 0) * .5 + i / 5) % 1;
    puff.position.set(Math.sin(i * 2 + phase) * .15, 1 + phase * .65, Math.cos(i * 2) * .12);
    puff.scale.setScalar(.6 + phase * 1.6); puff.material.opacity = (1 - phase) * .45;
  });
  if (!cookingJob) return;
  if (state.hutDurability <= 0 || (storm.phase === 'impact' && !state.shelter.inside)) {
    cookingJob = null; busyAction = false; flash('风灾中断烹饪，食材没有扣除；请先进入小屋避险'); return;
  }
  cookingJob.elapsed = Math.min(cookingJob.duration, cookingJob.elapsed + deltaSeconds);
  if (cookingJob.elapsed < cookingJob.duration) return;
  const result = cookMeal(state, cookingJob.recipeId);
  cookingJob = null; busyAction = false;
  flash(result.ok ? `${result.dish.name} Lv.${result.dish.level} 已入背包 · 食用恢复 ${result.dish.satiety} 体力` : cookingFailure(result.reason));
  saveState(state);
}

function handleKitchenAction(id) {
  if (cookingJob || busyAction || hutCrossing || fishingSession) { flash('请先完成当前动作'); return; }
  if (id.startsWith('kitchen-select:')) { selectedRecipeId = id.slice(15); drawHud(); return; }
  if (id === 'kitchen-prev' || id === 'kitchen-next') {
    kitchenPage = 1 - kitchenPage;
    selectedRecipeId = Object.keys(RECIPES)[kitchenPage * 4]; drawHud(); return;
  }
  if (id === 'kitchen-learn' || id === 'kitchen-upgrade') {
    const result = id === 'kitchen-learn' ? learnRecipe(state, selectedRecipeId) : upgradeRecipe(state, selectedRecipeId);
    flash(result.ok ? `${RECIPES[selectedRecipeId].name} · 已${id === 'kitchen-learn' ? '学会' : '升级'} Lv.${result.level}` : cookingFailure(result.reason));
    saveState(state); return;
  }
  if (id === 'kitchen-eat') {
    const itemId = Object.keys(DISHES).find((key) => state.inventory[key] > 0) || 'food';
    useInventoryItem(itemId); return;
  }
  if (id === 'kitchen-cook') {
    const quote = recipeQuote(state, selectedRecipeId);
    if (!quote.ok) { flash(cookingFailure(quote.reason)); return; }
    if (state.shelter.inside || (!voyage?.engaged && marineMode === 'land'
      && Math.hypot(avatar.position.x - KITCHEN_APPROACH.x, avatar.position.z - KITCHEN_APPROACH.z) <= 1.5)) beginCooking(selectedRecipeId);
    else {
      if (voyage?.engaged) { flash('请先返航靠岸，在家园厨房制作食物'); return; }
      if (storm.phase === 'impact') { flash('台风登陆中，请先进入小屋避险'); return; }
      hudOpen = false;
      if (walkTo(KITCHEN_APPROACH.x, KITCHEN_APPROACH.z, { type: 'cook', recipeId: selectedRecipeId }, .3)) flash('正在前往海岛厨房，抵达后制作料理');
    }
  }
}

function createTradeSign() {
  // A permanent shore-side stall is available even before the dock upgrade.
  const root = actionRoot(new THREE.Group(), { type: 'trade' });
  root.position.set(TRADE_SIGN_POSITION.x, groundHeightAt(TRADE_SIGN_POSITION.x, TRADE_SIGN_POSITION.z), TRADE_SIGN_POSITION.z);
  const timber = mat(0x70502e);
  const edge = mat(0xc89c57, .67);
  root.add(mesh(new THREE.CylinderGeometry(.065, .085, 1.65, 10), timber, -.42, .82, 0, false));
  root.add(mesh(new RoundedBoxGeometry(1.18, .58, .12, 3, .045), timber, 0, 1.42, 0, false));
  root.add(mesh(new THREE.TorusGeometry(.18, .033, 8, 20), edge, -.23, 1.43, .09, false));
  root.add(mesh(new THREE.SphereGeometry(.075, 10, 7), edge, -.23, 1.43, .1, false));
  for (const x of [-.49, .49]) root.add(mesh(new THREE.SphereGeometry(.038, 7, 6), edge, x, 1.42, .09, false));
  const labelCanvas = offscreen(320, 112);
  const labelContext = labelCanvas.getContext('2d');
  labelContext.fillStyle = 'rgba(5,48,53,.94)';
  roundRect(labelContext, 4, 4, 312, 104, 18);
  labelContext.strokeStyle = '#f1cf82';
  labelContext.lineWidth = 5;
  labelContext.strokeRect(10, 10, 300, 92);
  labelContext.fillStyle = '#fff4d1';
  labelContext.textAlign = 'center';
  labelContext.font = '700 49px sans-serif';
  labelContext.fillText('交易站', 160, 75);
  const labelTexture = new THREE.CanvasTexture(labelCanvas);
  labelTexture.colorSpace = THREE.SRGBColorSpace;
  const label = new THREE.Sprite(new THREE.SpriteMaterial({ map: labelTexture, transparent: true, depthTest: true }));
  label.position.set(0, 2.22, 0);
  label.scale.set(2.2, .78, 1);
  root.add(label);
  worldLabels.push(label);
  const hit = mesh(new THREE.SphereGeometry(.86, 10, 8), new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }), 0, 1.32, 0, false);
  root.add(hit);
  scene.add(root);
  visualProps.tradeSign = root;
  registerHomeCollision(root, 'home-trade-sign');
}

function createFreshwaterCollector() {
  const root = actionRoot(new THREE.Group(), { type: 'freshwater' });
  root.position.set(FRESHWATER_SITE.x, groundHeightAt(FRESHWATER_SITE.x, FRESHWATER_SITE.z), FRESHWATER_SITE.z);
  const bamboo = mat(0x8b713d, .88);
  const canvasMaterial = new THREE.MeshStandardMaterial({ color: 0x7da994, roughness: .9, side: THREE.DoubleSide });
  const barrelMaterial = mat(0x70482b, .92);
  for (const [x, z] of [[-.9, -.7], [.9, -.7], [-.9, .7], [.9, .7]]) {
    root.add(mesh(new THREE.CylinderGeometry(.045, .065, 2.15, 8), bamboo, x, 1.08, z, false));
  }
  const funnel = mesh(new THREE.ConeGeometry(1.18, .52, 4, 1, true), canvasMaterial, 0, 1.9, 0, false);
  funnel.rotation.y = Math.PI / 4;
  root.add(funnel);
  const barrel = mesh(new THREE.CylinderGeometry(.46, .52, 1.05, 14), barrelMaterial, .72, .54, .76, false);
  root.add(barrel);
  const rim = mesh(new THREE.TorusGeometry(.47, .045, 7, 18), bamboo, .72, 1.06, .76, false);
  rim.rotation.x = Math.PI / 2;
  root.add(rim);
  const waterLevel = mesh(new THREE.CircleGeometry(.42, 24), new THREE.MeshBasicMaterial({ color: 0x61cbd0, transparent: true, opacity: .78 }), .72, 1.07, .76, false);
  waterLevel.rotation.x = -Math.PI / 2;
  root.add(waterLevel);
  const pipe = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3([
    new THREE.Vector3(0, 1.63, 0), new THREE.Vector3(.25, 1.38, .24), new THREE.Vector3(.72, 1.25, .65),
  ]), 12, .035, 7, false), mat(0x5f7770, .55));
  root.add(pipe);
  const still = mesh(new THREE.CircleGeometry(.62, 20), new THREE.MeshStandardMaterial({ color: 0x7ca9ad, transparent: true, opacity: .56, side: THREE.DoubleSide }), -.92, 1.03, .84, false);
  still.rotation.x = -1.05;
  root.add(still);
  const drops = [];
  for (let i = 0; i < 5; i++) {
    const drop = mesh(new THREE.SphereGeometry(.035, 8, 6), mat(0x8ee6e3, .28), -.7 + i * .34, 2.75 + i * .13, -.35 + (i % 2) * .6, false);
    root.add(drop); drops.push(drop);
  }
  const labelCanvas = offscreen(320, 112);
  const labelContext = labelCanvas.getContext('2d');
  labelContext.fillStyle = 'rgba(5,48,53,.94)'; roundRect(labelContext, 4, 4, 312, 104, 18);
  labelContext.strokeStyle = '#9fe7d2'; labelContext.lineWidth = 5; labelContext.strokeRect(10, 10, 300, 92);
  labelContext.fillStyle = '#fff4d1'; labelContext.textAlign = 'center'; labelContext.font = '700 45px sans-serif';
  labelContext.fillText('淡水收集', 160, 73);
  const labelTexture = new THREE.CanvasTexture(labelCanvas); labelTexture.colorSpace = THREE.SRGBColorSpace;
  const label = new THREE.Sprite(new THREE.SpriteMaterial({ map: labelTexture, transparent: true, depthTest: true }));
  label.position.set(0, 2.75, 0); label.scale.set(2.15, .75, 1); root.add(label); worldLabels.push(label);
  root.add(mesh(new THREE.SphereGeometry(1.28, 10, 8), new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }), 0, 1.25, 0, false));
  scene.add(root);
  root.scale.setScalar(.65);
  visualProps.freshwater = { root, waterLevel, still, drops };
  registerHomeCollision(root, 'home-freshwater', { filter: object => object !== waterLevel && !drops.includes(object) });
  refreshFreshwaterVisuals();
}

function refreshFreshwaterVisuals() {
  const props = visualProps.freshwater;
  if (!props) return;
  const supply = state.freshwater;
  props.waterLevel.visible = supply.tank > 0;
  props.waterLevel.position.y = .58 + .48 * supply.tank / supply.tankCapacity;
  props.still.visible = supply.stillLevel > 0;
  const rainy = rainForDay(state.dayId) > 0 || ['preparing', 'impact'].includes(storm.phase);
  props.drops.forEach((drop, index) => {
    drop.visible = rainy;
    drop.position.y = 2.3 + ((sceneElapsed * 2.2 + index * .31) % .9);
  });
}

const farmFrameGeometry = new RoundedBoxGeometry(1, 1, 1, 1, .07);
const farmFrameMaterial = mat(0x9a723e);
let farmEnclosure;
function createFarmBeds() {
  if (!farmEnclosure) {farmEnclosure=createFarmEnclosure();farmEnclosure.position.set(FARM_ORIGIN.x,FARM_ORIGIN.y+.08,FARM_ORIGIN.z);scene.add(farmEnclosure);registerHomeCollision(farmEnclosure,'home-farm-fence');}
  (state.plots || []).forEach((plot, index) => {
    if (cropObjects[index]) return;
    const [dx, dz] = bedPoints[index] || [0, 0];
    const root = actionRoot(new THREE.Group(), { type: 'farmplot', plotId: plot.id });
    root.position.set(plots.garden.pos[0] + dx, plots.garden.pos[1] + .12, plots.garden.pos[2] + dz);
    const soilMat = mat(plot.elevation === 'high' ? 0x785b38 : 0x916b43);
    const soilBase = mesh(new THREE.SphereGeometry(1, 18, 8), mat(0x57432f), 0, .065, 0, false);
    soilBase.scale.set(1.02, .085, .7);
    root.add(soilBase);
    for (const [z, length, radius] of [[-.26, .9, .27], [.26, .85, .25]]) {
      const ridge = mesh(new THREE.SphereGeometry(1, 18, 8), soilMat, .03, .14, z, false);
      ridge.scale.set(length, .15, radius);
      root.add(ridge);
    }
    const groove = mesh(new THREE.SphereGeometry(1, 16, 6), mat(0x423525), 0, .11, 0, false);
    groove.scale.set(.85, .022, .055);
    root.add(groove);
    const frame = new THREE.InstancedMesh(farmFrameGeometry, farmFrameMaterial, 8);
    const framePart = new THREE.Object3D();
    const boards = [[0,.14,-.76,2.14,.12,.075],[0,.14,.76,2.14,.12,.075],[-1.07,.14,0,.075,.12,1.56],[1.07,.14,0,.075,.12,1.56],
      ...[-1,1].flatMap(x=>[-1,1].map(z=>[x*1.07,.16,z*.76,.12,.32,.12]))];
    boards.forEach(([x,y,z,sx,sy,sz],i)=>{framePart.position.set(x,y,z);framePart.scale.set(sx,sy,sz);framePart.updateMatrix();frame.setMatrixAt(i,framePart.matrix);});
    frame.castShadow = frame.receiveShadow = true;root.add(frame);
    const plants = new THREE.Group();
    // Plants emerge directly from the raised earth, not from a decorative bed mesh.
    plants.position.y = .29;
    plants.scale.setScalar(1.24);
    plants.userData.cropId = '';
    plants.visible = Boolean(plot.crop);
    root.userData.plants = plants;
    root.userData.protection = createFarmProtection();
    const labelCanvas = offscreen(256, 88);
    const labelTexture = new THREE.CanvasTexture(labelCanvas);
    labelTexture.colorSpace = THREE.SRGBColorSpace;
    const label = new THREE.Sprite(new THREE.SpriteMaterial({ map: labelTexture, transparent: true, depthTest: true }));
    label.scale.set(2.1, .72, 1);
    label.position.set(0, .84, -.86);
    root.userData.label = { canvas: labelCanvas, texture: labelTexture, sprite: label, text: '' };
    root.add(plants, root.userData.protection.root, label);
    worldLabels.push(label);
    scene.add(root);
    cropObjects[index] = root;
    registerHomeCollision(root, `farm-${plot.id}`);
  });
}

function refreshPlotLabel(root, text) {
  const label = root?.userData.label;
  if (!label || label.text === text) return;
  label.text = text;
  const ctx = label.canvas.getContext('2d');
  ctx.clearRect(0, 0, 256, 88);
  ctx.fillStyle = text.includes('可收获') ? 'rgba(141, 83, 27, .96)'
    : text.startsWith('空田') ? 'rgba(91, 61, 37, .94)' : 'rgba(41, 88, 47, .96)';
  ctx.fillRect(4, 7, 248, 72);
  ctx.strokeStyle = '#ddbe82';
  ctx.lineWidth = 5;
  ctx.strokeRect(7, 10, 242, 66);
  ctx.fillStyle = '#fff4d6';
  ctx.font = 'bold 38px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 128, 45, 232);
  label.texture.needsUpdate = true;
}

function leafBlade(material, x, y, z, angle, scale = 1, tilt = .35) {
  const leaf = mesh(leafGeometry, material, x, y, z, false);
  leaf.rotation.set(-Math.PI / 2 + tilt, 0, angle);
  leaf.scale.setScalar(scale);
  return leaf;
}

function createCropForm(group, cropId) {
  group.clear();
  group.userData.cropId = cropId;
  const stemMat = mat(0x467c45);
  const leafMat = new THREE.MeshStandardMaterial({ color: 0x72a95a, roughness: .88, side: THREE.DoubleSide });
  const fruitMat = mat(0xd97843, .55);
  let leafIndex = 0;
  const stem = (x, height, z = 0) => {
    const part = mesh(new THREE.CapsuleGeometry(.028, height, 3, 5), stemMat, x, height / 2, z, false);
    part.userData.growthRole = 'stem';
    group.add(part);
    return part;
  };
  const leaf = (x, y, z, angle, size, tilt) => {
    const part = leafBlade(leafMat, x, y, z, angle, size, tilt);
    part.userData.growthRole = 'leaf';
    part.userData.growthDelay = .18 + leafIndex++ * .035;
    group.add(part);
    return part;
  };
  const fruit = (x, y, z, size = .1) => {
    const part = mesh(new THREE.SphereGeometry(size, 10, 7), fruitMat, x, y, z, false);
    part.userData.growthRole = 'fruit';
    group.add(part);
    return part;
  };
  const sprout = new THREE.Group();
  const sproutStem = mesh(new THREE.CapsuleGeometry(.022, .16, 3, 6), stemMat, 0, .09, 0, false);
  const sproutLeafA = leafBlade(leafMat, 0, .15, 0, -.8, .4, .28);
  const sproutLeafB = leafBlade(leafMat, 0, .17, 0, 1.0, .36, .32);
  for (const part of [sproutStem, sproutLeafA, sproutLeafB]) part.userData.growthRole = 'seedling';
  sprout.add(sproutStem, sproutLeafA, sproutLeafB);
  group.userData.seedling = sprout;
  group.add(sprout);
  if (cropId === 'corn') {
    stem(0, .95);
    for (let i = 0; i < 6; i++) leaf(0, .18 + i * .11, 0, i * 1.12, .92, .18);
    const cob = mesh(new THREE.CapsuleGeometry(.115, .22, 5, 10), fruitMat, .1, .56, .04, false);
    cob.userData.growthRole = 'fruit'; group.add(cob);
    for (const side of [-1, 1]) leaf(.1, .44, side * .06, side * 1.1, .37, .12);
  } else if (cropId === 'pineapple') {
    for (let i = 0; i < 8; i++) leaf(0, .06, 0, i * .79, .86, .04);
    const pineapple = mesh(new THREE.SphereGeometry(.18, 12, 9), fruitMat, 0, .31, .01, false);
    pineapple.scale.set(.82, 1.32, .82); pineapple.userData.growthRole = 'fruit'; group.add(pineapple);
    for (let i = 0; i < 6; i++) leaf(0, .5, 0, i * 1.05, .35, .12);
  } else if (cropId === 'pumpkin') {
    stem(0, .2);
    for (let i = 0; i < 6; i++) leaf(0, .13, 0, i * 1.05, 1.0, .12);
    const pumpkinGeometry = new THREE.SphereGeometry(.24, 24, 12);
    const positions = pumpkinGeometry.attributes.position;
    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i), z = positions.getZ(i);
      const rib = 1 + .075 * Math.cos(Math.atan2(z, x) * 8);
      positions.setX(i, x * rib);
      positions.setZ(i, z * rib);
    }
    pumpkinGeometry.computeVertexNormals();
    const pumpkin = mesh(pumpkinGeometry, fruitMat, .16, .12, .11, false);
    pumpkin.scale.set(1.12, .7, 1); pumpkin.userData.growthRole = 'fruit'; group.add(pumpkin);
  } else if (cropId === 'sweetPotato') {
    stem(0, .2);
    for (let i = 0; i < 7; i++) leaf(0, .12, 0, i * .9, .94, .18);
    const tuber = fruit(.13, .045, -.12, .16);
    tuber.scale.set(1.35, .58, .85);
  } else {
    stem(0, .72);
    for (let i = 0; i < 6; i++) leaf(0, .2 + (i % 3) * .12, 0, i * 1.12 + .3, .73, .32);
    fruit(.12, .34, .06, .12); fruit(-.12, .44, .02, .105); fruit(.06, .56, -.03, .095);
  }
  // Three plants per bed read as a planted row at the island camera distance.
  const originals = [...group.children];
  for (const [x, z, size] of [[-.38, -.22, .82], [.37, .21, .76]]) {
    const plant = new THREE.Group();
    plant.position.set(x, 0, z);
    plant.scale.setScalar(size);
    for (const object of originals) plant.add(object.clone(true));
    group.add(plant);
  }
  group.traverse((part) => {
    if (part.isMesh) part.userData.matureScale = part.scale.clone();
  });
}

function createFarmProtection() {
  const root = new THREE.Group();
  const net = new THREE.Group();
  const drain = new THREE.Group();
  const postMat = mat(0x6f5034);
  const ropeMat = mat(0x9b805a, .92);
  const netMat = new THREE.MeshStandardMaterial({ color: 0x8eb9a3, transparent: true, opacity: .5, roughness: .76, side: THREE.DoubleSide });
  const postTops = [];
  for (const x of [-.78, .78]) for (const z of [-.48, .48]) {
    const post = mesh(new THREE.CylinderGeometry(.025, .04, .78, 7), postMat, x, .4, z, false);
    post.rotation.z = x * .1;
    net.add(post);
    postTops.push(new THREE.Vector3(x, .76, z));
    const anchor = new THREE.Vector3(x * 1.16, .025, z * 1.48);
    const cablePath = new THREE.CatmullRomCurve3([
      new THREE.Vector3(x, .68, z),
      new THREE.Vector3(x * 1.08, .34, z * 1.18),
      anchor,
    ]);
    net.add(mesh(new THREE.TubeGeometry(cablePath, 5, .009, 4, false), ropeMat, 0, 0, 0, false));
    const stake = mesh(new THREE.CylinderGeometry(.014, .024, .16, 6), postMat, anchor.x, anchor.y + .07, anchor.z, false);
    stake.rotation.z = -x * .18;
    net.add(stake);
  }
  for (const [from, to] of [[0, 1], [1, 3], [3, 2], [2, 0]]) {
    const ropePath = new THREE.CatmullRomCurve3([postTops[from], postTops[to]]);
    net.add(mesh(new THREE.TubeGeometry(ropePath, 6, .012, 5, false), ropeMat, 0, 0, 0, false));
  }
  const canopy = mesh(new THREE.PlaneGeometry(1.72, .96, 4, 2), netMat, 0, .72, 0, false);
  canopy.rotation.x = -Math.PI / 2;
  const positions = canopy.geometry.attributes.position;
  for (let i = 0; i < positions.count; i++) positions.setZ(i, positions.getZ(i) + Math.sin(positions.getX(i) * 3.6) * .11);
  positions.needsUpdate = true;
  net.add(canopy);
  const ditchBankMat = mat(0x8a6944, .96);
  const ditchFloorMat = mat(0x334c43, .94);
  const ditchLength = 1.52;
  const ditchHalfWidth = .18;
  const ditchFloorHalfWidth = .062;
  const ditchTopY = .018;
  const ditchBottomY = -.045;
  const slopeGeometry = (outerZ, innerZ) => {
    const geometry = new THREE.BufferGeometry();
    const vertices = [
      -ditchLength / 2, ditchTopY, outerZ,
      ditchLength / 2, ditchTopY, outerZ,
      -ditchLength / 2, ditchBottomY, innerZ,
      ditchLength / 2, ditchBottomY, innerZ,
    ];
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(outerZ < 0 ? [0, 2, 1, 1, 2, 3] : [0, 1, 2, 1, 3, 2]);
    geometry.computeVertexNormals();
    return geometry;
  };
  drain.add(mesh(slopeGeometry(-ditchHalfWidth, -ditchFloorHalfWidth), ditchBankMat, 0, 0, 0, false));
  drain.add(mesh(slopeGeometry(ditchHalfWidth, ditchFloorHalfWidth), ditchBankMat, 0, 0, 0, false));
  const ditchFloor = mesh(new THREE.PlaneGeometry(ditchLength, ditchFloorHalfWidth * 2), ditchFloorMat, 0, ditchBottomY, 0, false);
  ditchFloor.rotation.x = -Math.PI / 2;
  drain.add(ditchFloor);
  // Small low lips connect the recessed channel to the existing soil ridges.
  for (const side of [-1, 1]) {
    const lip = mesh(new THREE.CapsuleGeometry(.022, ditchLength - .16, 3, 8), ditchBankMat,
      0, .012, side * .235, false);
    lip.rotation.z = Math.PI / 2;
    drain.add(lip);
  }
  root.add(net, drain);
  return { root, net, drain };
}

function refreshFarmBeds() {
  if(farmEnclosure)farmEnclosure.visible=state.plots.length<=4;
  const availableSeed = usableCropSeed(state, selectedSeed);
  (state.plots || []).forEach((plot, index) => {
    const root = cropObjects[index];
    const group = root?.userData.plants;
    if (!group) return;
    group.visible = Boolean(plot.crop);
    const protection = cropObjects[index]?.userData.protection;
    const defense = state.defenses?.plots?.find((entry) => entry.id === plot.id);
    if (protection) {
      protection.net.visible = defense?.windNet === true || Number(defense?.windNet) > 0;
      protection.drain.visible = defense?.drainage === true || Number(defense?.drainage) > 0;
    }
    if (!plot.crop) {
      refreshPlotLabel(root, availableSeed ? `空田·${cropLabel(availableSeed)}可播` : '空田·缺农田种子');
      return;
    }
    const crop = cropOptions.find((entry) => entry.id === plot.crop.id);
    if (group.userData.cropId !== plot.crop.id) createCropForm(group, plot.crop.id);
    const progress = Math.max(0, Math.min(1, Number(plot.crop.growthMinutes || 0) / (crop?.minutes || 1)));
    refreshPlotLabel(root, `${cropLabel(plot.crop.id)}·${progress >= 1 ? '可收获' : progress < .38 ? '幼苗' : progress < .68 ? '生长中' : '结果中'}`);
    const fruitColor = crop?.id === 'tomato' ? 0xd64331 : crop?.id === 'sweetPotato' ? 0xb2814b :
      crop?.id === 'corn' ? 0xe5c24f : crop?.id === 'pineapple' ? 0xe7b849 : 0xf0a84d;
    if (group.userData.fruitColor !== fruitColor) {
      group.traverse((part) => {
        if (part.isMesh && part.userData.growthRole === 'fruit') part.material.color.setHex(fruitColor);
      });
      group.userData.fruitColor = fruitColor;
    }
    const stage = (from, to) => {
      const value = THREE.MathUtils.clamp((progress - from) / (to - from), 0, 1);
      return value * value * (3 - 2 * value);
    };
    group.traverse((part) => {
      if (!part.isMesh) return;
      const role = part.userData.growthRole;
      const baseScale = part.userData.matureScale;
      if (role === 'seedling') {
        const growth = .62 + stage(0, .24) * .38;
        part.visible = progress < .39;
        const spread = .72 + stage(0, .24) * .28;
        part.scale.set(baseScale.x * spread, baseScale.y * growth, baseScale.z * spread);
      } else {
        const delay = role === 'leaf' ? part.userData.growthDelay : .12;
        const growth = role === 'fruit' ? stage(.65, .94) : stage(delay, delay + .32);
        part.visible = role === 'fruit' ? progress >= .65 : progress >= .12;
        const spread = .72 + growth * .28;
        part.scale.set(baseScale.x * spread, baseScale.y * Math.max(.12, growth), baseScale.z * spread);
      }
    });
  });
}

function plotPoint(plotId) {
  const index = (state.plots || []).findIndex((plot) => plot.id === plotId);
  const [dx, dz] = bedPoints[index] || [0, 0];
  return [plots.garden.pos[0] + dx, plots.garden.pos[2] + dz];
}

function addPlot(kind) {
  const data = plots[kind];
  if (state.built[kind]) return showBuilding(kind);
  const group = actionRoot(new THREE.Group(), { type: 'build', kind });
  group.position.set(...data.pos);
  const pad = mesh(new THREE.CylinderGeometry(1.6, 1.72, .22, 24), new THREE.MeshStandardMaterial({ color: C.sand, roughness: .9, emissive: 0x332400, emissiveIntensity: .12 }), 0, .1, 0);
  const marker = mesh(new THREE.TorusGeometry(.75, .08, 7, 24), materials.gold, 0, .25, 0);
  marker.rotation.x = Math.PI / 2;
  group.add(pad, marker);
  group.userData.marker = marker;
  animated.push({ object: marker, spin: true, speed: .6, amount: 1 });
  scene.add(group);
  data.plot = group;
}

function showBuilding(kind) {
  const data = plots[kind];
  if (data.built) return;
  if (data.plot) {
    data.plot.visible = false;
    data.plot.userData.action = null;
  }
  if (kind === 'garden') {
    // The state-driven farm beds below are the garden. A second static GLB
    // would show permanent generic sprouts even when every plot is empty.
    data.built = new THREE.Group();
    return;
  }
  const model = buildingSources[kind].clone(true);
  model.position.set(...data.pos);
  if (kind === 'dock') {
    model.rotation.y = -.85;
    // Clicking the raised deck is an interaction, not a request to walk on the
    // terrain underneath it (which would visually sink the avatar into planks).
    actionRoot(model, { type: 'trade' });
  }
  model.scale.setScalar(sceneReady ? .001 : 1);
  scene.add(model);
  if (kind === 'hut') {
    model.traverse(object => { if (object.isMesh) object.userData.cameraOccluder = true; });
    hutRoofs.push(addHutDetails(model));
    const door = model.getObjectByName('Hut_Door');
    if (door) {
      // The GLB keeps the original leaf separate; the hinge is on its left jamb.
      const pivot = new THREE.Group();
      pivot.position.set(-.53, 0, -1.6);
      door.position.set(.43, .9, 0);
      pivot.add(door);
      model.add(pivot);
      pivot.rotation.y = state.shelter.doorOpen ? 1.35 : 0;
      hutDoorPivot = pivot;
    }
    model.traverse((object) => {
      if (object.name.startsWith('HutRoof')) hutRoofs.push(object);
      if (/^Hut_Bed(Base|Quilt|Pillow)/.test(object.name)) {
        actionRoot(object, { type: 'hut-bed' });
        if (object.name === 'Hut_BedBase') hutBed = object;
      }
    });
    // The shipped GLB merges furniture into Hut.001. Match the source bed's
    // footprint without regenerating or replacing the user's building asset.
    if (!hutBed) {
      hutBed = actionRoot(mesh(new THREE.BoxGeometry(1.34, .55, 1.94),
        new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }), -1.05, .35, -.5, false), { type: 'hut-bed' });
      model.add(hutBed);
    }
    const bedLabel = actionRoot(createActionLabel('床位 · 休息', 2.5), { type: 'hut-bed' });
    bedLabel.position.set(-1.05, 1.4, -.5);
    bedLabel.visible = state.shelter.inside;
    bedLabel.userData.indoorBedLabel = true;
    model.add(bedLabel);
  }
  gradeWorld(model, artDaylight);
  cameraOcclusion.apply(model);
  data.built = model;
  if (kind === 'hut') registerHomeCollision(model, 'home-hut-details', { filter: object => Boolean(object.userData.physicalParts) });
  if (sceneReady) animated.push({ object: model, grow: true, target: 1 });
}

function completeArrival(action) {
  if (action?.type === 'cook') { beginCooking(action.recipeId); return; }
  if (!action || (storm.phase === 'impact' && !['hut-door-open', 'hut-door-close'].includes(action.type))) return;
  if (action.type === 'voyage-board') { voyage?.board(); return; }
  if (action.type === 'hut-door-open') {
    state.shelter.doorOpen = true;
    flash('小屋门已打开 · 再点门进入，进屋后记得关门');
    saveState(state);
    drawHud();
    return;
  }
  if (action.type === 'hut-door-close') { closeHutDoor(); return; }
  let tool = action.type === 'tree-work' ? action.operation === 'chop' ? 'axe' : 'wateringCan'
    : action.type === 'farm-arrive' || action.type === 'install' || action.type === 'repair' || action.type === 'wood-patch' ? 'shovel'
    : action.type === 'capture' || action.type === 'sea-capture' ? (action.tool === 'dive' ? 'diveCatch' : action.speciesId === 'crab' ? 'hand' : action.tool === 'net' ? 'net' : 'rod')
      : action.type === 'world' && action.root?.userData.action?.kind === 'wood' ? 'shovel' : null;
  const plotAction = action.type === 'world' && action.root?.userData.action?.type === 'farmplot';
  if (plotAction) {
    const plot = state.plots?.find((entry) => entry.id === action.root.userData.action.plotId);
    const crop = plot?.crop && CROPS[plot.crop.id];
    tool = !plot?.crop ? (usableCropSeed(state, selectedSeed) ? 'shovel' : null) : crop && plot.crop.growthMinutes >= crop.minutes ? 'hand'
      : state.gameMinutes >= plot.crop.wateredUntil ? 'wateringCan' : null;
  }
  if (tool && !(tool === 'rod' && ['capture', 'sea-capture'].includes(action.type))) playAvatarAction(tool);
  if (action.type === 'home-project') {
    const result = submitHomeProject(state, action.projectId, avatar.position);
    if (result.changed) playAvatarAction('hand');
    if (result.completed) islandLifeVisual?.celebrate(action.projectId);
    flash(result.message);
  } else if (action.type === 'island-order') {
    const result = submitOrder(state, action.orderId, avatar.position);
    if (result.ok) { playAvatarAction('hand'); islandLifeVisual?.celebrateOrder?.(); }
    flash(result.ok ? `海风来信已交付 · 货款 ${result.baseValue} + 奖励 ${result.bonus} 金`
      : result.reason === 'insufficient-stock' ? '背包货物不足 · 先收集或从仓库取回，再到心愿板交付'
        : result.reason === 'already-completed' ? '这封来信已经交付，奖励已领取'
          : '来信已更新或未到交付处 · 到心愿板查看当前来信');
  } else if (action.type === 'world') {
    const { root } = action;
    const data = root?.userData.action;
    if (!data) return;
    if (data.type === 'gather' && data.active) {
      const source = root.getWorldPosition(new THREE.Vector3());
      if (!root.visible || Math.hypot(avatar.position.x - source.x, avatar.position.z - source.z) > 1.8) {
        flash('离可见的资源太远，请走近后再采集'); return;
      }
      const result = gather(state, data.kind, data.nodeId);
      if (!result.ok) { flash(result.reason === 'stamina' ? '体力不足，稍歇几秒即可恢复' : '这处资源尚未刷新，稍后再来'); return; }
      root.visible = false;
      data.active = false;
      flash(data.kind === 'wood' ? '搬回 2 份漂流木 · 可在维修仓储抢修受损设施' : '捡起 1 枚彩贝 · 消耗 2 体力');
    } else if (data.type === 'build') {
      const item = BUILDINGS[data.kind];
      if (!build(state, data.kind)) { flash(`${item.label}需要 ${item.cost.wood} 木材 + ${item.cost.shells} 贝壳`); return; }
      showBuilding(data.kind);
      flash(`${item.label}建成了！`);
    } else if (data.type === 'farmplot') {
      const plot = state.plots?.find((entry) => entry.id === data.plotId);
      if (!plot) return;
      selectedPlotId = plot.id;
      const crop = CROPS[plot.crop?.id];
      if (plot.crop && crop && plot.crop.growthMinutes >= crop.minutes) {
        const result = harvestCrop(state, data.plotId);
        flash(result.ok ? `收获 ${crop.label} ×${result.quantity ?? crop.yield}，已装入背包${result.journalNew ? ' · 海岛手记新增' : ''}`
          : result.reason === 'capacity' ? plotGuidance(state, plot, selectedSeed).detail
            : result.reason === 'stamina' ? '收获需要 2 体力 · 稍歇几秒自然恢复，再回来收获' : '作物还没成熟，稍后再来查看');
      } else if (!plot.crop) {
        const cropId = usableCropSeed(state, selectedSeed);
        if (!cropId) {
          const storedCrop = Object.keys(CROPS).some((id) => state.storage[`${id}Seed`] > 0);
          flash(storedCrop ? '农田种子在仓库，请先取回背包再播种'
            : state.inventory.palmSeed ? '背包只有椰树种子；田地需要地瓜、番茄等农作物种子'
              : '背包没有农作物种子，请在农田或商店购买');
          return;
        }
        const changedSeed = cropId !== selectedSeed;
        selectedSeed = cropId;
        const result = plantCrop(state, data.plotId, cropId);
        flash(result.ok ? changedSeed ? `原选种子已用完，已改种背包中的${cropLabel(cropId)}`
          : `种下${cropLabel(cropId)}，成熟前记得浇水`
          : result.reason === 'stamina' ? '体力不足，稍歇几秒再播种'
            : result.reason === 'no-seed' ? `${cropLabel(cropId)}种子已用完，请检查背包` : '田地暂时无法播种');
      } else if (plot.crop.growthMinutes < (crop?.minutes || 0) && state.gameMinutes >= plot.crop.wateredUntil) {
        const result = waterCrop(state, data.plotId);
        flash(result.ok ? `浇水完成，${crop.label}继续生长 · 水壶剩余 ${state.freshwater.canteen} L`
          : result.reason === 'water' ? '水壶已空，请先到淡水收集场补水'
            : result.reason === 'stamina' ? '浇水需要 1 体力 · 稍歇几秒再来' : '这块田现在无需浇水');
      } else { const hint = plotGuidance(state, plot, selectedSeed); flash(`${hint.status} · ${hint.detail}`); }
      refreshFarmBeds();
    }
  } else if (action.type === 'tree-work') {
    const tree = state.trees.find((entry) => entry.id === action.treeId);
    const result = action.operation === 'chop' ? chopTree(state, action.treeId)
      : action.operation === 'plant' ? plantTree(state, action.treeId) : tendTree(state, action.treeId);
    refreshTreeVisuals();
    if (result.ok) flash(action.operation === 'chop' ? `砍下椰树，获得木材 ×${result.wood}；树桩可重新种植`
      : action.operation === 'plant' ? '椰树苗已种下；每日养护可加速生长'
        : `已养护${tree?.stage === 'mature' ? '成树' : '树苗'}，健康 ${result.health}/100`);
    else flash(({ 'not-mature': '树还没有长成，暂时不能砍伐', stamina: '体力不足，稍歇几秒', occupied: '这里已有树木',
      'no-seed': '背包缺少椰树种子；农田种子不能种椰树', 'no-tree': '这里没有可养护的树',
      'already-tended': '这棵树今天已经养护过，明天再来' })[result.reason] || '林木操作未完成');
  } else if (action.type === 'trip-start') {
    const availability = captureAvailability(state, action.speciesId, action.tool);
    if (!availability.ok) { releaseMarineTarget(); flash(captureFailureMessage(availability)); return; }
    const target = marineTargets.find((entry) => entry.id === action.targetId);
    if (!targetAvailable(target)) { releaseMarineTarget(); flash('目标已经离开，请重新选取海产'); return; }
    state.boatMoored = false;
    boatTrip = { phase: 'outbound', speciesId: action.speciesId, tool: action.tool, targetId: target.id, elapsed: 0, duration: 1.6,
      fromX: heroBoat.position.x, fromZ: heroBoat.position.z,
      toX: target.x, toZ: target.z };
    flash(`解开缆绳，正驶向可见的${SEAFOOD[action.speciesId].label}`);
  } else if (action.type === 'capture' || action.type === 'sea-capture') {
    const target = marineTargets.find((entry) => entry.id === action.targetId);
    const captureDistance = action.tool === 'rod' && action.type === 'capture' ? 7.3 : 1.8;
    if (!targetAvailable(target) || activeMarineTarget?.id !== target.id || !target.proxy.visible
      || Math.hypot(avatar.position.x - target.x, avatar.position.z - target.z) > captureDistance) {
      releaseMarineTarget(); flash('目标已经离开，请重新选取可见海产'); return;
    }
    if (action.tool === 'rod') {
      startFishingSession(target, action.type === 'sea-capture');
      return;
    }
    const result = catchSeafood(state, action.speciesId, action.tool);
    if (result.ok && result.caught > 0) {
      state.gatherCooldowns[target.id] = state.gameMinutes + 360;
      spawnCatchEffects(target, result.caught);
    }
    releaseMarineTarget();
    if (!result.ok) {
      flash(captureFailureMessage(result));
    } else flash(result.caught ? `捕获${SEAFOOD[action.speciesId].label} ×${result.caught}　估值 ${result.value} 金${result.journalNew ? ' · 海岛手记新增' : ''}` : `${SEAFOOD[action.speciesId].label}逃脱了，体力已消耗`);
  } else if (action.type === 'buy') {
    const result = buyItem(state, action.itemId, action.quantity);
    if (result.ok && ['lowPlot', 'highPlot'].includes(action.itemId)) {
      createFarmBeds();
      flash(`新田块已开垦 · 当前 ${state.plots.length} 块`);
    } else if (result.ok && action.itemId === 'waveBarrier') {
      flash('消浪护栏已入背包 · 点击码头侧方海里的黄色安装浮标，或在背包点物资');
    } else if (result.ok && action.itemId === 'solarStill') {
      refreshFreshwaterVisuals();
      flash('太阳能蒸馏器已安装到淡水收集场 · 每日额外产出 2 L');
    } else if (result.ok && ['windNet', 'drainage', 'anchor', 'waterproofCabinet', 'windowReinforcement', 'raisedBed'].includes(action.itemId)) {
      flash('物资已入背包；将前往设施安装，也可在背包点它再次安装');
      beginInstallStock(action.itemId);
    } else {
      const label = shopCatalog.find((item) => item.id === action.itemId)?.name || action.itemId;
      flash(result.ok ? `已购 ${label} ×${action.quantity}，支出 ${result.cost} 金` : `购买未完成：${result.reason === 'gold' ? '金币不足' : result.reason === 'capacity' ? '背包空间不足' : '前置条件未满足或已拥有'}`);
    }
  } else if (action.type === 'install') {
    const targetIds = installTargets(action.itemId);
    const result = targetIds ? installDefense(state, action.itemId, targetIds) : { ok: false, reason: 'already-protected' };
    const label = shopCatalog.find((item) => item.id === action.itemId)?.name || '防灾设施';
    flash(result.ok ? action.itemId === 'waveBarrier' ? '消浪护栏已固定在近岸浅海；台风时可降低码头损伤'
      : `${label}已安装，背包物资已消耗，可在“已拥有装备”查看`
      : `安装未完成：${result.reason === 'already-protected' ? '当前设施已保护，暂不能重复安装' : result.reason === 'insufficient-stock' ? '背包没有该物资，请先从仓库取回' : result.reason === 'stamina' ? '体力不足' : '没有可安装的目标'}`);
  } else if (action.type === 'repair') {
    const result = repairAsset(state, action.asset);
    flash(result.ok ? `修复完成：${({ boat: '船只', dock: '码头', hut: '小屋' })[action.asset]}已恢复至 100 耐久，花费 ${result.cost} 金` : `修理未完成：${result.reason === 'gold' ? '金币不足' : result.reason === 'stamina' ? '体力不足' : '已满耐久'}`);
  } else if (action.type === 'wood-patch') {
    const result = patchAssetWithWood(state, action.asset);
    flash(result.ok ? `木材抢修完成：${({ boat: '船只', dock: '码头', hut: '小屋' })[action.asset]}恢复 ${result.points} 耐久，消耗 ${result.wood} 木材`
      : `抢修未完成：${result.reason === 'wood' ? '木材不足，可在海滩拾取漂流木或伐树' : result.reason === 'stamina' ? '体力不足' : '耐久已达 40，继续修理需要金币'}`);
  } else if (action.type === 'move-stock') {
    const result = moveAllStock(state, action.from, action.to);
    const amount = Object.values(state[action.to]).reduce((sum, n) => sum + n, 0);
    flash(result.ok ? `${action.to === 'storage' ? '货物已存入仓库' : '货物已取回背包'} · 当前 ${amount} 件` : `转存未完成：${result.reason === 'capacity' ? '目标空间不足' : result.reason === 'invalid-item' ? '旧版汇总鱼获需先在码头出售' : '没有可转移货物'}`);
  } else if (action.type === 'retrieve-item') {
    const result = moveStock(state, action.itemId, 1, 'storage', 'inventory');
    flash(result.ok ? '已从仓库取回 1 件；打开背包点该物品即可使用'
      : `取回失败：${result.reason === 'capacity' ? '背包已满' : '仓库里没有该物品'}`);
  } else if (action.type === 'sell') {
    let value = 0;
    const sold = [];
    for (const itemId of [...Object.keys(SEAFOOD), ...Object.keys(CROPS), 'legacyFish']) {
      const quantity = state.inventory[itemId] || 0;
      if (quantity > 0) {
        const result = sellItem(state, itemId, quantity);
        value += result.value || 0;
        sold.push(`${CROPS[itemId]?.label || SEAFOOD[itemId]?.label || '旧鱼获'}×${quantity}`);
      }
    }
    flash(value ? `售出 ${sold.slice(0, 3).join('、')}${sold.length > 3 ? '等货物' : ''}，收入 +${value} 金` : '背包没有可售货品');
  } else if (action.type === 'open-trade') {
    tradeMode = action.mode || 'buy';
    hudPage = 'shop';
    hudOpen = true;
    flash('已到交易站 · 选择购入或售出货物');
  } else if (action.type === 'water-station') {
    hudPage = 'water';
    hudOpen = true;
    flash(`已到淡水收集场 · 储水 ${state.freshwater.tank}/${state.freshwater.tankCapacity} L`);
  } else if (action.type === 'water-refill') {
    const result = refillCanteen(state);
    refreshFreshwaterVisuals();
    flash(result.ok ? `水壶补充 ${result.transferred} L · 水壶 ${state.freshwater.canteen}/${state.freshwater.canteenCapacity} L`
      : result.reason === 'full' ? '水壶已经装满' : '储水桶已空，等待露水或降雨补充');
  } else if (action.type === 'farm-arrive') {
    flash('走近田块，点击泥垄播种、浇水或收获');
  } else if (action.type === 'rest') {
    const recoveryCap = restRecoveryCap(state);
    const plan = bedRestPlan({ ...state, recoveryCap }, Boolean(restTransition));
    if (!plan.ok) {
      flash(restUnavailableMessage(plan.reason));
      return;
    }
    if (action.requestedKind && action.requestedKind !== plan.kind) {
      flash('途中已跨过昼夜时段，请重新选择休息方式');
      return;
    }
    restTransition = { ...plan, recoveryCap, fromMinutes: state.gameMinutes, initialStamina: state.stamina, elapsed: 0, savedElapsed: 0,
      standingPosition: avatar.position.clone(), standingRotation: avatar.rotation.clone() };
    const bedPosition = hutBed.getWorldPosition(new THREE.Vector3());
    avatar.position.set(bedPosition.x, bedPosition.y + .28, bedPosition.z + .55);
    avatar.rotation.set(-Math.PI / 2, 0, 0);
    busyAction = true;
    hudOpen = false;
    flash(plan.kind === 'nap' ? '正在小憩 · 最多 90 游戏分钟恢复体力' : '已回小屋休息 · 时间将逐步推进到清晨');
  }
  syncStorm();
  saveState(state);
  drawHud();
}

function restUnavailableMessage(reason) {
  if (reason === 'outside') return '先进入小屋，才能使用床位休息';
  if (reason === 'door') return '请先关闭小屋门，再上床休息';
  if (reason === 'busy') return '请先完成当前动作';
  return reason === 'full' ? '白天体力已满，无需休息；傍晚后可睡到清晨'
    : reason === 'storm' ? '台风预警期间不能休息，请先检查防灾措施' : '当前时间无法休息';
}

function requestBedRest() {
  const plan = bedRestPlan({ ...state, recoveryCap: restRecoveryCap(state) },
    Boolean(busyAction || avatarVault || voyage?.vaulting || hutCrossing || bedApproach || restTransition || fishingSession || cookingJob));
  if (!plan.ok) { flash(restUnavailableMessage(plan.reason)); return; }
  if (!hutBed || !avatar) { flash('床位正在准备，请稍后再试'); return; }
  const bed = hutBed.getWorldPosition(new THREE.Vector3());
  bedApproach = { start: avatar.position.clone(), end: new THREE.Vector3(bed.x + .85, avatar.position.y, bed.z), elapsed: 0 };
  avatarTarget = null;
  avatarRoute = [];
  arrivalAction = null;
  busyAction = true;
  hudOpen = false;
  flash('正在走到床边');
  drawHud();
}

function restPreviewText() {
  const cap = restRecoveryCap(state);
  const plan = bedRestPlan({ ...state, recoveryCap: cap });
  if (!plan.ok) return restUnavailableMessage(plan.reason);
  const minutes = Math.ceil(plan.targetMinutes - state.gameMinutes);
  return `${minutes} 游戏分钟 / 现实 ${Math.ceil(minutes / (PASSIVE_TIME_SCALE * 2) / 60)} 分钟 · 正常时间流逝，随时起床`;
}

function updateBedApproach(deltaSeconds) {
  if (!bedApproach) return;
  const approach = bedApproach;
  approach.elapsed += deltaSeconds;
  const progress = Math.min(1, approach.elapsed / 1.2);
  avatar.position.lerpVectors(approach.start, approach.end, progress);
  avatar.rotation.y = Math.atan2(approach.end.x - approach.start.x, approach.end.z - approach.start.z);
  const limbs = avatar.userData.limbs;
  limbs.leftLeg.rotation.x = Math.sin(progress * Math.PI * 6) * .3;
  limbs.rightLeg.rotation.x = -limbs.leftLeg.rotation.x;
  if (progress < 1) return;
  limbs.leftLeg.rotation.x = limbs.rightLeg.rotation.x = 0;
  bedApproach = null;
  busyAction = false;
  completeArrival({ type: 'rest' });
}

function stableRoll(key) {
  let hash = 2166136261;
  for (const char of String(key)) { hash ^= char.charCodeAt(0); hash = Math.imul(hash, 16777619); }
  return (hash >>> 0) / 4294967296;
}

function ensureStormSchedule() {
  const current = state.storm || (state.storm = {});
  if (Number.isFinite(current.impactAt)) return;
  const nextDay = Math.max(Number(current.nextDay) || 7, Number(state.dayId) || 1);
  const id = Math.max(1, Number(current.count || state.stormCount) + 1);
  const cycle = [1, 2, 2, 3];
  state.storm = {
    ...current,
    id,
    seed: Number(current.seed) || stableRoll(`storm:${id}`) * 0xffffffff >>> 0,
    intensity: Number(current.strength) || cycle[(id - 1) % cycle.length],
    impactAt: (nextDay - 1) * 1440 + 720,
    settledId: Number(current.settledId) || 0,
    stage: 'calm',
    result: current.result ?? null,
    count: Number(current.count) || 0,
    nextDay,
  };
}

function prepareCropForecast() {
  for (const plot of state.plots || []) {
    if (!plot.crop) continue;
    const crop = CROPS[plot.crop.id];
    if (!crop) continue;
    if (!Number.isFinite(plot.crop.expectedUnits)) {
      const expected = crop.yield * (Number(plot.yieldFactor) || 1);
      plot.crop.expectedUnits = Math.floor(expected) + (stableRoll(`${plot.id}:${plot.crop.plantedAt}`) < expected % 1 ? 1 : 0);
    }
    plot.crop.valuePerUnit = crop.price;
  }
}

function syncStorm() {
  ensureStormSchedule();
  prepareCropForecast();
  const result = updateStorm(state);
  storm.phase = result.stage === 'preparation' ? 'preparing' : result.stage;
  storm.strength = state.storm.intensity || 1;
  if (result.report) {
    state.stormCount = (state.stormCount || 0) + 1;
    state.storm.count = state.stormCount;
    const id = state.storm.id + 1;
    const offset = [-2, -1, 0, 1, 2][Math.floor(stableRoll(`interval:${id}`) * 5)];
    const gap = 8 + offset;
    const strength = [1, 2, 2, 3][(id - 1) % 4];
    state.storm = {
      id, seed: Math.floor(stableRoll(`storm:${id}`) * 0xffffffff) || 1,
      intensity: strength, impactAt: state.storm.impactAt + gap * 1440,
      settledId: 0, stage: 'calm', result: result.report, count: state.stormCount,
      nextDay: state.dayId + gap,
    };
    state.shelter.exposureMinutes = 0;
    state.shelter.lastCheckedMinutes = state.gameMinutes;
    flash(`风暴清算：货物损失 ${result.report.totals.valueLost} 金，维修 ${result.report.totals.repairCost} 金`);
  } else if (result.transition?.to === 'warning' || result.transition?.to === 'preparation' || result.transition?.to === 'impact') {
    flash(result.prompt);
    if (result.transition.to === 'preparation' && boatTrip) {
      releaseMarineTarget();
      boatTrip.phase = 'return'; boatTrip.elapsed = 0;
      boatTrip.fromX = heroBoat.position.x; boatTrip.fromZ = heroBoat.position.z;
      boatTrip.toX = BOAT_MOOR.x; boatTrip.toZ = BOAT_MOOR.z;
      flash('台风将近：立即回港，远海采集已取消');
    }
  }
  if (result.changed) saveState(state);
  return result;
}

const HUD_WIDTH = 1024;
const HUD_HEIGHT = 740;
const MOBILE_HUD_HEIGHT = 1060;
const HUD_TOP = 12;
const hudCanvas = offscreen(HUD_WIDTH, HUD_HEIGHT);
const hudContext = hudCanvas.getContext('2d');
const hudViewCanvas = offscreen(HUD_WIDTH, HUD_HEIGHT);
const hudTexture = new THREE.CanvasTexture(hudViewCanvas);
hudTexture.colorSpace = THREE.SRGBColorSpace;
const hudSprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: hudTexture, transparent: true, depthTest: false }));
hudSprite.center.set(.5, 0);
hudScene.add(hudSprite);
let hudWidth = 0;
let hudHeight = 0;
let hudButtons = [];
let hudOpen = state.shelter.inside;
let hudPanelRect = null;
let hudNavRect = null;
let hudContentOffset = 0;
let statusRect = null;
let hudLayout = null;
let hudScroll = 0;
let hudScrollPage = null;
let hudScrollGeometry = null;
const statusCanvas = offscreen(768, 146);
const statusTexture = new THREE.CanvasTexture(statusCanvas);
statusTexture.colorSpace = THREE.SRGBColorSpace;
const statusSprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: statusTexture, transparent: true, depthTest: false }));
statusSprite.center.set(0, 1);
hudScene.add(statusSprite);
const statusAlertCanvas = offscreen(1000, 88);
const statusAlertTexture = new THREE.CanvasTexture(statusAlertCanvas);
statusAlertTexture.colorSpace = THREE.SRGBColorSpace;
const statusAlertSprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: statusAlertTexture, transparent: true, depthTest: false, depthWrite: false }));
statusAlertSprite.center.set(0, 1); statusAlertSprite.visible = false; hudScene.add(statusAlertSprite);
let statusAlertRect = null;
function paintStatusAlert(text) {
  statusAlertSprite.visible = Boolean(text) && !hudOpen && !voyage?.mapOpen;
  statusAlertRect = null;
  if (!statusAlertSprite.visible) return;
  const rect = statusRect || hudLayout.status;
  const width = Math.min(screen.width - (screen.safeArea?.left || 0) - (screen.safeArea?.right || 0) - 24, 470);
  let ctx = statusAlertCanvas.getContext('2d'); ctx.font = '500 14px sans-serif';
  const lines = wrapCanvasText(ctx, text, width - 24, 3), height = 20 + lines.length * 20;
  resizeTextureCanvas(statusAlertCanvas, statusAlertTexture, Math.round(width * 2), height * 2);
  ctx = statusAlertCanvas.getContext('2d'); ctx.clearRect(0, 0, statusAlertCanvas.width, statusAlertCanvas.height);
  ctx.save(); ctx.scale(2, 2);
  ctx.fillStyle = 'rgba(64,54,34,.88)'; pill(ctx,1,1,width-2,height-2,12); ctx.fill();
  ctx.fillStyle = UI_COLORS.text; ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.font = '500 14px sans-serif';
  lines.forEach((line, i) => ctx.fillText(line, 12, 20 + i * 20)); ctx.restore();
  statusAlertRect = { x: rect.x, y: rect.y + rect.height + 8, width, height };
  statusAlertSprite.scale.set(width, height, 1);
  statusAlertSprite.position.set(rect.x, screen.height - rect.y - rect.height - 8, .1);
  statusAlertTexture.needsUpdate=true;
}
const feedbackCanvas = offscreen(800, 160);
const feedbackTexture = new THREE.CanvasTexture(feedbackCanvas);
feedbackTexture.colorSpace = THREE.SRGBColorSpace;
const feedbackSprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: feedbackTexture, transparent: true, depthTest: false }));
feedbackSprite.visible = false;
feedbackSprite.center.set(0, 1);
feedbackSprite.renderOrder = 20;
hudScene.add(feedbackSprite);
const feedbackAnchor = new THREE.Vector3();
const feedbackFoot = new THREE.Vector3();
const actorScreenCorners = [-.4,.4].flatMap(x => [0,1.96].flatMap(y => [-.36,.36].map(z => new THREE.Vector3(x,y,z))));
const actorScreenPoint = new THREE.Vector3();
function avatarScreenBounds() {
  avatar.updateWorldMatrix(true, false);
  let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity;
  for (const corner of actorScreenCorners) {
    actorScreenPoint.copy(corner).applyMatrix4(avatar.matrixWorld).project(camera);
    if (actorScreenPoint.z < -1 || actorScreenPoint.z > 1) continue;
    const x = (actorScreenPoint.x + 1) * screen.width / 2, y = (1 - actorScreenPoint.y) * screen.height / 2;
    left = Math.min(left, x); right = Math.max(right, x); top = Math.min(top, y); bottom = Math.max(bottom, y);
  }
  return Number.isFinite(left) ? { x: left - 6, y: top - 4, width: right - left + 12, height: bottom - top + 8 } : null;
}
const navCanvas = offscreen(1000, 112);
const navTexture = new THREE.CanvasTexture(navCanvas);
navTexture.colorSpace = THREE.SRGBColorSpace;
const navSprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: navTexture, transparent: true, depthTest: false }));
navSprite.center.set(.5, 0);
hudScene.add(navSprite);
let navButtons = [];
const fishingCanvas = offscreen(760, 520);
const fishingTexture = new THREE.CanvasTexture(fishingCanvas);
fishingTexture.colorSpace = THREE.SRGBColorSpace;
const fishingSprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: fishingTexture, transparent: true, depthTest: false }));
fishingSprite.visible = false;
fishingSprite.renderOrder = 25;
hudScene.add(fishingSprite);
let fishingRect = null;
let fishingButtons = [];
let fishingForceRect = null;
const restShade = new THREE.Sprite(new THREE.SpriteMaterial({ color: 0x001b24, transparent: true, opacity: .42, depthTest: false }));
restShade.renderOrder = 28;
restShade.visible = false;
hudScene.add(restShade);
const restCanvas = offscreen(640, 280);
const restTexture = new THREE.CanvasTexture(restCanvas);
restTexture.colorSpace = THREE.SRGBColorSpace;
const restSprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: restTexture, transparent: true, depthTest: false }));
restSprite.renderOrder = 29;
restSprite.visible = false;
hudScene.add(restSprite);

function roundRect(context, x, y, width, height, radius) {
  context.beginPath();
  context.roundRect ? context.roundRect(x, y, width, height, radius) : context.rect(x, y, width, height);
  context.fill();
}

function drawFishingPanel() {
  const session = fishingSession || fishingResult;
  fishingSprite.visible = Boolean(session);
  if (!session) { fishingButtons = []; fishingForceRect = null; return; }
  const layout = mobileLayout(screen);
  const mobile = layout.compact;
  const width = Math.min(screen.width - layout.safe.left - layout.safe.right - 16,
    (screen.height - layout.safe.top - layout.safe.bottom - 20) * fishingCanvas.width / fishingCanvas.height,
    mobile ? 500 : 510);
  const height = width * fishingCanvas.height / fishingCanvas.width;
  const target = marineTargets.find((entry) => entry.id === session.targetId);
  const projected = target ? new THREE.Vector3(target.x, water.position.y, target.z).project(camera) : null;
  const targetX = projected ? (projected.x + 1) * screen.width / 2 : screen.width / 2;
  const targetY = projected ? (1 - projected.y) * screen.height / 2 : screen.height / 2;
  fishingRect = {
    x: mobile ? layout.safe.left + (screen.width - layout.safe.left - layout.safe.right - width) / 2 : targetX > screen.width / 2 ? 16 : screen.width - width - 16,
    y: mobile ? layout.safe.top + (screen.height - layout.safe.top - layout.safe.bottom - height) / 2 : screen.height - height - 14,
    width, height,
  };
  fishingSprite.scale.set(width, height, 1);
  fishingSprite.position.set(fishingRect.x + width / 2, screen.height - fishingRect.y - height / 2, 0);
  const ctx = fishingCanvas.getContext('2d');
  ctx.clearRect(0, 0, 760, 520);
  ctx.fillStyle = 'rgba(4, 40, 49, .95)';
  roundRect(ctx, 5, 5, 750, 510, 26);
  ctx.strokeStyle = '#dfc47f'; ctx.lineWidth = 3;
  ctx.strokeRect(15, 15, 730, 490);
  if (session.phase === 'result') {
    fishingButtons = []; fishingForceRect = null;
    const resultText = (text, y, size, color, weight = '') => {
      ctx.fillStyle = color; ctx.font = `${weight}${mobile ? Math.max(36, size) : size}px sans-serif`;
      wrapCanvasText(ctx, text, 680).slice(0, 2).forEach((line, row) => ctx.fillText(line, 38, y + row * (mobile ? 42 : 34)));
    };
    ctx.textAlign = 'left';
    resultText(`${SEAFOOD[session.speciesId].label} · ${session.success ? '收竿成功' : '本次垂钓结束'}`, 72, 35, '#fff1c8', '700 ');
    resultText(session.explanation, 143, 27, '#ffdb82');
    resultText(`垂钓消耗：体力 ${session.penalty.stamina} · ${session.penalty.minutes} 游戏分钟`, mobile ? 244 : 212, 25, '#c4ebe0');
    resultText(session.tip, mobile ? 311 : 294, 25, '#c4ebe0');
    resultText(session.success ? `鱼获估值 ${session.value} 金${session.journalNew ? ' · 海岛手记新增' : ''}`
      : '换一处可见鱼群，稍后可继续垂钓', mobile ? 404 : 350, 25, '#c4ebe0');
    resultText('点击任意位置关闭，继续操作', 466, 25, '#fff1c8');
    fishingTexture.needsUpdate = true;
    return;
  }
  const bite = session.phase === 'bite';
  const reeling = session.phase === 'reeling';
  ctx.textAlign = 'left';
  ctx.fillStyle = '#fff1c8'; ctx.font = '700 35px sans-serif';
  ctx.fillText(`${SEAFOOD[session.speciesId].label} · ${reeling ? '收线中' : bite ? '鱼已咬钩' : session.phase === 'casting' ? '抛竿中' : '等待咬钩'}`, 38, 63, 680);
  ctx.font = '24px sans-serif'; ctx.fillStyle = bite ? '#ffdb82' : '#c4ebe0';
  ctx.fillText(reeling ? `鱼的动作：${({ light: '猛冲，轻收', steady: '平游，稳收', strong: '松劲，快收' })[fishingCue(session)]} · ${session.assisted ? '辅助收线中' : '让指针留在绿色区域'}`
    : bite ? '浮漂下沉！在黄色时间条结束前提竿' : '观察水面浮漂；未咬钩就提竿会惊走鱼', 38, 105, 680);
  fishingButtons = [];
  fishingForceRect = null;
  const button = (id, x, y, w, h, label, fill) => {
    if (mobile) { h = Math.max(h, 44 * fishingCanvas.width / width); w = Math.max(w, 44 * fishingCanvas.width / width); }
    ctx.fillStyle = fill; roundRect(ctx, x, y, w, h, 18);
    ctx.fillStyle = '#fff8df'; ctx.font = '700 31px sans-serif'; ctx.textAlign = 'center';
    ctx.fillText(label, x + w / 2, y + h / 2 + 11, w - 20);
    fishingButtons.push({ id, x, y, width: w, height: h });
  };
  if (reeling) {
    const guide = fishingForceGuide(session);
    ctx.textAlign = 'left'; ctx.font = '23px sans-serif'; ctx.fillStyle = '#e6f4e5';
    ctx.fillText(`收线 ${Math.floor(session.progress * 100)}%`, 40, 145);
    ctx.fillText(`鱼线受力 ${Math.floor(session.strain * 100)}%`, 402, 145);
    ctx.fillStyle = '#276467'; roundRect(ctx, 40, 158, 315, 20, 10);
    ctx.fillStyle = '#9bdf9d'; roundRect(ctx, 40, 158, Math.max(7, 315 * session.progress), 20, 10);
    ctx.fillStyle = '#276467'; roundRect(ctx, 402, 158, 315, 20, 10);
    ctx.fillStyle = session.strain > .72 ? '#ee8163' : '#e3bf69';
    roundRect(ctx, 402, 158, Math.max(7, 315 * session.strain), 20, 10);
    ctx.fillStyle = '#fff1c8'; ctx.font = '700 28px sans-serif';
    ctx.fillText(`力度 ${Math.round(session.forceValue * 100)}%`, 40, 221);
    ctx.fillStyle = '#a9ddc5'; ctx.font = '23px sans-serif';
    ctx.fillText(`安全 ${Math.round(guide.min * 100)}–${Math.round(guide.max * 100)}%`, 247, 221);
    ctx.fillText(`${guide.seconds.toFixed(1)}秒后 ${FISHING_FORCE_LABELS[guide.next]}`, 508, 221);
    button('force-down', 38, 234, 92, 92, '−5%', '#356f72');
    button('force-up', 632, 234, 92, 92, '+5%', '#356f72');
    fishingForceRect = { x: 145, y: 234, width: 470, height: 92 };
    ctx.fillStyle = '#725e51'; roundRect(ctx, 145, 261, 470, 35, 17);
    ctx.fillStyle = '#68bc93'; roundRect(ctx, 145 + 470 * guide.min, 261, 470 * (guide.max - guide.min), 35, 12);
    ctx.fillStyle = '#d9f8be'; ctx.fillRect(145 + 470 * guide.target - 2, 264, 4, 29);
    const knob = 145 + session.forceValue * 470;
    ctx.fillStyle = '#fff8de'; roundRect(ctx, knob - 9, 251, 18, 56, 8);
    ctx.textAlign = 'center'; ctx.fillStyle = '#c1d5c9'; ctx.font = '22px sans-serif';
    for (const percent of [0, 25, 50, 75, 100]) ctx.fillText(`${percent}%`, 145 + percent / 100 * 470, 345);
    for (const [index, force] of ['light', 'steady', 'strong'].entries()) {
      button(force, 38 + index * 171, 365, 154, 92, `${FISHING_FORCE_LABELS[force]} ${[25, 50, 75][index]}%`,
        !session.assisted && session.force === force ? '#b28636' : '#287974');
    }
    button('assist', 551, 365, 171, 92, session.assisted ? '辅助：开' : '辅助：关', session.assisted ? '#a07839' : '#466568');
    ctx.textAlign = 'left'; ctx.fillStyle = '#b9d8ce'; ctx.font = '22px sans-serif';
    ctx.fillText('拖动力度条 · ← / → 微调 · 开辅助可跟随推荐', 40, 496, 680);
  } else {
    const remaining = bite ? Math.max(0, 1 - (session.elapsed - session.biteAt) / session.biteWindow) : 0;
    ctx.fillStyle = '#1d5a60'; roundRect(ctx, 40, 144, 680, 31, 15);
    ctx.fillStyle = bite ? '#f7cb65' : '#75b3b2';
    roundRect(ctx, 40, 144, 680 * (bite ? Math.max(.02, remaining) : .16), 31, 15);
    ctx.textAlign = 'left'; ctx.font = '23px sans-serif'; ctx.fillStyle = '#d7e9df';
    ctx.fillText(bite ? `剩余 ${(session.biteWindow * remaining).toFixed(1)} 秒 · 看准浮漂下沉再提竿`
      : '轻晃不是咬钩；突然下沉才是时机', 40, 218, 680);
    button('hook', 35, 283, 475, 105, '提竿 · 空格 / 点击', bite ? '#b88737' : '#427c78');
    button('cancel', 525, 283, 200, 105, '收竿取消', '#745f53');
    ctx.textAlign = 'left'; ctx.fillStyle = '#b9d8ce'; ctx.font = '24px sans-serif';
    ctx.fillText('咬钩后可拖动力度条，也可开启辅助收线', 40, 443, 680);
  }
  fishingTexture.needsUpdate = true;
}

function dragFishingForce(x, y, continuing = false) {
  if (!fishingSession || fishingSession.phase !== 'reeling' || !fishingRect || !fishingForceRect) return false;
  const localX = (x - fishingRect.x) * fishingCanvas.width / fishingRect.width;
  const localY = (y - fishingRect.y) * fishingCanvas.height / fishingRect.height;
  const track = fishingForceRect;
  if (!continuing && (localX < track.x || localX > track.x + track.width || localY < track.y || localY > track.y + track.height)) return false;
  setFishingForce(fishingSession, (localX - track.x) / track.width);
  drawFishingPanel(); return true;
}

function handleFishingTap(x, y) {
  if (fishingResult && !fishingSession) {
    fishingResult = null; fishingSprite.visible = false; fishingRect = null;
    drawHud();
    return true;
  }
  if (!fishingSession) return false;
  if (!fishingRect || x < fishingRect.x || x > fishingRect.x + fishingRect.width
    || y < fishingRect.y || y > fishingRect.y + fishingRect.height) return true;
  const localX = (x - fishingRect.x) * fishingCanvas.width / fishingRect.width;
  const localY = (y - fishingRect.y) * fishingCanvas.height / fishingRect.height;
  if (dragFishingForce(x, y)) return true;
  const hit = fishingButtons.find((item) => localX >= item.x && localX <= item.x + item.width
    && localY >= item.y && localY <= item.y + item.height);
  if (hit) handleFishingAction(hit.id);
  return true;
}

function updateRestTransition(deltaSeconds) {
  if (!restTransition) {
    restShade.visible = false;
    restSprite.visible = false;
    return;
  }
  const rest = restTransition;
  const { progress, minutes: reachedMinutes, stamina } = restProgress(rest, deltaSeconds, PASSIVE_TIME_SCALE * 2);
  advanceGameTime(state, Math.max(0, reachedMinutes - state.gameMinutes) / 2);
  state.stamina = Math.min(rest.recoveryCap, Math.max(state.stamina, stamina));
  if (rest.elapsed - rest.savedElapsed >= 10) { saveState(state); rest.savedElapsed = rest.elapsed; }
  const ctx = restCanvas.getContext('2d');
  ctx.clearRect(0, 0, 640, 280);
  ctx.fillStyle = 'rgba(5,39,44,.97)';
  roundRect(ctx, 12, 12, 616, 256, 25);
  ctx.strokeStyle = '#ead38f';
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.fillStyle = '#fff1d0';
  ctx.textAlign = 'center';
  ctx.font = '700 34px sans-serif';
  ctx.fillText(rest.kind === 'nap' ? '小屋 · 白天小憩' : '小屋 · 夜间休息', 320, 72);
  const hour = Math.floor(state.clockHours), minute = Math.floor((state.clockHours % 1) * 60);
  ctx.font = '24px sans-serif';
  ctx.fillText(`第${state.dayId}天 ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} · 体力 ${Math.floor(state.stamina)}/100`, 320, 113);
  ctx.fillStyle = '#1b5658';
  roundRect(ctx, 64, 138, 512, 22, 11);
  ctx.fillStyle = '#b7dda2';
  roundRect(ctx, 64, 138, Math.max(10, 512 * progress), 22, 11);
  const compact = mobileLayout(screen).compact;
  const secondsLeft = Math.ceil((rest.targetMinutes - reachedMinutes) / (PASSIVE_TIME_SCALE * 2));
  ctx.fillStyle = '#fff1d0'; ctx.font = '20px sans-serif';
  ctx.fillText(`剩余 ${Math.ceil(rest.targetMinutes - reachedMinutes)} 游戏分钟 · 现实 ${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`, 320, compact ? 170 : 188);
  ctx.fillStyle = '#397459'; roundRect(ctx, compact ? 80 : 200, compact ? 175 : 205, compact ? 480 : 240, compact ? 97 : 48, 12);
  ctx.fillStyle = '#ffffff'; ctx.font = `700 ${compact ? 30 : 24}px sans-serif`; ctx.fillText('起床 · 保留已恢复体力', 320, compact ? 233 : 237);
  restTexture.needsUpdate = true;
  restShade.visible = !hudOpen && !voyage?.mapOpen;
  restSprite.visible = !voyage?.mapOpen;
  const inset = restSprite.scale.y / 2 + 12;
  restSprite.position.y = THREE.MathUtils.clamp(compact ? screen.height / 2 : screen.height * (hudOpen ? .8 : .2),
    (screen.safeArea?.bottom || 0) + inset, screen.height - (screen.safeArea?.top || 0) - inset);
  if (progress < 1) return;
  finishBedRest(false);
}

function finishBedRest(early = true) {
  if (!restTransition) return;
  const rest = restTransition;
  avatar.position.copy(rest.standingPosition);
  avatar.rotation.copy(rest.standingRotation);
  restTransition = null;
  busyAction = false;
  restShade.visible = false;
  restSprite.visible = false;
  flash(early ? `已经起床 · 恢复 ${Math.floor(Math.max(0, state.stamina - rest.initialStamina))} 体力`
    : rest.interruptedByStorm ? '休息到台风预警时刻；请立即检查防灾措施'
    : rest.kind === 'nap' ? '小憩结束，体力已恢复'
      : '休息到清晨 06:00，体力已恢复');
  saveState(state);
  drawHud();
}

function drawHud() {
  ensureDailyOrders(state);
  clearFinishedPlayerGoal(state);
  playerGoalHint = describePlayerGoal(state);
  hudLayout = mobileLayout(screen, 8);
  const narrowHud = hudLayout.compact;
  hudNavRect = hudLayout.nav;
  resizeTextureCanvas(navCanvas, navTexture, Math.round(hudNavRect.width * 2), Math.round(hudNavRect.height * 2));
  navSprite.scale.set(hudNavRect.width, hudNavRect.height, 1);
  navSprite.position.set(hudNavRect.x + hudNavRect.width / 2, screen.height - hudNavRect.y - hudNavRect.height, 0);
  const tallPage = hudPage === 'logistics' || hudPage === 'inventory';
  const contentHeight = hudPage === 'journal' ? islandLifePanelHeight(narrowHud, islandLifeMode)
    : hudPage === 'kitchen' ? (narrowHud ? COOKING_MOBILE_HEIGHT : HUD_HEIGHT)
    : hudPage === 'shop' ? (narrowHud ? MOBILE_HUD_HEIGHT : HUD_HEIGHT)
    : hudPage === 'logistics' ? (narrowHud ? 690 : 700)
      : hudPage === 'inventory' ? (narrowHud ? 1660 : 650)
        : hudPage === 'activity' ? (state.shelter.inside ? (narrowHud ? 820 : 420) : narrowHud ? 1150 : HUD_HEIGHT)
          : hudPage === 'water' ? (narrowHud ? 690 : 560)
        : narrowHud ? (tallPage ? 820 : 690) : (tallPage ? 560 : 420);
  hudContentOffset = narrowHud ? 0 : hudCanvas.height - contentHeight;
  if (hudScrollPage !== hudPage) { hudScroll = 0; hudScrollPage = hudPage; }
  if (narrowHud) {
    hudWidth = Math.min(hudLayout.panel.width, hudLayout.panel.height * HUD_WIDTH / 170);
    hudHeight = Math.min(contentHeight * hudWidth / HUD_WIDTH, hudLayout.panel.height);
    const panelX = hudLayout.landscape ? screen.width - hudLayout.safe.right - hudWidth - 8
      : hudLayout.safe.left + (screen.width - hudLayout.safe.left - hudLayout.safe.right - hudWidth) / 2;
    hudPanelRect = { ...hudLayout.panel, x: panelX, width: hudWidth, y: hudNavRect.y - hudHeight - 8, height: hudHeight };
    hudSprite.scale.set(hudWidth, hudHeight, 1);
    hudSprite.position.set(hudPanelRect.x + hudWidth / 2, screen.height - hudPanelRect.y - hudHeight, 0);
    hudScrollGeometry = panelScrollGeometry(contentHeight, hudHeight, hudWidth / HUD_WIDTH, hudScroll);
    hudScroll = hudScrollGeometry.scroll;
  }
  if (hudNavRect && !narrowHud) {
    const defaultWidth = Math.min(screen.width - (narrowHud ? 16 : 32), narrowHud ? 440 : 520);
    const availableHeight = Math.max(280, hudNavRect.y - (narrowHud ? 16 : 20));
    hudWidth = hudPage === 'shop' || hudPage === 'kitchen' || hudPage === 'journal'
      ? Math.min(screen.width - (narrowHud ? 16 : 32), narrowHud ? 440 : 900,
        availableHeight * HUD_WIDTH / contentHeight)
      : defaultWidth;
    hudHeight = hudWidth * hudCanvas.height / HUD_WIDTH;
    const panelX = screen.width < 600 || hudPage === 'shop' || hudPage === 'kitchen' || hudPage === 'journal' ? (screen.width - hudWidth) / 2 : screen.width - hudWidth - 16;
    const visiblePanelHeight = hudWidth * contentHeight / HUD_WIDTH;
    const panelY = hudNavRect.y - visiblePanelHeight - 8;
    hudPanelRect = { x: panelX, y: panelY, width: hudWidth, height: visiblePanelHeight };
    hudSprite.scale.set(hudWidth, hudHeight, 1);
    hudSprite.position.set(panelX + hudWidth / 2, screen.height - hudNavRect.y + 8, 0);
  }
  const ctx = hudContext;
  ctx.clearRect(0, 0, hudCanvas.width, hudCanvas.height);
  const clock = Number.isFinite(state.clockHours) ? state.clockHours : 6;
  const clockLabel = `${String(Math.floor(clock)).padStart(2, '0')}:${String(Math.floor((clock % 1) * 60)).padStart(2, '0')}`;
  const tide = tideAt(Number.isFinite(Number(state.gameHours)) ? Number(state.gameHours) : 6);
  const tideLabel = ({ low: '低潮', rising: '涨潮', high: '高潮', falling: '退潮' })[tide] || '平潮';
  const day = state.dayId || 1;
  const stamina = Number.isFinite(state.stamina) ? state.stamina : 100;
  const stormInfo = stormStatus(state);
  const stormLabel = stormReportOpen && state.storm?.result ? `上场台风清算 · ${state.storm.result.intensity}级` :
    storm.phase === 'warning' ? `台风预警 · ${storm.strength}级` :
    storm.phase === 'preparing' ? `台风准备 · ${storm.strength}级 · ${state.shelter.inside && !state.shelter.doorOpen ? '已关门避险' : '请进屋关门'}` : storm.phase === 'impact'
      ? state.shelter.inside && !state.shelter.doorOpen ? '台风来袭 · 屋内避险' : '台风来袭 · 暴露在风雨中' :
      `第${day}天 ${clockLabel} · ${tideLabel}`;
  statusRect = hudLayout.status;
  const { width: statusWidth, height: statusHeight } = statusRect;
  resizeTextureCanvas(statusCanvas, statusTexture, Math.round(statusWidth * 2), Math.round(statusHeight * 2));
  const top = statusCanvas.getContext('2d');
  top.save(); top.scale(2, 2);
  drawIslandStatus(top, { day, clock: clockLabel, gold: state.gold, tide: tideLabel, narrow: narrowHud, hour: clock, width: statusWidth, height: statusHeight });
  top.restore();
  statusSprite.scale.set(statusWidth, statusHeight, 1);
  statusSprite.position.set(statusRect.x, screen.height - statusRect.y, 0);
  const forecast = stormReportOpen && state.storm?.result
    ? `　货物损失 ${state.storm.result.totals.valueLost} 金 · ${state.storm.result.totals.itemsLost} 件 · 维修 ${state.storm.result.totals.repairCost} 金`
    : ['warning', 'preparation'].includes(stormInfo.stage)
    ? `　距登陆 ${Math.ceil(stormInfo.minutesToImpact / 60)} 小时`
    : state.storm.result ? `　上场损失 ${state.storm.result.totals.itemsLost} 件 · 维修 ${state.storm.result.totals.repairCost} 金`
      : `鱼获 ${state.fishValue || 0} 金 · 体力 ${Math.floor(stamina)}/100 · 走路或稍歇可恢复`;
  const marineStatus = voyage?.engaged ? (voyage.sailing ? `远航探索 · 船体 ${Math.round(state.boatDurability)}/100 · 已发现 ${state.voyage.discovered.length - 1}/4 座岛 · 海图规划航线` : '岛屿探索 · 点击资源采集，重新登船可返回家园') : marineMode === 'dive'
    ? `潜水剩余 ${Math.ceil(diveSecondsLeft)} 秒 · 当前 ${waterDepthMetres(avatar.position.x, avatar.position.z).toFixed(1)} 米 / 限深 ${diveDepthLimit()} 米`
    : marineMode === 'swim' ? '水面游泳 · 活动页可下潜 · 点击沙滩返回' : '';
  const urgent = storm.phase !== 'calm' || stormReportOpen ? `${stormLabel}${forecast}`
    : marineMode === 'dive' ? marineStatus : '';
  paintStatusAlert(urgent);
  statusTexture.needsUpdate = true;
  statusSprite.visible = !voyage?.mapOpen && !(hudOpen && ['shop', 'journal'].includes(hudPage));

  const tabNames = [['activity', '活动'], ['voyage', '航海'], ['farm', '农田'], ['inventory', '背包'], ['kitchen', '烹饪'], ['trees', '林木'], ['storm', stormReportOpen ? '返回防灾' : '防灾'], ['logistics', '维修仓储']];
  const nav = navCanvas.getContext('2d');
  navButtons = drawIslandNavigation(nav, tabNames, { cols: hudLayout.cols, rows: hudLayout.rows, active: hudOpen ? hudPage : null, pixelRatio: 2 });
  navTexture.needsUpdate = true;
  navSprite.visible = !fishingSession && !fishingResult && !voyage?.mapOpen;
  hudSprite.visible = hudOpen && !fishingSession && !fishingResult && !voyage?.mapOpen;
  if (!hudOpen) { hudButtons = []; return; }
  ctx.save();
  ctx.translate(0, hudContentOffset);
  ctx.fillStyle = 'rgba(5,48,53,.92)';
  const panelBottom = contentHeight - 16;
  roundRect(ctx, 8, 8, 1008, panelBottom, 28);
  ctx.strokeStyle = 'rgba(238,223,137,.62)';
  ctx.lineWidth = 3;
  ctx.strokeRect(24, 24, 976, panelBottom - (hudPage === 'journal' ? 20 : 32));
  ctx.fillStyle = '#fff6d9';
  ctx.font = `700 ${narrowHud ? 48 : 35}px sans-serif`;
  ctx.fillText(hudPage === 'shop' ? `码头交易 · ${tradeMode === 'buy' ? '购入' : '售出'}`
    : hudPage === 'activity' && state.shelter.inside ? '海风小屋'
    : hudPage === 'journal' ? '海岛手记'
    : hudPage === 'water' ? '淡水收集场' : tabNames.find(([id]) => id === hudPage)?.[1] || '操作', 45, 75);
  ctx.font = `${narrowHud ? 35 : 24}px sans-serif`;
  ctx.fillStyle = '#b8f4df';
  const homeSummary = islandLifeSummary(state);
  const pageSubtitle = hudPage === 'journal' ? `${homeSummary.title} Lv.${homeSummary.level} · 心愿 ${homeSummary.completed}/${homeSummary.total} · 收集 ${homeSummary.journalCount}/${homeSummary.journalTotal}`
    : hudPage === 'inventory' ? '选择物品查看价值、等级与状态'
    : hudPage === 'activity' && state.shelter.inside ? state.shelter.doorOpen ? '门已打开 · 关门避险或选择出屋' : '门已关闭 · 屋内避险，可休息或开门'
    : hudPage === 'kitchen' ? `金币 ${state.gold} · 学习、制作与升级菜谱 · 在小屋旁烹饪`
    : hudPage === 'water' ? '淡水用于浇田 · 饮水可即时恢复体力'
    : hudPage === 'shop' ? `金币 ${state.gold} · ${tradeMode === 'buy' ? '点击商品直接购买' : '点击货物售出该类全部库存'}`
      : hudPage === 'activity' ? playerGoalHint ? `${playerGoalHint.title} · ${playerGoalHint.text}` : nextPlayerAction(state).text
        : '点击场景可移动 / 交互';
  ctx.fillText(wrapCanvasText(ctx, pageSubtitle, 930, 1)[0] || '', 45, 112);
  ctx.fillStyle = '#94702f';
  roundRect(ctx, 889, 34, 82, 67, 16);
  ctx.fillStyle = '#fff6d9';
  ctx.font = '700 36px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('×', 930, 79);
  ctx.textAlign = 'start';

  const columns = [30, 520];
  const buttonWidth = 474;
  const sellableIds = [...Object.keys(SEAFOOD), ...Object.keys(CROPS), 'legacyFish'];
  const sellableCount = sellableIds.reduce((sum, id) => sum + (state.inventory[id] || 0), 0);
  const produceCount = Object.keys(CROPS).reduce((sum, id) => sum + (state.inventory[id] || 0), 0);
  const edibleCount = [...Object.keys(DISHES), ...Object.keys(CROPS), ...Object.keys(SEAFOOD)]
    .reduce((sum, id) => sum + (state.inventory[id] || 0), state.food || 0);
  const shopCards = hudPage === 'shop' ? drawTradePanel(ctx, narrowHud, hudContentOffset) : [];
  const report = state.storm?.result;
  const reportSummary = summarizeStormReport(report);
  const itemLabels = { crab: '沙蟹', reefFish: '珊瑚鱼', silverJack: '银鲹', lobster: '刺龙虾', pearlOyster: '珍珠贝', legacyFish: '旧鱼获', sweetPotato: '地瓜', tomato: '番茄', corn: '玉米', pineapple: '菠萝', pumpkin: '南瓜' };
  const dailyOrders = state.orderBoard?.dayId === state.dayId ? state.orderBoard.offers : [];
  const orderCards = dailyOrders.map((order, index) => {
    const requirements = order.requirements.map(({ itemId, quantity }) =>
      `${itemLabels[itemId] || itemId}×${quantity}（${state.inventory[itemId] || 0}）`).join(' + ');
    return {
      id: `order-deliver:${order.id}`, x: columns[index], y: 200,
      title: order.completed ? `今日订单 ${index + 1} · 已交付` : `交付订单 ${index + 1} · 额外奖励 ${order.bonus} 金`,
      detail: order.completed ? '已结清，不会重复扣货或发奖' : requirements,
      color: order.completed ? '#397459' : '#467b77',
    };
  });
  const stockRowsLost = reportSummary.inventory.rows;
  const stockBreakdown = stockRowsLost.slice(0, 3).map((row) => `${itemLabels[row.itemId] || row.itemId}×${row.lost}`).join('、') || '无库存损失';
  const assetBreakdown = ['boat', 'dock', 'hut'].map((key) => {
    const row = reportSummary.assets[key];
    return `${({ boat: '船', dock: '码头', hut: '小屋' })[key]} ${row.durabilityAfter ?? '—'}/100`;
  }).join(' · ');
  const protectionRows = reportSummary.protections;
  const protectionNames = { windNet: '防风网', drainage: '排水渠', anchor: '锚绳', waterproofCabinet: '防水柜', waveBarrier: '护栏', shutters: '门窗' };
  const protectionBreakdown = protectionRows.slice(0, 3).map(([key, row]) => `${protectionNames[key] || key} ${row.before}→${row.after}`).join(' · ') || '本场未安装防护';
  const plantedPlots = (state.plots || []).filter((plot) => plot.crop && CROPS[plot.crop.id]);
  const readyPlots = plantedPlots.filter((plot) => plot.crop.growthMinutes >= CROPS[plot.crop.id].minutes);
  const nextCrop = plantedPlots.filter((plot) => plot.crop.growthMinutes < CROPS[plot.crop.id].minutes)
    .sort((a, b) => (CROPS[a.crop.id].minutes - a.crop.growthMinutes) - (CROPS[b.crop.id].minutes - b.crop.growthMinutes))[0];
  const thirstyCrop = plantedPlots.find((plot) => plot.crop.growthMinutes < CROPS[plot.crop.id].minutes
    && state.gameMinutes >= plot.crop.wateredUntil);
  const nextProgress = nextCrop ? Math.min(1, nextCrop.crop.growthMinutes / CROPS[nextCrop.crop.id].minutes) : 0;
  const nextStage = nextProgress < .39 ? '萌芽' : nextProgress < .65 ? '长叶' : '结果';
  const selectedTree = state.trees.find((tree) => tree.id === selectedTreeId) || state.trees[0];
  const treeIndex = state.trees.indexOf(selectedTree);
  const treeProgress = Math.floor((selectedTree?.ageMinutes || 0) / TREE_MATURE_MINUTES * 100);
  const treeStatus = selectedTree?.stage === 'mature' ? `成树 · 健康 ${selectedTree.health}/100`
    : selectedTree?.stage === 'growing' ? `树苗 · 成长 ${treeProgress}% · 健康 ${selectedTree.health}/100`
      : selectedTree?.stage === 'stump' ? '已砍伐 · 可重新种植' : '空树位 · 可种植';
  const farmDetail = thirstyCrop
    ? `需浇水 · ${cropLabel(thirstyCrop.crop.id)}暂停生长`
    : nextCrop
    ? `可收 ${readyPlots.length} 块 · ${cropLabel(nextCrop.crop.id)}${nextStage} ${Math.floor(nextProgress * 100)}%`
    : readyPlots.length ? `可收 ${readyPlots.length} 块 · 点击田垄收获` : '空地点击田垄播种';
  const selectedPlot = selectFarmPlot(state, selectedPlotId);
  const plotHint = plotGuidance(state, selectedPlot, selectedSeed);
  const selectedPlotIndex = state.plots.indexOf(selectedPlot);
  const stormReportCards = report ? [
    { id: 'storm-report-card-crops', x: columns[0], y: 200, title: `田间损失 · ${reportSummary.plots.unitsLost} 件`, detail: `${reportSummary.plots.count} 块受灾田 · 价值 ${reportSummary.plots.valueLost} 金`, color: '#94702f' },
    { id: 'storm-report-card-stock', x: columns[1], y: 200, title: `库存损失 · ${reportSummary.inventory.unitsLost} 件`, detail: `${stockBreakdown}${stockRowsLost.length > 3 ? ' 等' : ''} · 价值 ${reportSummary.inventory.valueLost} 金`, color: '#765b8b' },
    { id: 'storm-report-card-assets', x: columns[0], y: 316, title: `维修费用 · ${reportSummary.totals.repairCost} 金`, detail: assetBreakdown, color: '#397459' },
    { id: 'storm-report-card-protection', x: columns[1], y: 316, title: `防护减损(估算) · ${reportSummary.totals.protectedUnitsExpected.toFixed(1)} 件`, detail: `${protectionBreakdown}${protectionRows.length > 3 ? ' 等' : ''} · 建筑减伤 ${reportSummary.totals.durabilitySaved} 耐久`, color: '#467b77' },
    { id: 'storm-report-card-shelter', title: !report.shelter ? '旧战报 · 无人员避险记录'
      : report.shelter.protected ? '人员避险 · 全程安全' : `人员暴露 · ${report.shelter.exposureMinutes} 分钟`,
    detail: `避险取决于进屋并关门 · 体力损失 ${report.shelter?.staminaLost || 0}`, color: '#765b8b' },
  ] : [
    { id: 'storm-report-card-crops', x: columns[0], y: 200, title: '尚无清算记录', detail: '第一次台风结算后，这里会保存详细报告', color: '#467b77' },
    { id: 'storm-report-card-stock', x: columns[1], y: 200, title: '损失按实结算', detail: '作物、背包与仓库分别列出，不重复扣除', color: '#397459' },
    { id: 'storm-report-card-assets', x: columns[0], y: 316, title: '耐久与维修费', detail: '显示船、码头、小屋灾前/灾后状态', color: '#94702f' },
    { id: 'storm-report-card-protection', x: columns[1], y: 316, title: '防护效果', detail: '显示每项已安装设施的磨损变化', color: '#765b8b' },
  ];
  const journalCard = { id: 'activity-journal', title: `海岛手记 · ${homeSummary.title} Lv.${homeSummary.level}`,
    detail: `心愿 ${homeSummary.completed}/${homeSummary.total} · 手记 ${homeSummary.journalCount}/${homeSummary.journalTotal} · 海风来信`, color: '#397459' };
  const definitions = hudPage === 'shop' || hudPage === 'kitchen' || hudPage === 'journal' ? [] : hudPage === 'water' ? [
    { id: 'water-drink', title: `饮用 1 L · 水壶 ${state.freshwater.canteen}/${state.freshwater.canteenCapacity} L`,
      detail: '即时恢复 25 体力 · 体力已满时不消耗淡水', color: '#287f74' },
    { id: 'water-refill', title: `从储水桶补水 · ${state.freshwater.tank}/${state.freshwater.tankCapacity} L`,
      detail: '需走到淡水收集场 · 农田浇水也消耗水壶淡水', color: '#397459' },
    { id: 'water-still', title: state.freshwater.stillLevel ? '太阳能蒸馏器 · 已安装' : '安装太阳能蒸馏器 · 80 金',
      detail: state.freshwater.stillLevel ? '晴雨皆可每日额外产出 2 L' : '前往交易站购买 · 降低旱日缺水风险', color: '#94702f' },
    { id: 'water-info', title: `明日预计收集 ${dailyWaterYield(state.dayId + 1, state.freshwater.stillLevel)} L`,
      detail: rainForDay(state.dayId + 1) ? '明日有降雨 · 06:00 汇入储水桶' : '明日晴朗 · 仅露水与蒸馏产水', color: '#467b77' },
  ] : hudPage === 'logistics' ? [
    { id: 'repair-boat', x: 30, y: 200, title: `修理船只 · ${state.boatDurability}/100`, detail: `修复至 100 耐久　${repairQuote(state, 'boat')?.cost || 0} 金`, color: '#467b77' },
    { id: 'repair-dock', x: 356, y: 200, title: `修理码头 · ${state.dockDurability}/100`, detail: `修复至 100 耐久　${repairQuote(state, 'dock')?.cost || 0} 金`, color: '#397459' },
    { id: 'repair-hut', x: 682, y: 200, title: `修理小屋 · ${state.hutDurability}/100`, detail: `修复至 100 耐久　${repairQuote(state, 'hut')?.cost || 0} 金`, color: '#765b8b' },
    ...['boat', 'dock', 'hut'].map((asset) => {
      const quote = woodPatchQuote(state, asset);
      const label = ({ boat: '船只', dock: '码头', hut: '小屋' })[asset];
      return { id: `wood-patch-${asset}`, title: `木材抢修${label} · ${state[`${asset}Durability`]}/100`,
        detail: quote.ok ? `修 ${quote.points} 耐久需 ${quote.wood} 木 · 现有 ${state.wood} 木` : '40 耐久以上请用金币修理', color: '#8a6539' };
    }),
    { id: 'store-all', x: 30, y: 316, title: '存入背包货物', detail: `${Object.values(state.inventory).reduce((sum, n) => sum + n, 0)} 件 · 全部存入仓库`, color: '#94702f' },
    { id: 'retrieve-all', x: 356, y: 316, title: '取出仓库货物', detail: `${Object.values(state.storage).reduce((sum, n) => sum + n, 0)} 件 · 全部取回背包`, color: '#94702f' },
  ] : hudPage === 'orders' ? [
    ...orderCards,
    ...(dailyOrders.length === 1 ? [{ id: 'order-info-next', x: columns[1], y: 200, title: '每日一份混合订单', detail: '无线电商店 Lv.3 可增加第二单', color: '#397459' }] : []),
    { id: 'order-info-pay', x: columns[0], y: 316, title: '交付收益', detail: '按商品正常售价结算，另加订单奖励', color: '#94702f' },
    { id: 'order-info-refresh', x: columns[1], y: 316, title: `已完成 ${state.ordersCompleted} 份`, detail: '每日 06:00 刷新；未完成订单不罚款', color: '#765b8b' },
  ] : hudPage === 'activity' && state.shelter.inside ? [
    ...hutControls(state.shelter),
    journalCard,
    { id: 'activity-rest', title: '在小屋休息', detail: restPreviewText(), color: '#765b8b' },
  ] : hudPage === 'activity' ? [
    ...hutControls(state.shelter),
    journalCard,
    { id: 'activity-voyage', title: `${questSummary(state).title} · ${questSummary(state).lit ? '航标已点亮' : `印记 ${questSummary(state).seals}/3`}`, detail: questSummary(state).text, color: '#a37b3a' },
    { id: 'activity-catch', x: columns[0], y: 200, title: `${SEAFOOD[selectedSeafood]?.label || '沙蟹'} · ${SEAFOOD[selectedSeafood]?.tool === 'rod' ? '抛竿垂钓' : '开始采集'}`,
      detail: SEAFOOD[selectedSeafood]?.tool === 'rod' ? '看浮漂提竿 · 跟随鱼的动作控制收线力度'
        : `${SEAFOOD[selectedSeafood]?.depth || '0–2 m'} · 成功率约 ${Math.round((SEAFOOD[selectedSeafood]?.chance || .85) * 100)}%`, color: '#287f74' },
    { id: 'activity-cycle', x: columns[1], y: 200, title: '切换海产目标', detail: '沙蟹 · 珊瑚鱼 · 银鲹 · 龙虾 · 珍珠贝', color: '#467b77' },
    { id: 'sell', x: columns[0], y: 316, title: '返回码头出售', detail: `${sellableCount} 件可售货物 · 果蔬 ${produceCount} 件`, color: '#397459' },
    { id: 'activity-meal', title: `烹饪与菜谱 · 食物 ${edibleCount} 件`,
      detail: `已做好 ${edibleDishCount(state)} 份 · 食用可即时恢复体力`, color: '#8a6539' },
    marineMode === 'land'
      ? { id: 'activity-rest', x: columns[1], y: 316, title: state.shelter.inside ? '在小屋休息' : '回小屋休息 · 先开门进入',
        detail: restPreviewText(), color: '#765b8b' }
      : { id: 'activity-dive', x: columns[1], y: 316,
        title: marineMode === 'dive' ? '浮出水面' : '下潜探索',
        detail: marineMode === 'dive' ? `剩余 ${Math.ceil(diveSecondsLeft)} 秒 · 自动上浮保命`
          : state.diveLevel ? `潜水服 Lv.${state.diveLevel} · 氧气 ${Math.floor(state.oxygen)}` : '徒手限深 3 米 · 闭气 12 秒',
        color: '#286e8a' },
    ...(state.hasNet ? [{ id: 'activity-net', title: `高级渔网 · 今日 ${state.netUsesDay}/3 次`,
      detail: '先选珊瑚鱼或银鲹 · 一网双次捕获判定', color: '#287f74' }] : []),
  ] : hudPage === 'trees' ? [
    { id: 'tree-chop', x: columns[0], y: 200, title: `砍伐椰树 · ${selectedTree?.stage === 'mature' ? '可砍伐' : '未成熟'}`, detail: '成熟树产 2–4 木材 · 消耗 8 体力', color: '#94702f' },
    { id: 'tree-plant', x: columns[1], y: 200,
      title: state.inventory.palmSeed ? `种植椰树 · 椰树种子 ${state.inventory.palmSeed} 枚`
        : state.storage.palmSeed ? `取回椰树种子 · 仓库 ${state.storage.palmSeed} 枚` : '购买椰树种子 · 10 金',
      detail: state.inventory.palmSeed ? '在空树位或树桩种植 · 约两天长成'
        : state.storage.palmSeed ? '农田种子不能种树 · 点击前往仓库' : '农田种子不能种树 · 点击前往商店', color: '#397459' },
    { id: 'tree-tend', x: columns[0], y: 316, title: '浇水养护', detail: '每日一次 · 加速 6 小时并恢复健康', color: '#287f74' },
    { id: 'tree-next', x: columns[1], y: 316, title: `查看下一棵 · ${treeIndex + 1}/${state.trees.length}`, detail: `当前 ${treeStatus} · 也可点场景中的树`, color: '#467b77' },
  ] : hudPage === 'farm' ? [
    { id: 'farm-go', x: columns[0], y: 200, title: `${plotHint.action} · 第 ${selectedPlotIndex + 1} 块田 · ${plotHint.status}`, detail: plotHint.detail, color: '#397459' },
    { id: 'farm-seed', x: columns[1], y: 200, title: `切换种子 · ${cropLabel(selectedSeed)} ×${state.inventory[`${selectedSeed}Seed`] || 0}`, detail: '缺货时田地自动改用背包中的农田种子', color: '#94702f' },
    { id: 'farm-buy-seed', x: columns[0], y: 316, title: '购买 1 包种子', detail: `${cropLabel(selectedSeed)} ${CROPS[selectedSeed].seedPrice} 金 · 前往码头`, color: '#94702f' },
    { id: 'farm-help', x: columns[1], y: 316, title: `切换田块 · ${selectedPlotIndex + 1}/${state.plots.length}`, detail: `已种 ${plantedPlots.length}/${state.plots.length} · ${farmDetail}`, color: '#287f74' },
  ] : hudPage === 'storm' && stormReportOpen ? stormReportCards : hudPage === 'storm' ? [
    ...hutControls(state.shelter),
    { id: 'defense-net', x: columns[0], y: 200, title: '防风网 · 60 金', detail: '一组保护最多 6 块田', color: '#397459' },
    { id: 'defense-drain', x: columns[1], y: 200, title: '排水渠 · 80 金', detail: '减轻台风水损与日常潮害', color: '#397459' },
    { id: 'defense-anchor', x: columns[0], y: 316, title: '加固锚绳 · 70 金', detail: '船只必须回港并完成安装', color: '#467b77' },
    { id: 'storm-report-open', x: columns[1], y: 316, title: '上次台风 · 查看明细', detail: '作物、库存、建筑与防护磨损', color: '#94702f' },
  ] : [];
  if (!['activity', 'farm', 'shop', 'inventory', 'kitchen', 'trees', 'storm', 'logistics', 'orders', 'water', 'journal'].includes(hudPage)) hudPage = 'activity';
  let cards = definitions.map(({ id, title, detail, color }, index) => {
    const denseActivity = !narrowHud && hudPage === 'activity' && definitions.length > 8;
    const x = narrowHud ? 30 : columns[index % 2];
    const y = narrowHud ? 160 + index * 218 : 135 + Math.floor(index / 2) * (denseActivity ? 116 : 135);
    const cardWidth = narrowHud ? 964 : buttonWidth;
    const cardHeight = narrowHud ? 205 : denseActivity ? 100 : 119;
    ctx.fillStyle = color;
    roundRect(ctx, x, y, cardWidth, cardHeight, 22);
    ctx.strokeStyle = 'rgba(255,246,217,.5)';
    ctx.lineWidth = 2;
    ctx.strokeRect(x + 3, y + 3, cardWidth - 6, cardHeight - 6);
    ctx.textAlign = 'center';
    ctx.fillStyle = '#fff6d9';
    ctx.font = `700 ${narrowHud ? 48 : 25}px sans-serif`;
    ctx.fillText(title, x + cardWidth / 2, y + (narrowHud ? 62 : denseActivity ? 40 : 52), cardWidth - 24);
    ctx.font = `${narrowHud ? 42 : 20}px sans-serif`;
    ctx.fillStyle = '#d7f1df';
    if (narrowHud) wrapCanvasText(ctx, detail, cardWidth - 48).forEach((line, row) =>
      ctx.fillText(line, x + cardWidth / 2, y + 119 + row * 53));
    else ctx.fillText(detail, x + cardWidth / 2, y + (denseActivity ? 77 : 86), cardWidth - 24);
    ctx.textAlign = 'start';
    return { id, x, y: y + hudContentOffset, width: cardWidth, height: cardHeight };
  });
  if (hudPage === 'inventory') {
    const inventoryUi = drawInventoryPanel(ctx, state, {
      mode: inventoryMode, page: inventoryPage, selectedId: selectedInventoryId,
      narrow: narrowHud, offset: hudContentOffset,
    });
    inventoryPage = inventoryUi.page;
    selectedInventoryId = inventoryUi.selectedId;
    cards = inventoryUi.buttons;
  }
  if (hudPage === 'shop') cards = shopCards;
  if (hudPage === 'journal') cards = drawIslandLifePanel(ctx, state, {
    mode: islandLifeMode, selectedProjectId: selectedHomeProject, narrow: narrowHud, offset: hudContentOffset,
  }).buttons;
  if (hudPage === 'kitchen') cards = drawCookingPanel(ctx, state, {
    selectedId: selectedRecipeId, narrow: narrowHud, offset: hudContentOffset,
    progress: cookingJob ? cookingJob.elapsed / cookingJob.duration : null, labels: itemLabels, page: kitchenPage,
  });
  ctx.restore();
  hudButtons = [{ id: 'close-panel', x: 889, y: hudContentOffset + 34, width: 82, height: 67 }, ...cards];
  if (narrowHud) {
    // Crop the scrollable body, preserving the header and touch-sized controls.
    const actualHeight = Math.max(contentHeight, 180 + definitions.length * 218,
      ...cards.map(card => card.y + card.height + 20));
    hudHeight = Math.min(actualHeight * hudWidth / HUD_WIDTH, hudLayout.panel.height);
    hudPanelRect.y = hudNavRect.y - hudHeight - 8; hudPanelRect.height = hudHeight;
    hudSprite.scale.set(hudWidth, hudHeight, 1);
    hudSprite.position.set(hudPanelRect.x + hudWidth / 2, screen.height - hudPanelRect.y - hudHeight, 0);
    hudScrollGeometry = panelScrollGeometry(actualHeight, hudHeight, hudWidth / HUD_WIDTH, hudScroll);
    hudScroll = hudScrollGeometry.scroll;
    const geometry = hudScrollGeometry;
    resizeTextureCanvas(hudViewCanvas, hudTexture, HUD_WIDTH, Math.ceil(geometry.visible));
    const view = hudViewCanvas.getContext('2d');
    view.clearRect(0, 0, HUD_WIDTH, hudViewCanvas.height);
    view.fillStyle = 'rgba(5,48,53,.96)'; view.fillRect(0, 0, HUD_WIDTH, hudViewCanvas.height);
    view.drawImage(hudCanvas, 0, 0, HUD_WIDTH, geometry.header, 0, 0, HUD_WIDTH, geometry.header);
    view.drawImage(hudCanvas, 0, geometry.header + hudScroll, HUD_WIDTH, geometry.body,
      0, geometry.header, HUD_WIDTH, geometry.body);
    view.fillStyle = '#9b7538'; roundRect(view, 854, 0, 170, 150, 12);
    view.fillStyle = '#fff6d9'; view.font = '700 46px sans-serif'; view.textAlign = 'center'; view.fillText('关闭', 939, 92);
    if (geometry.footer) {
      const y = geometry.visible - geometry.footer;
      view.fillStyle = '#194f50'; view.fillRect(0, y, HUD_WIDTH, geometry.footer);
      view.fillStyle = '#d9ecd7'; view.font = '40px sans-serif'; view.fillText('滑动查看更多', 512, y + 89);
      for (const [x, title] of [[0, '↑'], [854, '↓']]) {
        view.fillStyle = '#337a70'; roundRect(view, x, y, 170, 150, 12);
        view.fillStyle = '#fff6d9'; view.font = '700 56px sans-serif'; view.fillText(title, x + 85, y + 96);
      }
    }
  } else {
    resizeTextureCanvas(hudViewCanvas, hudTexture, HUD_WIDTH, hudCanvas.height);
    const view = hudViewCanvas.getContext('2d');
    view.clearRect(0, 0, hudViewCanvas.width, hudViewCanvas.height);
    view.drawImage(hudCanvas, 0, 0);
  }
  hudTexture.needsUpdate = true;
}

function flash(text) {
  feedbackText = String(text);
  feedbackDuration = Math.min(9000, Math.max(2800, Array.from(feedbackText).length * 100));
  feedbackPending = true;
  messageUntil = Date.now() + feedbackDuration;
  characterTipPaintKey = '';
  drawHud();
}

function positionFeedback() {
  characterTipRect = characterTipAction = null;
  feedbackSprite.visible = false;
  if (!avatar?.visible || accountDialogOpen || !foreground || voyage?.mapOpen || restTransition || fishingSession || fishingResult) return;
  const hasFeedback = feedbackText && (feedbackPending || Date.now() < messageUntil);
  const idle = !hasFeedback && !hudOpen && !busyAction && !cookingJob && !bedApproach && !hutCrossing && !boatTrip;
  const goal = idle ? playerGoalHint : null;
  const action = idle && !voyage?.sailing ? nearbyAction || (goal ? playerGoalAction : null) : null;
  if (!hasFeedback && !action && !goal) return;
  feedbackAnchor.set(0, 1.96, 0);
  avatar.localToWorld(feedbackAnchor);
  feedbackAnchor.project(camera);
  feedbackFoot.copy(avatar.position).project(camera);
  if (feedbackAnchor.z < -1 || feedbackAnchor.z > 1) return;
  const anchor = { x: (feedbackAnchor.x + 1) * screen.width / 2, y: (1 - feedbackAnchor.y) * screen.height / 2 };
  const pads = screen.isTouch && !hudOpen && !voyage?.sailing ? touchPadLayout(screen, hudNavRect) : null;
  const profile = !screen.isWeChat ? { x: screen.width - (screen.safeArea?.right || 0) - 62, y: (screen.safeArea?.top || 0) + 8, width: 54, height: 54 } : null;
  const compass = !hudOpen && !bedApproach ? explorationLayout(screen, {
    statusRect: statusAlertRect ? { ...statusRect, height: statusAlertRect.y + statusAlertRect.height - statusRect.y } : statusRect,
  }).compass : null;
  const ctx = feedbackCanvas.getContext('2d');
  const layout = layoutCharacterTip({ screen, anchor, text: hasFeedback ? feedbackText
    : goal ? `${goal.title} · ${goal.progress || ''}\n${goal.text}` : '',
    actionLabel: action ? interactionName(action) : '', pixelRatio: 2, maxLines: 4,
    measureText: (text, font) => { ctx.font = font; return ctx.measureText(text); },
    avatarRect: avatarScreenBounds(),
    statusRect, navRect: hudNavRect, panelRect: hudOpen ? hudPanelRect : null,
    controlsRect: voyage?.controlsRect, obstacles: [statusAlertRect, profile, compass, pads?.move, pads?.action, ...cameraUi.rects].filter(Boolean) });
  if (!layout) return;
  // A full-screen panel must not consume the reading time before the tip is visible.
  if (hasFeedback && feedbackPending) { messageUntil = Date.now() + feedbackDuration; feedbackPending = false; }
  const key = JSON.stringify([layout.lines, layout.actionLines, layout.keyLabel, layout.rect.width, layout.rect.height, layout.font]);
  if (key !== characterTipPaintKey) {
    resizeTextureCanvas(feedbackCanvas, feedbackTexture, layout.canvas.width, layout.canvas.height);
    drawCharacterTip(feedbackCanvas.getContext('2d'), layout);
    feedbackTexture.needsUpdate = true; characterTipPaintKey = key;
  }
  characterTipRect = layout.rect; characterTipAction = action;
  feedbackSprite.visible = true;
  feedbackSprite.scale.set(layout.rect.width, layout.rect.height, 1);
  feedbackSprite.position.set(layout.rect.x, screen.height - layout.rect.y, .5);
  feedbackSprite.material.opacity = hasFeedback ? Math.min(1, (messageUntil - Date.now()) / 350) : 1;
}

const cropOptions = Object.entries(CROPS).map(([id, crop]) => ({ id, seed: `${id}Seed`, name: crop.label, minutes: crop.minutes, yield: crop.yield, value: crop.price }));
const shopCatalog = [
  ['sweetPotatoSeed', '地瓜种子', '12 小时成熟 · 2 枚'], ['tomatoSeed', '番茄种子', '24 小时成熟 · 2 枚'],
  ['cornSeed', '玉米种子', '36 小时成熟 · 3 枚'], ['pineappleSeed', '菠萝种子', '48 小时成熟 · 2 枚'],
  ['pumpkinSeed', '南瓜种子', '72 小时成熟 · 2 枚'], ['diveSupply', '潜水耗材包', '出海潜水的必备消耗品'],
  ['palmSeed', '椰树种子', '10 金 · 可在树桩或空树位种植'],
  ['rod2', '鱼竿 Lv.2', '解锁银鲹 · 提竿窗口更宽'], ['rod3', '鱼竿 Lv.3', '扩大提竿容错 · 收线更稳'],
  ['shovel2', '铁锹 Lv.2', '播种省体力 · 可扩至 8 田'], ['shovel3', '铁锹 Lv.3', '播种更省力 · 需度过首场台风'],
  ['net', '高级渔网', '双次判定 · 每日最多 3 网'], ['dive1', '潜水服 Lv.1', '解锁龙虾 · 需铁船与礁区探索'],
  ['dive2', '潜水服 Lv.2', '解锁珍珠贝 · 需快艇'], ['ironBoat', '小铁船', '解锁外海 · 木船换购补差'],
  ['speedBoat', '快艇', '航程减半 · 需码头 Lv.2 与两场台风'], ['dock2', '码头 Lv.2', '开放快艇升级 · 需度过首场台风'],
  ['windNet', '防风网', '保护最多 6 块田'], ['drainage', '排水渠', '减轻风暴积水与潮害'],
  ['anchor', '加固锚绳', '减轻已回港船只损伤'], ['waterproofCabinet', '防水柜', '保护仓库中选定货物'],
  ['waveBarrier', '消浪护栏', '在海中浮标处安装 · 降低码头风暴损伤'], ['windowReinforcement', '门窗加固件', '降低小屋风暴损伤'],
  ['raisedBed', '高畦改造', '选择一块低地田免受日常潮峰影响'], ['backpack', '背包扩容', '容量 20 → 30'],
  ['warehouse', '仓库扩容', '容量 40 → 80'], ['hut2', '小屋升级', '增加储物空间'], ['shop2', '无线电商店 Lv.2', '累计售货 150 金后解锁订单'],
  ['shop3', '无线电商店 Lv.3', '完成 5 份订单后每日双订单'], ['lamp', '手提灯', '夜间照明，不改变鱼获概率'],
  ['solarStill', '太阳能蒸馏器', '每日额外收集 2 L 淡水'],
  ['lowPlot', '开垦低地田', '便宜扩田 · 需承担潮汐风险'], ['highPlot', '开垦高地田', '扩建安全农田 · 数量有限'],
].map(([id, name, description]) => ({ id, name, description }));

function tradePageSize() { return mobileLayout(screen).compact ? 6 : 9; }

function tradeIcon(itemId, mode) {
  const cropId = itemId.endsWith('Seed') && itemId !== 'palmSeed' ? itemId.slice(0, -4) : null;
  const gearIcons = {
    rod2: 'rod', rod3: 'rod', shovel2: 'shovel', shovel3: 'shovel',
    ironBoat: 'boat', speedBoat: 'boat', dock2: 'dock', hut2: 'hut',
    dive1: 'dive', dive2: 'dive', diveSupply: 'dive',
    shop2: 'shop', shop3: 'shop', warehouse: 'warehouse',
    lowPlot: 'lowPlot', highPlot: 'highPlot',
  };
  return {
    icon: cropId || gearIcons[itemId] || (itemId === 'palmSeed' ? 'palmSeed' : itemId === 'legacyFish' ? 'reefFish' : itemId),
    kind: cropId || itemId === 'palmSeed' ? 'seed'
      : mode === 'sell' && SEAFOOD[itemId] ? 'seafood'
        : mode === 'sell' && CROPS[itemId] ? 'crop' : 'gear',
  };
}

function drawTradePanel(ctx, narrow, offset) {
  const buttons = [];
  const perPage = tradePageSize();
  const sellableItems = [...Object.keys(SEAFOOD), ...Object.keys(CROPS), 'legacyFish']
    .filter((id) => (state.inventory[id] || 0) > 0);
  const items = tradeMode === 'buy' ? shopCatalog : sellableItems;
  const pageCount = Math.max(1, Math.ceil(items.length / perPage));
  if (tradeMode === 'buy') shopPage = Math.min(shopPage, pageCount - 1);
  else sellPage = Math.min(sellPage, pageCount - 1);
  const page = tradeMode === 'buy' ? shopPage : sellPage;
  const controlY = narrow ? 160 : 129, controlHeight = narrow ? 150 : 68;
  for (const [mode, title, x] of [['buy', '购入', 30], ['sell', '售出', 266]]) {
    ctx.fillStyle = tradeMode === mode ? '#bc883b' : '#256967';
    roundRect(ctx, x, controlY, 222, controlHeight, 15);
    ctx.fillStyle = '#fff6d9'; ctx.font = `700 ${narrow ? 44 : 29}px sans-serif`; ctx.textAlign = 'center';
    ctx.fillText(title, x + 111, controlY + controlHeight / 2 + 10);
    buttons.push({ id: `shop-mode:${mode}`, x, y: controlY + offset, width: 222, height: controlHeight });
  }
  for (const [id, title, x] of [['shop-prev', '‹', 520], ['shop-next', '›', 851]]) {
    ctx.fillStyle = '#286f70'; roundRect(ctx, x, controlY, narrow ? 150 : 123, controlHeight, 15);
    ctx.fillStyle = '#fff6d9'; ctx.font = '700 42px sans-serif'; ctx.textAlign = 'center';
    ctx.fillText(title, x + 61, controlY + controlHeight / 2 + 14);
    buttons.push({ id, x, y: controlY + offset, width: narrow ? 150 : 123, height: controlHeight });
  }
  ctx.fillStyle = '#fff6d9'; ctx.font = '700 25px sans-serif'; ctx.textAlign = 'center';
  ctx.fillText(`${page + 1} / ${pageCount}`, 748, controlY + controlHeight / 2 + 8);
  ctx.textAlign = 'start';
  if (!items.length) {
    ctx.fillStyle = '#b8f4df'; ctx.font = '700 32px sans-serif';
    ctx.fillText('背包暂无可售货物', 44, 330);
    ctx.font = '25px sans-serif'; ctx.fillText('捕鱼、采集或收获后，到码头交易站出售。', 44, 378);
    return buttons;
  }
  const columns = narrow ? 1 : 3;
  const cardWidth = narrow ? 964 : 310;
  const cardHeight = narrow ? 225 : 155;
  const startY = narrow ? 330 : 211;
  const gapX = narrow ? 16 : 16;
  const gapY = narrow ? 12 : 13;
  items.slice(page * perPage, (page + 1) * perPage).forEach((entry, index) => {
    const itemId = tradeMode === 'buy' ? entry.id : entry;
    const x = 30 + index % columns * (cardWidth + gapX);
    const y = startY + Math.floor(index / columns) * (cardHeight + gapY);
    const quantity = state.inventory[itemId] || 0;
    const price = tradeMode === 'buy' ? SHOP[itemId] : itemId === 'legacyFish'
      ? state.legacyFishValue || 0 : quantity * (SEAFOOD[itemId]?.price ?? CROPS[itemId]?.price ?? 0);
    const title = tradeMode === 'buy' ? entry.name
      : SEAFOOD[itemId]?.label || CROPS[itemId]?.label || '旧鱼获';
    const detail = tradeMode === 'buy' ? entry.description : `库存 ${quantity} 件 · 一次售出全部`;
    const affordable = tradeMode !== 'buy' || state.gold >= price;
    ctx.fillStyle = affordable ? (tradeMode === 'buy' ? '#80602f' : '#2a7160') : '#505a58';
    roundRect(ctx, x, y, cardWidth, cardHeight, 17);
    ctx.strokeStyle = 'rgba(255,246,217,.65)'; ctx.lineWidth = 2;
    ctx.strokeRect(x + 2, y + 2, cardWidth - 4, cardHeight - 4);
    const iconSize = narrow ? 130 : 100;
    drawIcon(ctx, tradeIcon(itemId, tradeMode), x + 10, y + (cardHeight - iconSize) / 2, iconSize);
    const textX = x + (narrow ? 153 : 120);
    const maxTextWidth = cardWidth - (textX - x) - 10;
    ctx.fillStyle = '#fff6d9'; ctx.font = `700 ${narrow ? 46 : 23}px sans-serif`;
    ctx.fillText(title, textX, y + (narrow ? 47 : 43), maxTextWidth);
    ctx.fillStyle = affordable ? '#ffe3a0' : '#e6b6a9';
    ctx.font = `700 ${narrow ? 42 : 23}px sans-serif`;
    ctx.fillText(`${tradeMode === 'buy' ? '价格' : '可得'} ${price} 金`, textX, y + (narrow ? 90 : 79), maxTextWidth);
    ctx.fillStyle = '#d8eee1'; ctx.font = `${narrow ? 36 : 18}px sans-serif`;
    if (narrow) wrapCanvasText(ctx, detail, maxTextWidth).forEach((line, row) => ctx.fillText(line, textX, y + 140 + row * 42));
    else ctx.fillText(detail, textX, y + 109, maxTextWidth);
    if (tradeMode === 'buy' && quantity > 0) {
      ctx.fillStyle = '#c7f3d4'; ctx.font = `${narrow ? 34 : 18}px sans-serif`;
      ctx.fillText(`已拥有 ${quantity}`, textX, y + (narrow ? 214 : 137), maxTextWidth);
    }
    buttons.push({ id: `shop-${tradeMode}:${itemId}`, x, y: y + offset, width: cardWidth, height: cardHeight });
  });
  return buttons;
}

function cropLabel(id) { return CROPS[id]?.label || '地瓜'; }

function beginCapture(speciesId, tool, requestedTarget = null) {
  const spec = SEAFOOD[speciesId];
  const needsBoat = Boolean(spec?.boatTier);
  if (!spec) return;
  const availability = captureAvailability(state, speciesId, tool);
  if (!availability.ok) { flash(captureFailureMessage(availability)); return; }
  const target = requestedTarget && targetAvailable(requestedTarget) ? requestedTarget
    : findMarineTarget(speciesId, needsBoat ? { x: BOAT_MOOR.x, z: BOAT_MOOR.z } : null);
  if (!target || target.speciesId !== speciesId) { flash(`附近没有可见的${spec.label}，等它们回游再试`); return; }
  reserveMarineTarget(target);
  if (needsBoat) {
    if (!walkTo(DOCK_APPROACH.x, DOCK_APPROACH.z, { type: 'trip-start', speciesId, tool, targetId: target.id }, 1.0)) releaseMarineTarget();
    return;
  }
  const shoreCast = tool === 'rod';
  const radius = Math.hypot(target.x, target.z);
  const shoreRadius = shoreCast ? Math.min(radius, shoreRadiusAt(target.x, target.z) - .65) : radius;
  const destination = shoreCast
    ? { x: target.x * shoreRadius / radius, z: target.z * shoreRadius / radius }
    : { x: target.x, z: target.z };
  if (!walkTo(destination.x, destination.z, { type: 'capture', speciesId, tool, targetId: target.id }, shoreCast ? .55 : 1.15)) releaseMarineTarget();
}

function startBoatReturnTrip() {
  if (!heroBoat) return;
  boatTrip = {
    phase: 'return', elapsed: 0, duration: 1.4,
    fromX: heroBoat.position.x, fromZ: heroBoat.position.z,
    toX: BOAT_MOOR.x, toZ: BOAT_MOOR.z,
  };
}

function updateBoatTrip(deltaSeconds) {
  if (!boatTrip || !heroBoat) return;
  boatTrip.elapsed += deltaSeconds;
  const t = Math.min(1, boatTrip.elapsed / boatTrip.duration);
  const eased = t * t * (3 - 2 * t);
  heroBoat.position.x = boatTrip.fromX + (boatTrip.toX - boatTrip.fromX) * eased;
  heroBoat.position.z = boatTrip.fromZ + (boatTrip.toZ - boatTrip.fromZ) * eased;
  const dx = boatTrip.toX - boatTrip.fromX, dz = boatTrip.toZ - boatTrip.fromZ;
  if (Math.hypot(dx, dz) > .01) heroBoat.rotation.y = Math.atan2(-dz, dx);
  seatAvatarInBoat(avatar, heroBoat);
  if (t < 1) return;
  const trip = boatTrip;
  boatTrip = null;
  if (trip.phase === 'outbound') {
    spendActionTime(state, 10);
    state.explored.reef = true;
    completeArrival({ type: 'sea-capture', speciesId: trip.speciesId, tool: trip.tool, targetId: trip.targetId });
    if (!fishingSession) startBoatReturnTrip();
  } else {
    spendActionTime(state, 10);
    state.boatMoored = true;
    resetAvatarSeatedJoints(avatar);
    avatar.rotation.set(0, avatar.rotation.y, 0);
    avatar.position.set(DOCK_APPROACH.x, groundHeightAt(DOCK_APPROACH.x, DOCK_APPROACH.z) + .02, DOCK_APPROACH.z);
    // Keep the catch/failure feedback visible through the short return journey.
    // The result panel stays until the player dismisses it.
    if (!fishingResult && (!feedbackText || Date.now() >= messageUntil)) flash('已返港，鱼获与潜水状态已保存');
    saveState(state);
    drawHud();
  }
}

function openDockTrade(mode = 'buy') {
  hudOpen = false;
  drawHud();
  return walkTo(DOCK_APPROACH.x, DOCK_APPROACH.z, { type: 'open-trade', mode }, .9);
}

function buyAtDock(itemId) {
  const index = shopCatalog.findIndex((item) => item.id === itemId);
  if (index >= 0) shopPage = Math.floor(index / tradePageSize());
  openDockTrade('buy');
}

function installTargets(itemId) {
  if (itemId === 'raisedBed') {
    const ids = state.plots.filter((plot) => plot.elevation === 'low' && !plot.raised).slice(0, 1).map((plot) => plot.id);
    return ids.length ? ids : null;
  }
  if (itemId === 'windNet' || itemId === 'drainage') {
    const ids = state.plots.filter((plot) => !state.defenses.plots.find((entry) => entry.id === plot.id)?.[itemId])
      .slice(0, 6).map((plot) => plot.id);
    return ids.length ? ids : null;
  }
  const field = { anchor: 'anchor', waterproofCabinet: 'waterproofCabinet', waveBarrier: 'waveBarrier', windowReinforcement: 'shutters' }[itemId];
  return field && !state.defenses[field] ? [] : null;
}

function beginInstallStock(itemId) {
  if (!(state.inventory[itemId] > 0)) { flash('背包没有该物资；若在仓库，请先取回'); return false; }
  if (!installTargets(itemId)) { flash('当前没有可安装的位置；多余物资会留在背包'); return false; }
  const destination = itemId === 'waveBarrier' ? WAVE_BARRIER_APPROACH
    : itemId === 'anchor' ? DOCK_APPROACH
    : ['waterproofCabinet', 'windowReinforcement'].includes(itemId) ? HUT_APPROACH
      : { x: plots.garden.pos[0], z: plots.garden.pos[2] };
  hudOpen = false;
  return walkTo(destination.x, destination.z, { type: 'install', itemId });
}

function useInventoryItem(itemId) {
  if (INSTALLABLE_STOCK[itemId]) { beginInstallStock(itemId); return; }
  if (itemId === 'wood') { hudPage = 'logistics'; hudOpen = true; return; }
  if (itemId === 'diveSupply') { hudPage = 'activity'; hudOpen = true; return; }
  if (itemId === 'palmSeed') {
    const tree = state.trees.find((entry) => ['empty', 'stump'].includes(entry.stage) && treeVisuals.has(entry.id));
    if (!tree) { flash('暂无空树位；砍伐成熟树后可在树桩重新种植'); return; }
    selectedTreeId = tree.id;
    hudOpen = false;
    walkTo(tree.x, tree.z, { type: 'tree-work', treeId: tree.id, operation: 'plant' }, 1.2);
    return;
  }
  const cropId = itemId.endsWith('Seed') ? itemId.slice(0, -4) : null;
  if (CROPS[cropId]) {
    const index = state.plots.findIndex((plot) => !plot.crop);
    if (index < 0) { flash('田块已种满，收获后再播种'); return; }
    const root = cropObjects[index];
    if (!root) { flash('田块还在加载，请稍后再试'); return; }
    selectedSeed = cropId;
    hudOpen = false;
    walkTo(root.position.x, root.position.z, { type: 'world', root }, .65);
    return;
  }
  if (DISHES[itemId] || CROPS[itemId] || SEAFOOD[itemId] || itemId === 'food') {
    const result = eatMeal(state, itemId);
    flash(result.ok ? `吃下${result.label} · 恢复 ${Math.floor(result.staminaGain)} 体力`
      : result.reason === 'full' ? '体力已满，无需食用补给' : '这份食物已经用完');
    saveState(state);
    return;
  }
  if (itemId === 'legacyFish') {
    const sellables = [...Object.keys(SEAFOOD), ...Object.keys(CROPS), 'legacyFish']
      .filter((id) => (state.inventory[id] || 0) > 0);
    sellPage = Math.max(0, Math.floor(sellables.indexOf(itemId) / tradePageSize()));
    openDockTrade('sell');
  }
}

function tradeIsInReach() {
  return Math.hypot(avatar.position.x - DOCK_APPROACH.x, avatar.position.z - DOCK_APPROACH.z) <= 2.3;
}

function openIslandLife(mode = null) {
  voyage?.dismiss();
  if (['wishes', 'journal', 'orders'].includes(mode)) { islandLifeMode = mode; hudScroll = 0; }
  hudPage = 'journal'; hudOpen = true;
  drawHud();
}

function queueIslandOrder(orderId) {
  if (hutCrossing || boatTrip || fishingSession || busyAction || cookingJob || restTransition) {
    flash('请先完成当前动作，再交付来信'); return;
  }
  const status = dailyOrderStatus(state, orderId);
  if (!status || status.completed || !status.available) {
    flash(status?.completed ? '这封来信已经交付，奖励已领取' : '货物还未备齐 · 先收集或从仓库取回'); return;
  }
  if (state.voyage.mode !== 'home') { flash('先返航，再到家园心愿板交付来信'); return; }
  if (state.shelter.inside) { flash('先开门走出小屋，再交付来信'); return; }
  if (marineMode === 'dive') surfaceDive();
  hudOpen = false;
  if (walkTo(HOME_BOARD_APPROACH.x, HOME_BOARD_APPROACH.z, { type: 'island-order', orderId }, .35)
    && Math.hypot(avatar.position.x - HOME_BOARD.x, avatar.position.z - HOME_BOARD.z) > 2)
    flash('正前往心愿板 · 到达后交付海风来信');
  drawHud();
}

function handlePageAction(id) {
  if ((avatarVault || voyage?.vaulting) && !id.startsWith('tab-') && id !== 'close-panel') {
    flash('等落地后再操作'); return;
  }
  const viewingIslandLife = id === 'activity-journal' || id === 'life-return' || id.startsWith('life-mode:') || id.startsWith('life-select:')
    || id.startsWith('life-track:') || id.startsWith('life-order-track:');
  if (restTransition && !viewingIslandLife && !['tab-inventory', 'tab-farm', 'tab-voyage', 'close-panel', 'inventory-prev', 'inventory-next', 'farm-help'].includes(id)
    && !id.startsWith('inventory-select:') && !id.startsWith('inventory-mode:')) { flash('休息中可查看信息；先起床再进行操作'); return; }
  if (cookingJob && !viewingIslandLife && !id.startsWith('tab-') && id !== 'close-panel') { flash('正在烹饪，请等待这一份料理完成'); return; }
  if (id === 'activity-journal') { openIslandLife(); return; }
  if (id === 'life-return') { hudPage = 'activity'; hudOpen = true; drawHud(); return; }
  if (id.startsWith('life-mode:')) {
    islandLifeMode = ['journal', 'orders'].includes(id.slice(10)) ? id.slice(10) : 'wishes';
    hudScroll = 0; drawHud(); return;
  }
  if (id.startsWith('life-track:') || id.startsWith('life-order-track:')) {
    const [kind, goalId] = id.startsWith('life-order-track:') ? ['order', id.slice(17)] : id.slice(11).split(':');
    const result = selectPlayerGoal(state, kind, goalId);
    if (result.ok) { hudOpen = false; saveState(state); }
    flash(result.message); return;
  }
  if (id.startsWith('life-order-deliver:')) { queueIslandOrder(id.slice(19)); return; }
  if (id === 'tab-orders') { openIslandLife('orders'); return; }
  if (id.startsWith('life-select:')) { selectedHomeProject = id.slice(12); drawHud(); return; }
  if (id.startsWith('life-contribute:')) {
    if (hutCrossing || boatTrip || fishingSession || busyAction) { flash('请先完成当前动作，再提交材料'); return; }
    const projectId = id.slice(16), status = projectStatus(state, projectId);
    if (!status || status.completed || status.locked || !status.available) {
      flash(status?.completed || status?.locked ? status.detail : '没有可提交的材料；先收集或从仓库取回。'); return;
    }
    if (state.voyage.mode !== 'home') { flash('先返航，再到家园心愿板提交材料'); return; }
    if (state.shelter.inside) { flash('先开门走出小屋，再提交材料'); return; }
    if (marineMode === 'dive') surfaceDive();
    hudOpen = false;
    if (walkTo(HOME_BOARD_APPROACH.x, HOME_BOARD_APPROACH.z, { type: 'home-project', projectId }, .35)
      && Math.hypot(avatar.position.x - HOME_BOARD.x, avatar.position.z - HOME_BOARD.z) > 2)
      flash(`正前往心愿板 · 到达后提交${status.title}材料`);
    drawHud(); return;
  }
  if (id === 'tab-voyage' || id === 'activity-voyage') { voyage?.open(); return; }
  if (id.startsWith('tab-')) {
    voyage?.dismiss();
    if (id === 'tab-storm') stormReportOpen = false;
    const nextPage = id.slice(4);
    hudOpen = hudPage === nextPage ? !hudOpen : true;
    hudPage = nextPage;
    drawHud();
    return;
  }
  if (id === 'close-panel') { hudOpen = false; drawHud(); return; }
  if (id === 'activity-meal') { hudPage = 'kitchen'; hudOpen = true; drawHud(); return; }
  if (id.startsWith('kitchen-')) { handleKitchenAction(id); return; }
  if (id.startsWith('inventory-use:') && (DISHES[id.slice(14)] || CROPS[id.slice(14)] || SEAFOOD[id.slice(14)] || id.slice(14) === 'food')) {
    if (busyAction) { flash('请先完成当前动作'); return; }
    useInventoryItem(id.slice(14)); drawHud(); return;
  }
  if (id === 'water-drink') {
    const result = drinkWater(state);
    flash(result.ok ? `饮水完成 · 恢复 ${Math.floor(result.staminaGain)} 体力`
      : result.reason === 'full' ? '体力已满，无需饮水' : '水壶已空，请到淡水收集场补水');
    saveState(state); drawHud(); return;
  }
  if (id === 'water-info') return;
  if (id.startsWith('inventory-mode:')) {
    inventoryMode = id.slice('inventory-mode:'.length);
    inventoryPage = 0;
    selectedInventoryId = null;
    drawHud();
    return;
  }
  if (id.startsWith('inventory-select:')) {
    selectedInventoryId = id.slice('inventory-select:'.length);
    drawHud();
    return;
  }
  if (id === 'inventory-prev' || id === 'inventory-next') {
    const pages = Math.max(1, Math.ceil(getInventoryRows(state, inventoryMode).length / (mobileLayout(screen).compact ? 4 : 6)));
    inventoryPage = (inventoryPage + (id === 'inventory-next' ? 1 : -1) + pages) % pages;
    selectedInventoryId = null;
    drawHud();
    return;
  }
  if (id === 'hut-door') { activateHutDoor(); return; }
  if (id === 'hut-close') { closeHutDoor(); return; }
  if (id === 'hut-exit') { if (state.shelter.inside && state.shelter.doorOpen) startHutCrossing(false); return; }
  if (storm.phase === 'impact') { flash('台风登陆中，请在小屋内避险'); return; }
  if (state.shelter.inside && !['activity-rest', 'farm-help'].includes(id)) { flash('先开门走出小屋，再进行室外操作'); return; }
  if (id === 'water-refill') {
    const inReach = Math.hypot(avatar.position.x - FRESHWATER_SITE.x, avatar.position.z - FRESHWATER_SITE.z) < 2.2;
    if (inReach) completeArrival({ type: 'water-refill' });
    else { hudOpen = false; walkTo(FRESHWATER_SITE.x + 1.35, FRESHWATER_SITE.z, { type: 'water-refill' }, .9); }
    return;
  }
  if (id === 'water-still') {
    if (state.freshwater.stillLevel) { flash('太阳能蒸馏器已安装，每日额外产出 2 L'); return; }
    buyAtDock('solarStill');
    return;
  }
  if (id.startsWith('inventory-retrieve:')) {
    const itemId = id.slice('inventory-retrieve:'.length);
    hudOpen = false;
    walkTo(HUT_APPROACH.x, HUT_APPROACH.z, { type: 'retrieve-item', itemId });
    return;
  }
  if (id.startsWith('inventory-use:')) {
    useInventoryItem(id.slice('inventory-use:'.length));
    drawHud();
    return;
  }
  if (id === 'tree-next') {
    const index = state.trees.findIndex((tree) => tree.id === selectedTreeId);
    selectedTreeId = state.trees[(index + 1) % state.trees.length].id;
    drawHud(); return;
  }
  if (['tree-chop', 'tree-plant', 'tree-tend'].includes(id)) {
    const tree = state.trees.find((entry) => entry.id === selectedTreeId);
    if (!tree || !treeVisuals.has(tree.id)) { flash('这处树位不可到达，请选择另一棵'); return; }
    const operation = id.slice('tree-'.length);
    if (operation === 'plant' && !state.inventory.palmSeed) {
      const cropSeeds = Object.entries(CROPS).filter(([cropId]) => state.inventory[`${cropId}Seed`] > 0)
        .map(([, crop]) => crop.label);
      if (state.storage.palmSeed) hudPage = 'logistics';
      else {
        buyAtDock('palmSeed');
        flash(cropSeeds.length ? `${cropSeeds.slice(0, 2).join('、')}种子只能种田；请到交易站买椰树种子`
          : '背包没有椰树种子；正前往交易站');
        return;
      }
      hudOpen = true;
      flash('椰树种子在仓库，请先取回背包；农田种子不能种椰树');
      return;
    }
    if (operation === 'chop' && tree.stage !== 'mature') { flash('树还没有长成，暂时不能砍伐'); return; }
    if (operation === 'plant' && !['empty', 'stump'].includes(tree.stage)) { flash('这里已有树木，请选择空树位或树桩'); return; }
    if (operation === 'tend' && !['growing', 'mature'].includes(tree.stage)) { flash('这里没有可养护的树'); return; }
    walkTo(tree.x, tree.z, { type: 'tree-work', treeId: tree.id, operation }, 1.2);
    return;
  }
  if (id === 'shop-next' || id === 'shop-prev') {
    const count = tradeMode === 'buy' ? shopCatalog.length : [...Object.keys(SEAFOOD), ...Object.keys(CROPS), 'legacyFish']
      .filter((itemId) => (state.inventory[itemId] || 0) > 0).length;
    const pages = Math.max(1, Math.ceil(count / tradePageSize()));
    const step = id === 'shop-next' ? 1 : -1;
    if (tradeMode === 'buy') shopPage = (shopPage + step + pages) % pages;
    else sellPage = (sellPage + step + pages) % pages;
    drawHud(); return;
  }
  if (id.startsWith('shop-mode:')) { tradeMode = id.slice('shop-mode:'.length); drawHud(); return; }
  if (id === 'shop-toggle') { tradeMode = tradeMode === 'buy' ? 'sell' : 'buy'; drawHud(); return; }
  if (id.startsWith('shop-buy:')) {
    if (!tradeIsInReach()) { hudOpen = false; flash('请走近码头交易站后再购买'); return; }
    completeArrival({ type: 'buy', itemId: id.slice('shop-buy:'.length), quantity: 1 });
    return;
  }
  if (id.startsWith('shop-sell:')) {
    if (!tradeIsInReach()) { hudOpen = false; flash('请走近码头交易站后再出售'); return; }
    const itemId = id.slice('shop-sell:'.length);
    const result = sellItem(state, itemId);
    flash(result.ok ? `售出${SEAFOOD[itemId]?.label || CROPS[itemId]?.label || '旧鱼获'} ×${result.quantity}，收入 ${result.value} 金`
      : '出售失败：背包没有这类货物');
    saveState(state);
    return;
  }
  if (id === 'shop-empty' || id === 'shop-info') return;
  if (id === 'storm-report-open') { stormReportOpen = true; drawHud(); return; }
  if (id.startsWith('storm-report-card-')) return;
  if (id.startsWith('order-deliver:')) {
    queueIslandOrder(id.slice('order-deliver:'.length)); return;
  }
  else if (id === 'activity-catch') beginCapture(selectedSeafood, SEAFOOD[selectedSeafood]?.tool || 'hand');
  else if (id === 'activity-net') {
    if (selectedSeafood !== 'reefFish' && selectedSeafood !== 'silverJack') {
      flash('渔网适用于珊瑚鱼或银鲹；请先切换海产目标'); return;
    }
    if (state.netUsesDay >= 3) { flash('今天已下 3 网，明天再来'); return; }
    beginCapture(selectedSeafood, 'net');
  }
  else if (id === 'activity-dive') toggleDive();
  else if (id === 'activity-cycle') {
    const species = Object.keys(SEAFOOD);
    selectedSeafood = species[(species.indexOf(selectedSeafood) + 1) % species.length];
    flash(`目标切换为${SEAFOOD[selectedSeafood].label}`);
  }
  else if (id === 'sell') {
    openDockTrade('sell');
  } else if (id === 'activity-rest') {
    requestBedRest();
  } else if (id === 'farm-go') {
    const plot = selectFarmPlot(state, selectedPlotId);
    if (!plot) { flash('田地正在准备中，请稍后再试'); return; }
    const target = state.plots.indexOf(plot), hint = plotGuidance(state, plot, selectedSeed);
    selectedPlotId = plot.id;
    if (hint.operation === 'retrieve-seed') {
      selectedSeed = hint.seedId;
      hudOpen = false;
      walkTo(HUT_APPROACH.x, HUT_APPROACH.z, { type: 'retrieve-item', itemId: hint.itemId });
      return;
    }
    if (hint.operation === 'buy-seed') { buyAtDock(hint.itemId); return; }
    if (hint.operation === 'manage-stock') {
      hudOpen = true; hudPage = 'inventory'; inventoryMode = 'bag'; inventoryPage = 0;
      flash(hint.detail); return;
    }
    if (hint.operation === 'recover') { hudOpen = false; flash(hint.detail); return; }
    if (hint.operation === 'refill-water') { hudOpen = true; hudPage = 'water'; flash(hint.detail); return; }
    const root = cropObjects[target];
    if (root) walkTo(root.position.x, root.position.z, { type: 'world', root }, .65);
    else flash('田地正在准备中，请稍后再试');
  } else if (id === 'farm-seed') {
    const index = cropOptions.findIndex((crop) => crop.id === selectedSeed);
    selectedSeed = cropOptions[(index + 1) % cropOptions.length].id;
    flash(`已选择${cropLabel(selectedSeed)}种子`);
  } else if (id === 'farm-buy-seed') buyAtDock(cropOptions.find((crop) => crop.id === selectedSeed).seed, 1);
  else if (id === 'farm-help') {
    const active = state.plots.indexOf(selectFarmPlot(state, selectedPlotId));
    selectedPlotId = state.plots[(active + 1) % state.plots.length]?.id;
  }
  else if (id === 'defense-net') buyAtDock('windNet');
  else if (id === 'defense-drain') buyAtDock('drainage');
  else if (id === 'defense-anchor') buyAtDock('anchor');
  else if (id === 'defense-cabinet') buyAtDock('waterproofCabinet');
  else if (id === 'repair-boat' || id === 'repair-dock') {
    walkTo(DOCK_APPROACH.x, DOCK_APPROACH.z, { type: 'repair', asset: id === 'repair-boat' ? 'boat' : 'dock' });
  } else if (id === 'repair-hut') {
    walkTo(HUT_APPROACH.x, HUT_APPROACH.z, { type: 'repair', asset: 'hut' });
  } else if (id.startsWith('wood-patch-')) {
    const asset = id.slice('wood-patch-'.length);
    const destination = asset === 'hut' ? HUT_APPROACH : DOCK_APPROACH;
    walkTo(destination.x, destination.z, { type: 'wood-patch', asset });
  } else if (id === 'store-all' || id === 'retrieve-all') {
    const from = id === 'store-all' ? 'inventory' : 'storage';
    const to = id === 'store-all' ? 'storage' : 'inventory';
    walkTo(HUT_APPROACH.x, HUT_APPROACH.z, { type: 'move-stock', from, to });
  }
}

function handleHudTap(x, y) {
  if (restTransition) {
    const localX = (x - (restSprite.position.x - restSprite.scale.x / 2)) * 640 / restSprite.scale.x;
    const localY = (y - (screen.height - restSprite.position.y - restSprite.scale.y / 2)) * 280 / restSprite.scale.y;
    const compact = mobileLayout(screen).compact;
    if (restSprite.visible && localX >= (compact ? 80 : 200) && localX <= (compact ? 560 : 440)
      && localY >= (compact ? 175 : 205) && localY <= (compact ? 272 : 253)) { finishBedRest(); return true; }
    if (restSprite.visible && localX >= 0 && localX <= 640 && localY >= 0 && localY <= 280) return true;
  }
  const inside = (rect) => rect && x >= rect.x && x <= rect.x + rect.width && y >= rect.y && y <= rect.y + rect.height;
  let button;
  const inNavHitArea = hudNavRect && x >= hudNavRect.x && x <= hudNavRect.x + hudNavRect.width
    && y >= hudNavRect.y - 6 && y <= hudNavRect.y + hudNavRect.height + 6;
  if (inNavHitArea) {
    const localX = (x - hudNavRect.x) * navCanvas.width / hudNavRect.width;
    const localY = (y - hudNavRect.y) * navCanvas.height / hudNavRect.height;
    button = navButtons.find(({ x: bx, y: by, width, height }) => localX >= bx && localX <= bx + width && localY >= by && localY <= by + height);
    if (!button) return true;
  } else if (hudOpen && inside(hudPanelRect)) {
    const localX = (x - hudPanelRect.x) * HUD_WIDTH / hudPanelRect.width;
    if (hudLayout.compact) {
      const geometry = hudScrollGeometry;
      const point = panelPoint(hudPanelRect, geometry, x, y);
      if (point.kind === 'header') {
        if (localX >= 854) handlePageAction('close-panel');
        return true;
      }
      if (point.kind === 'footer') {
        if (localX < 170 || localX >= 854) {
          hudScroll += (localX < 170 ? -1 : 1) * geometry.body * .8; drawHud();
        }
        return true;
      }
    }
    const localYs = [y].map((touchY) =>
      (touchY - hudPanelRect.y) * HUD_WIDTH / hudWidth + hudContentOffset + (hudLayout.compact ? hudScroll : 0));
    button = localYs.map((localY) => hudButtons.find(({ x: bx, y: by, width, height }) =>
      localX >= bx && localX <= bx + width && localY >= by && localY <= by + height)).find(Boolean);
    if (!button) return true;
  } else return false;
  if (button.disabled) return true;
  handlePageAction(button.id);
  if (!button.id.startsWith('tab-') && !button.id.startsWith('inventory-')
    && !button.id.startsWith('shop-') && !button.id.startsWith('kitchen-')
    && !button.id.startsWith('life-') && button.id !== 'activity-journal'
    && !(button.id === 'farm-go' && ['inventory', 'water'].includes(hudPage))
    && !keepHutControlsOpen(button.id, state.shelter)
    && !['activity-meal', 'close-panel', 'tree-next', 'tree-plant', 'activity-cycle', 'farm-seed', 'farm-help', 'storm-report-open'].includes(button.id)) {
    hudOpen = false;
  }
  saveState(state);
  drawHud();
  return true;
}

function updateCamera(seconds = 0, time = 0, stormWind = 0) {
  // Cutaway follows entering the hut, independently of screen size and zoom.
  const keepRoof = !state.shelter.inside && !forceCutaway;
  for (const roof of hutRoofs) roof.visible = keepRoof;
  cameraPose = explorationCamera.step(seconds, { position: avatar?.position || { x: HUT_SPAWN.x, y: 2, z: HUT_SPAWN.z },
    heading: voyage?.sailing ? voyage.heading : avatar?.rotation.y || 0,
    moving: Boolean(avatarTarget || voyage?.walking || voyage?.sailing && state.voyage.speed > .1),
    sailing: Boolean(voyage?.sailing || boatTrip || fishingSession?.fromBoat), inside: state.shelter.inside,
    overview: voyage?.overviewFocus || { x: 0, z: 0, radius: 14 }, viewport: screen });
  azimuth = cameraPose.yaw;
  camera.aspect = screen.width / screen.height;
  camera.fov = cameraPose.fov; camera.near = cameraPose.near; camera.far = cameraPose.far;
  const shake = ['impact','preparing'].includes(storm.phase) ? stormWind : 0;
  camera.position.set(cameraPose.position.x + Math.sin(time * 1.7) * shake * .018,
    cameraPose.position.y + Math.sin(time * 1.3) * shake * .012,
    cameraPose.position.z + Math.cos(time * 1.5) * shake * .018);
  camera.lookAt(cameraPose.target.x, cameraPose.target.y, cameraPose.target.z);
  camera.updateProjectionMatrix();
  camera.updateMatrixWorld();
  cameraOcclusion.update(camera.position, avatar?.position || cameraPose.target, cameraPose.mode === 'follow' && Boolean(avatar?.visible));
}

function resize() {
  screen = viewport();
  renderer.setPixelRatio(screen.dpr);
  renderer.setSize(screen.width, screen.height, false);
  updateCamera();
  hudCamera.left = 0; hudCamera.right = screen.width;
  hudCamera.top = screen.height; hudCamera.bottom = 0;
  hudCamera.near = -1; hudCamera.far = 1;
  hudCamera.updateProjectionMatrix();
  restShade.position.set(screen.width / 2, screen.height / 2, 0);
  restShade.scale.set(screen.width, screen.height, 1);
  restSprite.position.set(screen.width / 2, screen.height / 2, 0);
  const restWidth = Math.min(600, screen.width - (screen.safeArea?.left || 0) - (screen.safeArea?.right || 0) - 28,
    (screen.height - (screen.safeArea?.top || 0) - (screen.safeArea?.bottom || 0) - 28) * 640 / 280);
  restSprite.scale.set(restWidth, restWidth * 280 / 640, 1);
  hudLayout = mobileLayout(screen, 8);
  const narrow = hudLayout.compact;
  // The source canvas has no texture; only the cropped view canvas is uploaded.
  const sourceHeight = narrow ? 2400 : HUD_HEIGHT;
  if (hudCanvas.height !== sourceHeight) hudCanvas.height = sourceHeight;
  hudWidth = Math.min(screen.width - (narrow ? 16 : 32), narrow ? 440 : 520);
  hudHeight = hudWidth * hudCanvas.height / HUD_WIDTH;
  const { x: navX, y: navY, width: navWidth, height: navHeight } = hudLayout.nav;
  resizeTextureCanvas(navCanvas, navTexture, Math.round(navWidth * 2), Math.round(navHeight * 2));
  hudNavRect = { x: navX, y: navY, width: navWidth, height: navHeight };
  navSprite.scale.set(navWidth, navHeight, 1);
  navSprite.position.set(navX + navWidth / 2, screen.height - navY - navHeight, 0);
  const panelX = narrow ? (screen.width - hudWidth) / 2 : screen.width - hudWidth - 16;
  const panelY = navY - hudHeight - 8;
  hudPanelRect = { x: panelX, y: panelY, width: hudWidth, height: hudHeight };
  hudSprite.scale.set(hudWidth, hudHeight, 1);
  hudSprite.position.set(panelX + hudWidth / 2, screen.height - panelY - hudHeight, 0);
  statusRect = hudLayout.status;
  const { width: statusWidth, height: statusHeight, x: statusX } = statusRect;
  statusSprite.scale.set(statusWidth, statusHeight, 1);
  statusSprite.position.set(statusX, screen.height - statusRect.y, 0);
  drawHud();
  voyage?.resize();
  if (fishingSession || fishingResult) drawFishingPanel();
}

function worldActionEnabled(root) {
  return root.userData.action?.type !== 'hut-bed' || state.shelter.inside;
}

function resolveWorldPick(x, y) {
  camera.updateMatrixWorld();
  scene.updateMatrixWorld();
  raycaster.setFromCamera(new THREE.Vector2(x / screen.width * 2 - 1, 1 - y / screen.height * 2), camera);
  const selection = pickWorldAction(raycaster, interactive, worldActionEnabled, cameraOcclusion.isHitVisible);
  const point = raycaster.intersectObjects([...walkGroundMeshes, water].filter(isWorldVisible), false)[0]?.point.clone();
  return { root: selection?.root, point };
}

function pick(x, y, selection = resolveWorldPick(x, y)) {
  if (restTransition || bedApproach || fishingSession || cookingJob || avatarVault || voyage?.vaulting) { flash('请先完成当前动作'); return; }
  if (!avatar) { flash('岛屿正在准备，请稍候'); return; }
  const root = isWorldVisible(selection.root) && worldActionEnabled(selection.root) ? selection.root : null;
  const point = selection.point;
  if (root?.userData.action.type === 'hut-bed') { requestBedRest(); return; }
  if (voyage?.pick(root?.userData.action, point)) return;
  if (state.shelter.inside && root?.userData.action.type !== 'hut-door' && root?.userData.action.type !== 'kitchen') {
    if (state.shelter.doorOpen && isOutsideHut(point)) startHutCrossing(false, { x: point.x, z: point.z });
    else flash(state.shelter.doorOpen ? '点击小屋门或屋外地面走出' : '小屋门已关闭，请先点击门打开');
    return;
  }
  if (root) { activateWorldAction(root); return; }
  if (point && !state.shelter.inside) walkTo(point.x, point.z);
}

function activateWorldAction(root) {
  if (!isWorldVisible(root) || !worldActionEnabled(root) || accountDialogOpen || !foreground) return;
  if (restTransition || bedApproach || fishingSession || cookingJob || avatarVault || voyage?.vaulting) { flash('请先完成当前动作'); return; }
  if (root.userData.action.type === 'player-goal') {
    if (playerGoalHint?.kind === 'wish') selectedHomeProject = playerGoalHint.id;
    openIslandLife(playerGoalHint?.kind === 'wish' ? 'wishes' : playerGoalHint?.kind === 'journal' ? 'journal' : 'orders'); return;
  }
  if (voyage?.pick(root.userData.action, root.getWorldPosition(new THREE.Vector3()))) return;
  if (root?.userData.action.type === 'hut-bed') { requestBedRest(); return; }
    if (root.userData.action.type === 'island-life') {
      openIslandLife(['ready', 'available'].includes(root.userData.orderStatus) ? 'orders' : 'wishes'); return;
    }
    if (root.userData.action.type === 'voyage-board') { boardVoyageFromHome(); return; }
    if (root.userData.action.type === 'hut-door') { activateHutDoor(); return; }
    if (root.userData.action.type === 'kitchen') { hudPage = 'kitchen'; hudOpen = true; drawHud(); return; }
    if (state.shelter.inside) { flash('先开门离开小屋'); return; }
    if (root.userData.action.type === 'wave-barrier-site') {
      if (state.defenses.waveBarrier) flash('消浪护栏已安装在海中，正在保护码头');
      else if (state.inventory.waveBarrier > 0) beginInstallStock('waveBarrier');
      else if (state.storage.waveBarrier > 0) flash('消浪护栏在仓库，请先取回背包再来安装');
      else { flash('需要先购买消浪护栏，正前往交易站'); buyAtDock('waveBarrier'); }
      return;
    }
    if (root.userData.action.type === 'trade') { openDockTrade('buy'); return; }
    if (root.userData.action.type === 'freshwater') {
      hudOpen = false;
      walkTo(FRESHWATER_SITE.x + 1.35, FRESHWATER_SITE.z, { type: 'water-station' }, .9);
      return;
    }
    if (root.userData.action.type === 'tree') {
      selectedTreeId = root.userData.action.treeId;
      hudPage = 'trees'; hudOpen = true;
      drawHud();
      return;
    }
    if (root.userData.action.type === 'marine') {
      const target = marineTargets.find((entry) => entry.id === root.userData.action.targetId);
      if (targetAvailable(target)) beginCapture(target.speciesId, SEAFOOD[target.speciesId].tool, target);
      else flash('这只海产已经离开，等它重新出现');
      return;
    }
    if (root.userData.action.type === 'gather' && !root.userData.action.active) { flash('这堆物资还在整理，稍后会补充'); return; }
    const point = root.getWorldPosition(new THREE.Vector3());
    walkTo(point.x, point.z, { type: 'world', root }, root.userData.action.type === 'farmplot' ? .65 : .85);
    return;
}

function interactionName(root) {
  const action = root.userData.action;
  if (action.type === 'island-life') return root.userData.orderStatus === 'ready' ? '来信货物已备齐'
    : root.userData.orderStatus === 'available' ? '查看海风来信' : '查看家园心愿';
  if (action.type === 'player-goal') return '查看追踪目标';
  if (action.type === 'voyage-quest') return questSiteHint(state, action.siteId);
  if (action.type === 'voyage-gather') return voyageGatherLabel(state, action);
  if (action.type === 'farmplot') {
    const plot = state.plots.find(entry => entry.id === action.plotId);
    if (plot) { const hint = plotGuidance(state, plot, selectedSeed); return hint.ready ? hint.action : '查看农田'; }
  }
  return ({ 'hut-door': state.shelter.doorOpen ? '进入小屋' : '打开小屋', 'hut-bed': '休息',
    farmplot: '查看农田', tree: '查看林木', gather: '采集', trade: '交易', freshwater: '收集淡水',
    kitchen: '烹饪', 'island-life': '查看家园心愿', 'voyage-board': '登船', 'voyage-gather': '采集资源', 'voyage-port': '航向浮标',
    marine: '捕捉', 'wave-barrier-site': '安装护栏', build: '建造' })[action.type] || '交互';
}

const nearbyPosition = new THREE.Vector3();
function updateNearbyActions() {
  if (!avatar) return;
  nearbyAction = null; let nearest = 3.4;
  for (const root of interactive) {
    const action = root.userData.action;
    if (!action || !isWorldVisible(root) || !worldActionEnabled(root) || action.active === false || action.type === 'voyage-port'
      || (state.shelter.inside && !['hut-door','hut-bed','kitchen'].includes(action.type))) continue;
    root.getWorldPosition(nearbyPosition);
    const distance = Math.hypot(avatar.position.x-nearbyPosition.x,avatar.position.z-nearbyPosition.z);
    if (distance < nearest) { nearbyAction=root;nearest=distance; }
  }
  // The nearest action is now shown beside the player in screen-space pixels.
  for (const label of worldLabels) label.visible = false;
}

const padRight = new THREE.Vector3(), padForward = new THREE.Vector3();
function updateTouchMovement(seconds) {
  if (avatarVault) return;
  const blocked = !avatar || accountDialogOpen || !foreground || hudOpen || voyage?.mapOpen || voyage?.sailing
    || fishingSession || fishingResult || restTransition || bedApproach || cookingJob || hutCrossing || busyAction
    || boatTrip || state.shelter.inside || storm.phase === 'impact';
  if (blocked) { stopManualMovement(); return; }
  const held = direction => [...movementKeys].some(key => movementKey(key) === direction);
  const keyboard = { x: Number(held('right')) - Number(held('left')),
    y: Number(held('down')) - Number(held('up')) };
  const keyboardLength = Math.hypot(keyboard.x, keyboard.y);
  const stick = keyboardLength ? { x: keyboard.x / keyboardLength, y: keyboard.y / keyboardLength } : touchControls.vector;
  if (Math.hypot(stick.x,stick.y)<.01) {
    voyage?.stopManualMove();
    if (padWalking) { avatarTarget=null;avatarRoute=[];arrivalAction=null;padWalking=false; }
    return;
  }
  camera.updateMatrixWorld(); padRight.setFromMatrixColumn(camera.matrixWorld,0);
  camera.getWorldDirection(padForward);padForward.y=0;padForward.normalize();
  const dx=padRight.x*stick.x-padForward.x*stick.y, dz=padRight.z*stick.x-padForward.z*stick.y;
  if (voyage?.ashore) { voyage.manualMove(dx, dz, seconds); return; }
  // Manual input follows the requested direction and slides at obstacles;
  // click-to-move alone chooses an automatic route around them.
  avatarRoute = []; arrivalAction = null; padWalking = true;
  avatarTarget = new THREE.Vector3(avatar.position.x+dx*1.1,avatar.position.y,avatar.position.z+dz*1.1);
}

function boardVoyageFromHome() {
  if (!voyage || !avatar || boatTrip || fishingSession || busyAction || avatarVault) { flash('请先完成当前动作'); return; }
  if (state.shelter.inside) { flash('先开门离开小屋，再到码头登船'); return; }
  if (['preparing', 'impact'].includes(storm.phase)) { flash('台风将至，请留在家园避险，风浪平息后出航'); return; }
  if (state.boatDurability <= 0 || state.boatTier === 'wreck') { flash('船只已损坏，先维修再出航'); return; }
  hudOpen = false;
  return walkTo(DOCK_APPROACH.x, DOCK_APPROACH.z, { type: 'voyage-board' }, .8);
}

let gesture = null;
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
function activateTapGesture(candidate, mouse = false) {
  if (candidate?.cameraUi) {
    if (isPointerTap(candidate, { mouse, maxDuration: 700 })) cameraUi.tap(candidate.x, candidate.y);
    return;
  }
  if (candidate?.characterTip) {
    if (isPointerTap(candidate, { mouse, maxDuration: 700 })) activateWorldAction(candidate.characterTip);
    return;
  }
  if (candidate?.voyageUi) {
    if (isPointerTap(candidate, { mouse, maxDuration: 700 })) voyage?.tap(candidate.x, candidate.y);
    return;
  }
  if (candidate?.hud) {
    if (isPointerTap(candidate, { mouse, maxDuration: 700 })) handleHudTap(candidate.x, candidate.y);
    return;
  }
  if (isPointerTap(candidate, { mouse })) {
    if (!voyage?.tap(candidate.x, candidate.y) && !handleFishingTap(candidate.x, candidate.y)
      && !handleHudTap(candidate.x, candidate.y)) pick(candidate.x, candidate.y, candidate.worldSelection);
  }
}

function rotateCameraGesture(candidate, point) {
  const { dx, dy, startedDrag } = movePointerGesture(candidate, point);
  if (candidate.moved > TAP_MOVE_TOLERANCE) {
    explorationCamera.rotate(startedDrag ? point.x - candidate.x : dx, startedDrag ? point.y - candidate.y : dy);
  }
}

onTouches({
  start(points) {
    if (points.length === 1 && cameraUi.contains(points[0].x, points[0].y)) {
      gesture = { ...createPointerGesture(points[0]), cameraUi: true }; return;
    }
    if (points.length && characterTipAction && insideRect(characterTipRect, points[0].x, points[0].y)) {
      gesture = { ...createPointerGesture(points[0]), characterTip: characterTipAction }; return;
    }
    if (points.length && (fishingResult || (restSprite.visible && insideRect({ x: restSprite.position.x - restSprite.scale.x / 2,
      y: screen.height - restSprite.position.y - restSprite.scale.y / 2, width: restSprite.scale.x, height: restSprite.scale.y }, points[0].x, points[0].y)))) {
      gesture = { ...createPointerGesture(points[0]), overlay: true }; return;
    }
    if (points.length && voyage?.mapOpen) { gesture = { ...createPointerGesture(points[0]), map: true }; return; }
    if (points.length && !fishingSession && (insideRect(hudOpen ? hudPanelRect : null, points[0].x, points[0].y)
      || insideRect(hudNavRect, points[0].x, points[0].y))) {
      gesture = { ...createPointerGesture(points[0]), hud: true, panel: insideRect(hudOpen ? hudPanelRect : null, points[0].x, points[0].y) }; return;
    }
    if (touchControls.start(points)) {
      const free = points.filter(point => !touchControls.ownsPointer(point.id));
      gesture = free.length === 1 ? { ...createPointerGesture(free[0]), pad: true, cameraId: free[0].id } : { pad: true };
      return;
    }
    if (points.length === 1 && dragFishingForce(points[0].x, points[0].y)) { gesture = { fishingForce: true }; return; }
    if (points.length === 1 && voyage?.beginHold(points[0].x, points[0].y)) { gesture = { helm: true }; return; }
    if (points.length && (voyage?.containsControls(points[0].x, points[0].y)
      || insideRect(voyage?.controlsRect, points[0].x, points[0].y))) {
      gesture = { ...createPointerGesture(points[0]), voyageUi: true }; return;
    }
    // Keep the object under the press, even if fish, waves or a following camera
    // move between press and release. A drag still cancels this selection.
    if (points.length === 1) gesture = { ...createPointerGesture(points[0]), worldSelection: resolveWorldPick(points[0].x, points[0].y) };
    if (points.length === 2) gesture = { pinch: distance(points[0], points[1]), size: explorationCamera.zoom };
  },
  move(points) {
    if (!gesture) return;
    if (gesture.pad) {
      touchControls.move(points);
      const looking = points.find(point => point.id === gesture.cameraId);
      if (looking) rotateCameraGesture(gesture, looking);
      return;
    }
    if (gesture.map || gesture.overlay || gesture.voyageUi || gesture.characterTip || gesture.cameraUi) { if (points.length) movePointerGesture(gesture, points[0]); return; }
    if (gesture.hud) {
      if (points.length) {
        const { dy } = movePointerGesture(gesture, points[0]);
        if (gesture.panel && hudLayout.compact && gesture.moved > TAP_MOVE_TOLERANCE) { hudScroll -= dy * HUD_WIDTH / hudWidth; drawHud(); }
      }
      return;
    }
    if (gesture.fishingForce) { if (points.length) dragFishingForce(points[0].x, points[0].y, true); return; }
    if (gesture.helm) return;
    if (fishingSession) return;
    if (points.length === 1 && !gesture.pinch) {
      rotateCameraGesture(gesture, points[0]);
    } else if (points.length === 2 && gesture.pinch) {
      explorationCamera.setZoom(gesture.size * gesture.pinch / Math.max(1, distance(points[0], points[1])));
    }
  },
  end(points, release) {
    if (gesture?.pad) {
      touchControls.end(points);
      if (!points.some(point => point.id === gesture.cameraId)) gesture = points.length ? { pad: true } : null;
      return;
    }
    if (gesture?.fishingForce) { gesture = null; return; }
    if (voyage?.endHold() || gesture?.helm) { gesture = null; return; }
    activateTapGesture(gesture, release?.pointerType === 'mouse');
    gesture = points.length ? gesture : null;
  },
  cancel() {
    touchControls.cancel(); voyage?.endHold(); gesture = null;
  },
});

onResize(resize);

function frame(timeMs) {
  const active = foreground && !accountDialogOpen;
  const time = timeMs * .001;
  const stormWind = storm.phase === 'impact' ? 1.8 : storm.phase === 'preparing' ? .95 : storm.phase === 'warning' ? .42 : .08;
  const deltaSeconds = previousFrameMs ? Math.min(.1, Math.max(0, (timeMs - previousFrameMs) / 1000)) : 0;
  previousFrameMs = timeMs;
  if (active && deltaSeconds > 0) {
    if (restTransition) updateRestTransition(deltaSeconds);
    else if (!fishingSession && !boatTrip) advanceGameTime(state, deltaSeconds * PASSIVE_TIME_SCALE);
    syncStorm();
    updateFishingSession(deltaSeconds, timeMs);
    updateHutDoor(deltaSeconds);
    updateBedApproach(deltaSeconds);
    updateCooking(deltaSeconds);
    updateTouchMovement(deltaSeconds);
    updateAvatar(deltaSeconds);
    animateAvatarFace(avatar, time, Boolean(fishingSession) || avatar?.userData.actionUntil > sceneElapsed);
    updateCoastalAvatar(deltaSeconds);
    updateEndurance(deltaSeconds);
    updateBoatTrip(deltaSeconds);
    updateCatchEffects(deltaSeconds);
    for (const object of interactive) {
      if (object.userData.indoorBedLabel) object.visible = state.shelter.inside;
      const data = object.userData.action;
      if (data?.type !== 'gather') continue;
      data.active = (state.gatherCooldowns[data.nodeId] || 0) <= state.gameMinutes;
      object.visible = data.active;
    }
    refreshFarmBeds();
    refreshSiteProps();
    refreshFreshwaterVisuals();
    refreshEquipmentVisuals();
    refreshTreeVisuals();
    refreshBoatVisuals();
    if (timeMs - lastSaveMs > 5000) { saveState(state); lastSaveMs = timeMs; }
    if (timeMs - lastHudDrawMs > 700) { drawHud(); lastHudDrawMs = timeMs; }
  }
  water.material.uniforms.uTime.value = time;
  water.material.uniforms.uStorm.value = Math.min(1, stormWind / 1.8);
  for (const uniform of windShaderUniforms) {
    uniform.time.value = time;
    uniform.strength.value = stormWind;
  }
  water.material.uniforms.uBoatWake.value = boatTrip ? 1 : 0;
  if (boatTrip) {
    const dx = boatTrip.toX - boatTrip.fromX;
    const dz = boatTrip.toZ - boatTrip.fromZ;
    const length = Math.hypot(dx, dz) || 1;
    water.material.uniforms.uBoatDir.value.set(dx / length, dz / length);
  }
  const hour = Number.isFinite(Number(state.clockHours)) ? Number(state.clockHours) : 6;
  islandLifeVisual?.update(state, sceneElapsed, hour);
  const wildlife = wildlifeActivityAt(hour);
  const fishSeconds = active ? deltaSeconds * wildlife.fishSpeed : 0;
  fishActivityTime += fishSeconds;
  birdFlightTime += active ? deltaSeconds * wildlife.birdFlight : 0;
  birdFlightUniform.value = wildlife.birdFlight;
  birdTimeUniform.value = birdFlightTime;
  for (const uniform of animatedShaderUniforms) uniform.value = fishActivityTime;
  const tidePhase = -Math.cos(hour * Math.PI / 6);
  updateWalkDestination();
  water.material.uniforms.uTide.value = tidePhase;
  water.position.y = -.02 + tidePhase * .14;
  if (active) voyage?.update(deltaSeconds, water.position.y);
  if (visualProps.barrierMarker) visualProps.barrierMarker.position.y = water.position.y + .12 + Math.sin(time * 1.5) * .035;
  // The baked hull keel sits .27 m above its root; keep that keel at the
  // changing surface while the gunwale and deck stay visibly above it.
  const boatFloatY = boatFloatOffset(water.position.y, time);
  if (heroBoat && !isBoatPassenger(boatTrip, fishingSession) && !voyage?.engaged) {
    heroBoat.position.set(BOAT_MOOR.x + Math.sin(time * .42) * (.12 + stormWind * .045),
      BOAT_MESH_ORIGIN.y + boatFloatY, BOAT_MOOR.z + Math.sin(time * .31) * .08);
    heroBoat.rotation.set(Math.sin(time * (1.1 + stormWind)) * (.018 + stormWind * .025), 0,
      Math.sin(time * (.86 + stormWind * .7)) * (.024 + stormWind * .035));
    water.material.uniforms.uBoat.value.set(heroBoat.position.x, heroBoat.position.z);
    refreshBoatVisuals();
  }
  else if (heroBoat && !voyage?.engaged) {
    heroBoat.position.y = BOAT_MESH_ORIGIN.y + boatFloatY;
    heroBoat.rotation.x = Math.sin(time * 1.1) * .018;
    heroBoat.rotation.z = Math.sin(time * .86) * .024;
    seatAvatarInBoat(avatar, heroBoat, { keepArms: Boolean(fishingSession?.fromBoat) });
    water.material.uniforms.uBoat.value.set(heroBoat.position.x, heroBoat.position.z);
    refreshBoatVisuals();
  }
  if (voyage?.engaged) {
    water.material.uniforms.uBoat.value.set(heroBoat.position.x, heroBoat.position.z);
    water.material.uniforms.uBoatWake.value = voyage.wake;
    water.material.uniforms.uBoatDir.value.set(Math.sin(voyage.heading), Math.cos(voyage.heading));
    refreshBoatVisuals();
  }
  updateCamera(active ? deltaSeconds : 0, time, stormWind);
  const focus = cameraPose?.target;
  const light = environment.update(hour, storm.phase, time, active ? deltaSeconds : 0,
    { x: focus?.x || 0, z: focus?.z || 0 }, water.position.y);
  environment.updateLamp({ enabled: state.hasLamp, position: avatar?.position, visible: avatar?.visible });
  artDaylight.value = light.sunlight * (1 - light.cloud * .78);
  water.material.uniforms.uDaylight.value = light.waterLight;
  water.material.uniforms.uSunlight.value = light.sunlight * (1 - light.cloud * .78);
  water.material.uniforms.uMoonlight.value = light.moonlight * (1 - light.cloud * .94);
  water.material.uniforms.uSunDirection.value.copy(environment.sun.position).sub(environment.sun.target.position).normalize();
  water.material.uniforms.uMoonDirection.value.copy(environment.moon.position).sub(environment.moon.target.position).normalize();
  for (const target of marineTargets) if (target.member.roamer) target.member.roamer.active = targetAvailable(target);
  for (const item of animated) {
    if (item.school) {
      const { inst, data, bird, matrix, pos, quat, bankQuat, scale, crabGait, normal, slopeQuat } = item.school;
      for (let i = 0; i < data.length; i++) {
        const fish = data[i];
        let heading;
        if (bird) {
          const pose = seagullPose(fish, birdFlightTime, wildlife.birdFlight);
          pos.set(pose.x, pose.y, pose.z);
          heading = pose.heading;
        } else if (fish.target?.reserved) {
          pos.set(fish.target.x, fish.y, fish.target.z);
          heading = fish.crab?.heading ?? fish.roamer?.heading ?? fish.phase;
        } else if (fish.crab) {
          stepSandCrab(fish.crab, active ? deltaSeconds : 0);
          pos.set(fish.crab.x, fish.y, fish.crab.z);
          heading = fish.crab.heading;
        } else if (fish.roamer) {
          stepRoamer(fish.roamer, fishSeconds, fishRoamers);
          pos.set(fish.roamer.x, fish.y + Math.sin(fishActivityTime * .85 + fish.bob) * fish.bobble, fish.roamer.z);
          heading = fish.roamer.heading;
        } else {
          const angle = fish.phase + time * fish.speed;
          pos.set(Math.cos(angle) * fish.radius, fish.y + Math.sin(time * 1.4 + fish.bob) * fish.bobble,
                  Math.sin(angle) * fish.radius);
          heading = Math.atan2(Math.cos(angle), -Math.sin(angle)) + (fish.speed < 0 ? Math.PI : 0);
        }
        if (fish.crab) {
          pos.y = groundHeightAt(pos.x, pos.z) - fish.groundMinY * fish.size + .012;
          crabGait.setXY(i, fish.crab.gait, fish.target?.reserved ? 0 : fish.crab.activity);
        }
        if (fish.target) {
          fish.target.x = pos.x; fish.target.y = pos.y; fish.target.z = pos.z;
          const available = targetAvailable(fish.target);
          fish.target.proxy.visible = available;
          fish.target.proxy.position.set(pos.x, pos.y, pos.z);
          if (!available) {
            if (fish.crab) sandTracks?.follow(fish.target.id, pos, { kind: 'crab', enabled: false });
            scale.setScalar(0.0001); matrix.compose(pos, quat, scale); inst.setMatrixAt(i, matrix); continue;
          }
        }
        quat.setFromAxisAngle(Y_AXIS, -heading);
        if (fish.crab) {
          sandTracks?.follow(fish.target.id, pos, { kind: 'crab', angle: -heading,
            enabled: active && !fish.target.reserved });
          // Match the sloping beach plane so uphill feet don't disappear in
          // sand while the downhill legs float in the air.
          const dx = (groundHeightAt(pos.x + .16, pos.z) - groundHeightAt(pos.x - .16, pos.z)) / .32;
          const dz = (groundHeightAt(pos.x, pos.z + .16) - groundHeightAt(pos.x, pos.z - .16)) / .32;
          normal.set(-dx, 1, -dz).normalize();
          slopeQuat.setFromUnitVectors(Y_AXIS, normal).multiply(quat);
          quat.copy(slopeQuat);
        }
        if (fish.bank) {
          bankQuat.setFromAxisAngle(X_AXIS, fish.bank * Math.sin((bird ? birdFlightTime : fish.roamer ? fishActivityTime : time) * 1.7 + fish.bob) * (bird ? wildlife.birdFlight : 1));
          quat.multiply(bankQuat);
        }
        scale.setScalar(fish.size);
        matrix.compose(pos, quat, scale);
        inst.setMatrixAt(i, matrix);
      }
      inst.instanceMatrix.needsUpdate = true;
      if (crabGait) crabGait.needsUpdate = true;
      continue;
    }
    if (item.bob) item.object.position.y = item.baseY + Math.sin(time * item.speed) * item.amount;
    if (item.spin) item.object.rotation.z = time * item.speed;
    if (item.grow && item.object.scale.x < item.target) {
      const scale = Math.min(item.target, item.object.scale.x + deltaSeconds * 3.2);
      item.object.scale.setScalar(scale);
    }
    if (!item.bob && !item.spin && !item.grow) item.object.rotation.z = Math.sin(time * item.speed) * item.amount;
  }
  if (activeMarineTarget && marineTargetMarker) marineTargetMarker.position.set(activeMarineTarget.x, water.position.y + .12, activeMarineTarget.z);
  if (avatar && sandTracks) {
    sandTracks.follow('avatar', avatar.position, { enabled: active && avatar.visible
      && !hutCrossing && !state.shelter.inside && !boatTrip
      && (voyage?.engaged ? voyage.ashore : marineMode === 'land') });
    sandTracks.update(active ? deltaSeconds : 0, time, tidePhase, water.material.uniforms.uStorm.value);
    sandTrackVisual.sync();
  }
  if (webAccountRoot) webAccountRoot.style.visibility = voyage?.mapOpen ? 'hidden' : '';
  updateNearbyActions();
  positionFeedback();
  touchControls.update(screen, hudNavRect, Boolean(avatar) && screen.isTouch && !hudOpen && !voyage?.sailing && !voyage?.mapOpen && !fishingSession && !fishingResult && !restTransition && !bedApproach && !cookingJob && !hutCrossing && !busyAction && !state.shelter.inside && storm.phase !== 'impact' && foreground && !accountDialogOpen, nearbyAction ? interactionName(nearbyAction) : '活动');
  cameraUi.update(screen, { mode: explorationCamera.mode,
    visible: Boolean(avatar) && !hudOpen && !voyage?.mapOpen && !fishingSession && !fishingResult && !restTransition && !bedApproach && !accountDialogOpen && foreground,
    navRect: hudNavRect, statusRect: statusAlertRect ? { ...statusRect, height: statusAlertRect.y + statusAlertRect.height - statusRect.y } : statusRect, controlsRect: voyage?.controlsRect });
  if (avatar) staminaVisual.update(state.stamina, avatar.position, camera);
  explorationHud.update({ screen, target: avatar && compassTarget(state.voyage, voyage?.sailing ? state.voyage : avatar.position, azimuth),
    seconds: active ? deltaSeconds : 0,
    show: Boolean(avatar) && !hudOpen && !voyage?.mapOpen && !fishingSession && !restTransition && !bedApproach && !accountDialogOpen,
    ashore: voyage?.ashore, mobile: hudLayout?.compact,
    navRect: hudNavRect, statusRect: statusAlertRect ? { ...statusRect, height: statusAlertRect.y + statusAlertRect.height - statusRect.y } : statusRect, azimuth, controlsRect: voyage?.controlsRect });
  renderer.clear();
  renderer.render(scene, camera);
  renderer.clearDepth();
  renderer.render(hudScene, hudCamera);
  nextFrame(frame);
}

async function start() {
  await loadSceneAssets();
  state.boatMoored = true;
  createResources();
  Object.keys(plots).forEach(addPlot);
  createSiteProps();
  createTradeSign();
  createFreshwaterCollector();
  createHutDoorSign();
  createCookingStation();
  islandLifeVisual = createIslandLifeVisual(scene, { groundHeightAt });
  interactive.push(islandLifeVisual.board);
  islandLifeVisual.update(state, 0, state.clockHours);
  registerHomeCollision(islandLifeVisual.board, 'home-order-board');
  for (const [id, root] of islandLifeVisual.decorations) registerHomeCollision(root, `home-project-${id}`);
  createAvatar();
  voyage = createVoyageScene({ scene, hudScene, state, interactive, surfUniforms: water.material.uniforms, palmSource: voyagePalmSource, artAssets: voyageArtAssets, getAvatar: () => avatar, getBoat: () => heroBoat,
    onDiscovery: event => explorationHud.announce(event),
    flash, save: saveState, boardFromHome: boardVoyageFromHome, getScreen: () => screen,
    getHudBounds: () => ({ nav: hudNavRect, panel: hudOpen ? hudPanelRect : null, status: statusRect }),
    closeHud: () => { hudOpen = false; drawHud(); }, isBlocked: () => busyAction || !foreground, isHudOpen: () => hudOpen,
    useSupply: (kind) => {
      const result = kind === 'drink' ? drinkWater(state) : eatMeal(state);
      flash(result.ok ? `${kind === 'drink' ? '饮水完成' : `吃下${result.label}`} · 恢复 ${Math.floor(result.staminaGain)} 体力`
        : result.reason === 'full' ? '体力已满，暂时无需补给' : kind === 'drink' ? '水壶已空，请到泉眼岛装水' : '食物不足，可去椰林岛采集椰果');
      saveState(state);
    },
    onHome: () => {
      avatarTarget = null; avatarRoute = []; arrivalAction = null; marineMode = 'land';
      avatar.rotation.set(0, 0, 0); avatar.visible = true;
      avatar.position.set(DOCK_APPROACH.x, groundHeightAt(DOCK_APPROACH.x, DOCK_APPROACH.z) + .02, DOCK_APPROACH.z);
      updateCamera();
    },
  });
  walkGroundMeshes.push(...voyage.remoteGround);
  sandTracks = createSandTracks({ sampleSand: sampleTrackSand });
  sandTrackVisual = createSandTrackVisual(sandTracks);
  scene.add(sandTrackVisual.mesh);
  voyage.resume();
  voyage.update(0, water.position.y);
  createFarmBeds();
  gradeWorld(scene, artDaylight);
  cameraOcclusion.apply(scene);
  getHomeColliders(true);
  sceneReady = true;
  onForegroundChange((visible) => {
    foreground = visible;
    previousFrameMs = 0;
    touchControls.cancel();
    stopManualMovement();
    voyage?.releaseControls();
    if (!visible) saveState(state);
  });
  ensureStormSchedule();
  syncStorm();
  resize();
  setTimeout(resize, 120);
  drawHud();
  saveState(state);
  nextFrame(frame);
}

start().catch((error) => {
  console.error('Island assets failed to load', error);
  resize();
  flash('岛屿资源加载失败，请重新编译');
  nextFrame(frame);
});

if (!initialViewport.isWeChat && typeof globalThis.window?.addEventListener === 'function') {
  canvas.addEventListener('wheel', (event) => {
    if (accountDialogOpen || voyage?.mapOpen || !foreground || fishingSession || fishingResult || restTransition) return;
    const factor = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? hudPanelRect.height : 1;
    if (hudOpen) {
      if (hudLayout?.compact && hudScrollGeometry?.maxScroll && insideRect(hudPanelRect, event.clientX, event.clientY)) {
        hudScroll += event.deltaY * factor * HUD_WIDTH / hudWidth;
        drawHud(); event.preventDefault();
      }
      return;
    }
    if (insideRect(hudNavRect, event.clientX, event.clientY) || cameraUi.contains(event.clientX, event.clientY)
      || insideRect(voyage?.controlsRect, event.clientX, event.clientY)) return;
    explorationCamera.setZoom(explorationCamera.zoom * Math.exp(Math.max(-250, Math.min(250, event.deltaY * factor)) * .001));
    event.preventDefault();
  }, { passive: false });
  window.addEventListener('keydown', (event) => {
    if (event.target?.closest?.('input, textarea, dialog, [contenteditable="true"]')) return;
    if (accountDialogOpen || !foreground) return;
    if (voyage?.key(event, true)) return;
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (fishingSession) {
      if (event.repeat && !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      const force = ({ 1: 'light', 2: 'steady', 3: 'strong' })[event.key];
      if (event.key === 'Escape') handleFishingAction('cancel');
      else if (event.key === ' ' || event.key === 'Enter') handleFishingAction('hook');
      else if (force) handleFishingAction(force);
      else if (event.key === 'ArrowLeft') handleFishingAction('force-down');
      else if (event.key === 'ArrowRight') handleFishingAction('force-up');
      else if (event.key.toLowerCase() === 'f') handleFishingAction('assist');
      event.preventDefault();
      return;
    }
    const direction = movementKey(event.key);
    if (direction && !hudOpen && !voyage?.mapOpen && !state.shelter.inside) {
      movementKeys.add(normalizeMovementKey(event.key)); event.preventDefault(); return;
    }
    if (event.repeat) return;
    if (!hudOpen && !voyage?.mapOpen && event.key.toLowerCase() === 'c') {
      explorationCamera.toggleMode(); event.preventDefault(); return;
    }
    if (!hudOpen && !voyage?.mapOpen && event.key.toLowerCase() === 'r') {
      explorationCamera.recenter(voyage?.sailing ? voyage.heading : avatar?.rotation.y || 0); event.preventDefault(); return;
    }
    if (event.key.toLowerCase() === 'e' && characterTipAction) {
      activateWorldAction(characterTipAction); event.preventDefault(); return;
    }
    if (event.key.toLowerCase() === 'm') { voyage?.open(); event.preventDefault(); return; }
    const pages = ['activity', 'voyage', 'farm', 'inventory', 'kitchen', 'trees', 'storm', 'logistics', 'orders'];
    const index = Number(event.key) - 1;
    if (event.key === 'Escape' && voyage?.mapOpen) { voyage.dismiss(); return; }
    if (event.key === 'Escape' && hudOpen) { hudOpen = false; drawHud(); return; }
    if (index >= 0 && index < (state.shopLevel >= 2 ? 9 : 8)) {
      handlePageAction(`tab-${pages[index]}`);
      event.preventDefault();
    }
  });
  window.addEventListener('keyup', (event) => { voyage?.key(event, false); movementKeys.delete(normalizeMovementKey(event.key)); });
  window.addEventListener('blur', () => { voyage?.releaseControls(); stopManualMovement(); touchControls.cancel(); gesture = null; });
}
